import { BillDiscountSheet } from "@/modules/pos/components/BillDiscountSheet";
import { DEFAULT_BILL_DISCOUNT_MAX_PCT } from "@/modules/pos/lib/billDiscount";
import { usePrivateToast } from "@/shared/usePrivateToast";
import { invoiceForCheck, invoiceForPlates } from "@/modules/pos/lib/preBillCheck";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { canAnswerQrRequests, canSendToKitchen } from "@/modules/pos/lib/kitchenRouting";
import { usePrivateApi } from "@/shared/usePrivateApi";
import { invalidateInvoiceMutation } from "@/shared/invalidation";
import { countedKey } from "@/lib/i18n/plural";
import { useLocalName } from "@/shared/localName";
import { useEffect, useMemo, useRef, useState } from "react";
import { billPlaceLabel, deliveryContactMissing, settleTableTitle } from "@/lib/invoice/place";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { endpoints } from "@/lib/api/endpoints";
import { useTranslate } from "@/lib/i18n";
import {
  queryKeys,
  useApiQuery,
} from "@/shared/queries";
import {
  isAlreadySettledRefusal,
  isItemNotReturnableRefusal,
  useErrorMessage,
} from "@/shared/errors";
import { Button, ConfirmModal, ErrorState, LoadingSkeleton } from "@a2z/ui";
import { ListCard, Screen, useMoney } from "@/shared/ui";
import {
  hasPendingItemsConflict,
  isApprovalRefusal,
  isRecoverableConflict,
  isChangedReplay,
  isExpiredCommand,
  createRecoverableCommandSender,
  type CommandApproval,
  type InvoiceCommand,
} from "@/shared/invoices/invoiceCommands";
import { useManagerApproval } from "@/modules/pos/useManagerApproval";
import { contactPayload, useDeliveryContactDraft } from "@/modules/pos/lib/deliveryContactDraft";
import {
  hasPermission,
  PERMISSIONS,
  useSession,
} from "@/modules/auth/useSession";
import type {
  CommandResult,
  InvoiceItem,
  InvoiceItemFulfillment,
  OffPremiseGridEntry,
} from "@/lib/api/types";
import type {
  AddonGroup,
  AggregatorChoice,
  Category,
  Invoice,
  InvoicePayment,
  KitchenStation,
  MenuItem,
  SubstitutionIngredient,
  SubstitutionOptions,
  SubstitutionRemovable,
  PosPriceTier,
  RestaurantSettings,
  SettlementMethod,
  StaffMember,
  TableGridArea,
} from "@/lib/api/types";
import { MenuBoard } from "@/modules/pos/components/MenuBoard";
import { useMenuAvailability } from "@/modules/pos/useMenuAvailability";
import {
  OrderPanel,
  type PanelTable,
} from "@/modules/pos/components/OrderPanel";
import { ChitPrintJob } from "@/modules/kitchen/components/ChitPrintJob";
import { usePrintCoordinator } from "@/modules/printing/PrintCoordinator";
import {
  addonLabel,
  comboLabel,
  isChargeable,
  isRungOnTheBill,
  liveOpenTotals,
  plateNameAr,
  tillTotals,
  type PlateGroup,
} from "@/modules/pos/lib/groupPlates";
import {
  matchingParty,
  nextPartyLabel,
  paidPlateIds,
  runSettle,
} from "@/modules/pos/lib/settleSheet";
import { tenderReferenceField } from "@/modules/pos/lib/tenderReference";
import {
  closeUnblockAction,
  queuedTicketIds,
  tillCloseAction,
  tillPrimaryAction,
  type CloseBlocking,
} from "@/modules/pos/lib/tillActions";
import { printStartsFood, recordsReady, settingsWorkflow } from "@/modules/kitchen/lib/workflow";
import { CloseBlockedNote } from "@/modules/pos/components/CloseBlockedNote";
import { RequestCard, RequestCardRow } from "@/modules/pos/components/RequestCard";
import { formatSectionStamp } from "@/modules/pos/lib/sectionTime";
import { OutOfStockFireConfirm } from "@/modules/pos/components/OutOfStockFireConfirm";
import { outOfStockDrafts, type OutOfStockDraft } from "@/modules/pos/lib/outOfStockDrafts";
import { initialPane, type TillPane } from "@/modules/pos/lib/initialPane";
import type { DispatchBoardResponse } from "@/lib/api/dispatchTypes";
import { dispatchByInvoice } from "@/modules/pos/lib/dispatchBoard";
import { dispatchQueryKeys, useDispatchAccess } from "@/modules/pos/lib/useDispatchBoard";
import {
  blockedAddonIds,
  type OptionRemoval,
} from "@/modules/pos/lib/swapCollisions";
import { shouldPromptForCovers } from "@/modules/pos/lib/coversPrompt";
import {
  createGuestCountWriteQueue,
  GUEST_COUNT_MAX,
  GUEST_COUNT_MIN,
  nextGuestCountValue,
} from "@/modules/pos/lib/guestCountStepper";
import { shouldShowCoversSanityHint } from "@/modules/pos/lib/coversSanityHint";
import { TillSummaryBar } from "@/modules/pos/components/TillSummaryBar";
import { TillPhoneLead } from "@/modules/pos/components/TillPhoneLead";
import {
  billDiscountPricingOf,
  invoiceChargeRates,
  invoiceDiscountPercent,
  handoffDecision,
  quoteFromPrice,
  resolveGroupsForItem,
} from "@/modules/pos/lib/pricing";
import {
  AddonPicker,
  type AddonPickerResume,
} from "@/modules/pos/components/AddonPicker";
import {
  PaperRegisterTabs,
  PaperTillBarLeading,
} from "@/modules/pos/components/PaperRegister";
import { usePaperTill } from "@/shared/posTheme";
import { PaperDishThumb } from "@/modules/pos/components/PaperMenu";
import { IngredientWarningDialog } from "@/modules/pos/components/IngredientWarningDialog";
import { SubstitutionPicker } from "@/modules/pos/components/SubstitutionPicker";
import { VoidItemDialog } from "@/modules/pos/components/VoidItemDialog";
import {
  buildRefundCommandPayload,
  buildVoidCommandPayload,
  returnableItemIds,
  type ReasonCode,
} from "@/modules/pos/lib/voidReasons";
import { SettleSheet } from "@/modules/pos/components/SettleSheet";
import { PaperSettleSheet } from "@/modules/pos/components/PaperSettleSheet";
import { TransferTableDialog } from "@/modules/pos/components/TransferTableDialog";
import { NoteDialog } from "@/modules/pos/components/NoteDialog";
import { GuestCountDialog } from "@/modules/pos/components/GuestCountDialog";
import { CaptainSheet } from "@/modules/pos/components/CaptainChips";
import { captainChips } from "@/modules/pos/lib/captain";
import { PriceOverrideDialog } from "@/modules/pos/components/PriceOverrideDialog";
import { Receipt } from "@/modules/pos/components/Receipt";
import { RefundSheet } from "@/modules/pos/components/RefundSheet";
import { DrawerStrip } from "@/modules/pos/components/DrawerStrip";
import { OpenDrawerDialog } from "@/modules/pos/components/OpenDrawerDialog";
import { PartySheet } from "@/modules/pos/components/PartySheet";
import { PrintChoiceSheet } from "@/modules/pos/components/PrintChoiceSheet";
import { PartialSettleSheet } from "@/modules/pos/components/PartialSettleSheet";
import { OverpaymentDialog } from "@/modules/pos/components/OverpaymentDialog";
import { CustomerDialog } from "@/modules/pos/components/CustomerDialog";
import { ChargesDialog } from "@/modules/pos/components/ChargesDialog";
import { HandOverDialog } from "@/modules/pos/components/HandOverDialog";
import { BillAssignPanel, BillDispatchStrip } from "@/modules/pos/components/delivery/BillDispatchStrip";
import { ORDER_CONTACT_MISSING_ID } from "@/modules/pos/components/delivery/OffPremiseOrderHead";
import { CommandIds } from "@/modules/pos/lib/assignDrivers";
import { ReprintQuestion } from "@/modules/pos/components/ReprintQuestion";
import {
  isStationWithoutPrinter,
  reprintFallbackAction,
  type ReprintReason,
} from "@/modules/pos/lib/reprintChit";
import type { PrintArtifactAction } from "@/modules/printing/renderDocument";
import {
  markablePlates,
  outstandingOnBill,
  resolveAssignPartyPayload,
} from "@/modules/pos/lib/partyShares";
import { RefundReceipt } from "@/modules/pos/components/RefundReceipt";
import { buildRefundDocument } from "@/modules/pos/lib/refundDocument";
import { useInvoiceRefundPrinting } from "@/modules/pos/useInvoiceRefundPrinting";
import {
  PreBillCheckDialog,
  PreBillCheckPaper,
} from "@/modules/pos/components/PreBillCheck";
import { usePreBillCheckPrinting } from "@/modules/pos/usePreBillCheckPrinting";
import { isRefundableSettlementMethod } from "@/modules/pos/lib/refundAllocation";
import { voidReasonLabelForCode } from "@/modules/pos/lib/voidReasons";
import {
  STAFF_MEAL_REASON,
  isStaffMealBill,
  staffMealCanCloseNow,
  staffMealNextStep,
  staffMealPlatesToComp,
} from "@/modules/pos/lib/staffMeal";
import { DinerPaymentCard } from "@/modules/pos/components/DinerPaymentCard";
import { TableServiceRequests } from "@/modules/pos/serviceRequests/TableServiceRequests";


/**
 * HOW LONG A CLOSE STAYS UNDOABLE FROM ITS OWN TOAST (UX-round D17, ruling E2).
 * «UX-round Dn» = the 2026-09-17 UX round's own numbering (`.roundtable/2026-09-17-restaurant-ux/decisions.md`), not §11's.
 *
 * Closing a bill gets no confirm — it prints nothing, tells nobody and moves
 * no money, so the three-question test lands on "no dialog, ship an undo", and
 * a confirm on the most repeated act of a shift is a tap people learn to
 * dismiss without reading. The undo is what catches the mis-tap instead, so
 * the window has to be long enough to actually be used.
 *
 * **8 seconds, and the reasoning is a stopwatch rather than a taste.** The tap
 * is followed by: the toast animating in (0.3s), an Arabic sentence read on a
 * 390px screen held in one hand (~1.5s), the "that is not what I meant"
 * (~1–2s), and a thumb travelling to a control at the top of the screen
 * (~1s) — call it 5s for a cashier who is paying attention. The toast
 * primitive's own default for a success with an action is 5s
 * (`SUCCESS_ACTION_DURATION_MS`), which is tuned for a desktop "Retry" and is
 * the floor here, not the budget: this person is mid-service, is being spoken
 * to, and is reading right-to-left. 8s doubles the realise-and-reach half of
 * that without letting the toast follow her to the next table, where a tap on
 * a stale undo would reopen a bill she has moved on from. It is also short
 * enough that the screen behind the toast is unlikely to have been refreshed
 * by another device's revision into a different shape while the offer is
 * still standing.
 *
 * The window is not the only way back — `pos.reopen` is a permanent control on
 * a CLOSED bill and carries the same grant — so it does not need to be generous.
 * It needs to be honest, which is what the deadline check at the call site is
 * for: the toast primitive pauses its timer while a pointer rests on the card,
 * so the BUTTON can outlive the window, and a button that outlives its window
 * must say so rather than act.
 */
const CLOSE_UNDO_WINDOW_MS = 8_000;
/**
 * When a close's undo window ends. Read in the close's own async handler,
 * never in render; a module function so the React compiler does not mistake
 * the handler chain (T6's start-then-close reaches it too) for render code.
 */
function closeUndoDeadline(): number {
  return Date.now() + CLOSE_UNDO_WINDOW_MS;
}

/**
 * T6 / D173 — the still-cooking warning's lines: one per dish and station,
 * counted, in the order the plates are listed. A plate with no station (or
 * a station list this session cannot read) names the dish alone.
 */
function stillCookingLines(
  plates: readonly InvoiceItem[],
  stations: readonly KitchenStation[] | undefined,
  localName: (names: { nameAr: string; nameEn: string | null }) => string,
): { key: string; dish: string; station: string | null; count: number }[] {
  const lines = new Map<string, { key: string; dish: string; station: string | null; count: number }>();
  for (const plate of plates) {
    const key = `${plate.productId}:${plate.stationId ?? ""}`;
    const seen = lines.get(key);
    if (seen) {
      seen.count += 1;
      continue;
    }
    const station = stations?.find((row) => row.id === plate.stationId);
    lines.set(key, {
      key,
      dish: localName({ nameAr: plate.nameAr, nameEn: plate.nameEn }),
      station: station ? localName({ nameAr: station.nameAr, nameEn: station.nameEn }) : null,
      count: 1,
    });
  }
  return [...lines.values()];
}

/**
 * Router state carried from the pre-order table view.
 *
 * `TablePage` cannot stage a line — staging needs this screen's menu, addon
 * groups and pricing — so it passes the product that opened the table and lets
 * this screen do it.
 */
export type InvoiceHandoff = {
  addProductId?: string;
  /**
   * B108 — the add-ons the waiter already chose, on the table screen, BEFORE
   * this bill existed.
   *
   * The picker used to run here, which meant the invoice was opened first and
   * the question asked afterwards: backing out of it left a 0 EGP bill holding
   * the table. It now runs on `TablePage` and the answers travel with the dish,
   * so this screen stages a plate that has already been answered rather than
   * opening a second picker over the same question.
   *
   * Absent on a plain dish, and on any hand-off written by a build that did not
   * carry this field — an empty list is the honest reading of both.
   */
  addAddonIds?: string[];
  /**
   * PAPER — how many identical plates the table screen's picker asked for
   * (its quantity stepper). Absent means one, as before.
   */
  addQuantity?: number;
  /**
   * T8 (2026-10-06) — "settle this one": the floor's one-tap settle on a
   * CLOSED off-premise bill. The bag has left and the money is owed, so the
   * screen opens straight on its settle sheet instead of on the bill with a
   * Settle button still to find. Honoured once, at mount, and only while the
   * bill is CLOSED — on anything else it is simply ignored.
   */
  settle?: boolean;
  /**
   * 2026-10-20 — «تسليم» from the Delivery tab on a bag whose driver ACCEPTED
   * it: the screen opens the bill's own close / hand-over flow (`requestClose`,
   * so every rule the close keeps — the empty bag, the cooking warning, the
   * unsent plates — still applies). Honoured once, and only while OPEN.
   */
  handOver?: boolean;
  /**
   * 2026-10-20 — the till just accepted a customer's online order
   * (`OnlineOrdersStrip`): the server opened this bill and FIRED its food, so
   * in a paper workflow the plates wait QUEUED for a paper that only this
   * screen can print when no live print computer serves the station (D18).
   * The screen hands every still-queued ticket to the same at-fire chit its
   * own «إرسال» does (`queueFireChits`) — same stand-down where a live device
   * prints the ORIGINAL, same `printTicket` that starts the food. Honoured
   * once, at mount, and only while OPEN.
   */
  printFired?: boolean;
};

/**
 * One open table — the redesign's order view: the menu on the left, the order
 * panel on the right.
 *
 * The two panes are one screen because that is what the mock draws and what a
 * till actually needs: tapping a menu tile must show its effect in the order
 * without a modal opening and closing over the menu. The previous shape (an
 * invoice list plus an "add items" sheet) hid the menu behind the result and
 * the result behind the menu.
 *
 * BELOW `lg` THE PANES DO NOT STACK — they SWITCH (round 10, QA §6).
 *
 * They used to stack, panel first, on the reasoning that the order is what a
 * phone is reading. The founder's 390×844 pass showed why that was wrong in
 * both directions: whichever pane came second was a full screen away, so a
 * cashier either scrolled the whole menu to reach the total and the close, or
 * scrolled the whole bill to reach the menu. A 420px order column beside a menu
 * grid genuinely does not exist at 390px, but "put one under the other" is not
 * the only answer to that.
 *
 * So at narrow widths exactly ONE pane is displayed — `narrow="show"` /
 * `narrow="hide"` on the two components, which resolve to mutually exclusive
 * display utilities — and a permanent `TillSummaryBar` under them carries the
 * running total, the line count and the contextual primary action, and is
 * itself the switch between the two. Neither pane is ever more than one tap
 * away, and the money is never off screen at all.
 *
 * Two details that are structure, not decoration:
 *   - The hidden pane is `display:none`, not unmounted, so the menu search and
 *     both scroll positions survive a look at the bill and back.
 *   - The bar is a `shrink-0` flex sibling inside the shell's `100svh` column,
 *     not a sticky overlay, so it cannot cover the last rows of either list and
 *     no scroll container needs a padding-bottom to compensate.
 *
 * At `lg` and above nothing changed: `lg:flex-row`, both panes rendered, the
 * 420px column and its pinned foot — the foot exactly as the artboard draws it,
 * the width deliberately wider (see `OrderPanel`) — and the
 * bar is `lg:hidden`. `lg` is 1024px — the same line `useIsMobile`'s
 * `MOBILE_BREAKPOINT` and the design rulebook draw — and it is drawn in CSS
 * rather than through that hook on purpose: nothing here needs to be COMPUTED
 * differently per width, only displayed, and a JS breakpoint would re-render
 * the whole till on every resize to reach the same result.
 *
 * Every mutation still goes through `dispatchCommand`/`sendCommand`, never a
 * hand-rolled fetch — that is what keeps the idempotency guarantee real.
 *
 * The stale-close case (decision D9) is handled explicitly: the server rejects
 * a close carrying a version that has moved, and this surfaces it as
 * "this invoice changed — refresh", not a generic error toast.
 */
export function InvoicePage() {
  const { apiFetch } = usePrivateApi();
  const toast = usePrivateToast();
  const t = useTranslate();
  const money = useMoney();
  const errorMessage = useErrorMessage();
  const { invoiceId = "", locale = "ar" } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: session } = useSession();
  const { ownsAutomaticPrinting } = usePrintCoordinator();

  const invoiceQuery = useApiQuery<Invoice>(
    queryKeys.invoice(invoiceId),
    endpoints.restaurant.invoice(invoiceId),
    { refetchOnWindowFocus: true },
  );
  const menuQuery = useApiQuery<MenuItem[]>(
    queryKeys.menuItems,
    endpoints.restaurant.menuItems,
    // B117/Q9 — the till has to notice an 86 without being told. See
    // Availability updates arrive through the shared Firestore revision listener.
    {
      // A reopened invoice resumes menu freshness; closed bills keep their invoice/payment reads.
      refetchOnWindowFocus: true,
    },
  );

  /**
   * The store's addon groups.
   *
   * Declared HERE, above the hand-off, because the hand-off's "does this dish
   * need its picker" decision depends on it. It used to sit below and be read
   * out of the cache instead, which is the bug documented on
   * `handoffNeedsAddons`.
   */
  const addonGroupsQuery = useApiQuery<AddonGroup[]>(
    queryKeys.addonGroups,
    endpoints.restaurant.addonGroups,
  );

  /**
   * The handoff from the pre-order table view (`TablePage`): the item the
   * waiter picked, which is what opened this invoice and turned the table
   * busy. It arrives as router state and is staged here rather than there,
   * because staging needs this screen's menu, addon groups and pricing.
   *
   * Seeded synchronously from the menu CACHE rather than in an effect: the
   * table view read the same query key a moment ago, so the item is in hand at
   * mount — and an initial value cannot double-stage under StrictMode the way
   * an effect can.
   */
  /*
   * CAPTURED ONCE, at mount, in a lazy initialiser — not read from
   * `location.state` on every render.
   *
   * The effect below clears the router state (so a refresh cannot replay the
   * hand-off), and the picker is DERIVED from this id. Read live, the two
   * fought: the effect cleared the state, the next render therefore saw no
   * hand-off, and the picker that had just opened unmounted with the dish
   * never added — the waiter's tap vanished in silence. A lazy initialiser is
   * synchronous and pure, so it survives the clear without an effect writing
   * state (which is the lint rule that pushed the picker into render in the
   * first place).
   *
   * A refresh still cannot replay it: the state really is gone from history by
   * then, so the remounted component initialises this to `null`.
   */
  const [handoffProductId] = useState<string | null>(
    () => (location.state as InvoiceHandoff | null)?.addProductId ?? null,
  );
  /**
   * B108 — the add-ons chosen on the table screen, seeded in the same pass as
   * the dish for the same reason: the router state is cleared a moment later,
   * so reading it in an effect would read nothing.
   *
   * A non-empty list is the positive claim "this dish was already asked about",
   * which is what keeps the picker below from opening a second time over a
   * question the waiter has answered.
   */
  const [handoffAddonIds] = useState<string[]>(
    () => (location.state as InvoiceHandoff | null)?.addAddonIds ?? [],
  );
  const [handoffQuantity] = useState<number>(
    () => (location.state as InvoiceHandoff | null)?.addQuantity ?? 1,
  );
  /*
   * T8 — the floor's one-tap settle on a CLOSED off-premise bill, captured in
   * the same pass as the dish for the same reason: the router state is
   * cleared a moment later (the effect below), so an effect would read
   * nothing. The sheet it opens is DERIVED from this flag and the bill's
   * state (`handoffSettleOpen`, the `pickerItem` pattern above), never set
   * from an effect; `handoffSettleDone` — set only from the sheets' own
   * close paths — is what ends it.
   */
  const [handoffSettle] = useState<boolean>(
    () => (location.state as InvoiceHandoff | null)?.settle === true,
  );
  const [handoffSettleDone, setHandoffSettleDone] = useState(false);
  useEffect(() => {
    if (!handoffSettle) return;
    // A refresh must not replay the hand-off: the state is gone from history.
    navigate(location.pathname + location.search, { replace: true, state: null });
    // Once, at mount — the flag it reads is itself captured once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handoffSettle]);
  /*
   * From the QUERY, for the same reason `handoffNeedsAddons` is: reading the
   * menu cache at mount cannot tell "the menu has not loaded" from "this dish
   * is not on the menu", and the first answer is not safe to act on. An
   * unresolved dish used to fall through as "no addons" and be added blind.
   */
  const handoffItem = handoffProductId
    ? ((menuQuery.data ?? []).find((entry) => entry.id === handoffProductId) ??
      null)
    : null;
  /**
   * An addon item cannot be added blind — it arrives with its picker open.
   *
   * Resolved for THIS dish: a dish carrying a group it currently offers
   * nothing from must not open a picker, because an empty picker reads as a
   * failed load and blocks confirm on data that is perfectly fine.
   *
   * **From the QUERY, never from `getQueryData` at mount.** This used to read
   * the addon-group cache synchronously — and `TablePage`, the only screen
   * that hands a dish over, never reads that key. So on the first table-side
   * order of a session the cache was EMPTY, `resolveGroupsForItem` returned
   * `[]`, this was `false`, and the dish was staged with no selections at all.
   * The groups landed a moment later, far too late. The server then refused
   * it, correctly, and the waiter's first order of any dish with a required
   * group — a size, a doneness, a bread choice, i.e. most of a real menu —
   * failed and had to be repeated. On a table that already had a bill the
   * cache happened to be warm and the picker opened, which is why this looked
   * intermittent rather than broken.
   *
   * `null` while the query is still in flight: "I do not know yet" is a third
   * answer, and the hand-off below waits for it rather than guessing `false`.
   */
  /*
   * B108: a dish that arrives ALREADY ANSWERED is added, never asked about
   * again. The picker now runs on the table screen — before the bill exists —
   * so re-deciding here would put the same question in front of the waiter
   * twice, on a plate they have just configured.
   *
   * The full decision still runs for everything else, including the WAIT arm:
   * a hand-off from an older build, or a dish tapped on THIS screen, is exactly
   * the case it was written for.
   */
  const handoff =
    handoffAddonIds.length > 0
      ? "ADD"
      : handoffDecision(
          handoffProductId,
          menuQuery.data === undefined ? undefined : handoffItem,
          addonGroupsQuery.data,
        );

  /**
   * The inline recovery card's state, and WHICH refusal put it there.
   *
   * D22 makes this two-valued rather than a boolean: the close gate runs before
   * the version guard, so the same "your screen is behind" moment arrives
   * either as a version conflict or as `RESTAURANT_INVOICE_HAS_PENDING_ITEMS`.
   * The action is identical (refresh, read, act again); the sentence is not.
   */
  const [conflictKind, setConflictKind] = useState<
    "version" | "pending" | "replayed" | null
  >(null);
  /**
   * The plates the void dialog may act on — every plate of the tapped group
   * the server would accept, newest last, so voiding N takes the N most
   * recently added (the taps the cashier most likely regrets).
   */
  const [voidTarget, setVoidTarget] = useState<{
    plateIds: string[];
    /**
     * D141 — each plate's state when the sheet opened, aligned with
     * `plateIds`. The sheet reads the LIVE bill first (the kitchen may start a
     * plate while the cashier is choosing a reason) and falls back to this
     * snapshot only for a plate the latest read no longer lists.
     */
    fulfillmentStates: InvoiceItemFulfillment[];
    comp: boolean;
  } | null>(null);
  /**
   * B117/Q4 — the plate the remake dialog is asking about.
   *
   * Its own state rather than a third flag on `voidTarget`: a remake takes
   * nothing off the bill, so it shares none of the void path's count picker or
   * last-N-plates rule. One plate, one reason.
   */
  const [remakeTarget, setRemakeTarget] = useState<{
    itemId: string;
    key: string;
  } | null>(null);
  const [cancelAsked, setCancelAsked] = useState(false);
  const [settleAsked, setSettleAsked] = useState(false);
  /* The dark till's whole-bill sheet over an OPEN direct takeaway (founder,
     2026-10-08) — see `settleOpenTakeaway`. Holds the BILL it was asked for,
     so it cannot reappear over the next bill this screen shows. */
  const [openTakeawaySettleFor, setOpenTakeawaySettleFor] = useState<string | null>(null);
  /* PAPER (2026-10-03) — the redesign's Settle window, which replaces the two
     settle sheets on the light PAPER till. See `PaperSettleSheet`. */
  const [paperSettleAsked, setPaperSettleAsked] = useState(false);
  const [transferAsked, setTransferAsked] = useState(false);
  /** R1/F3 — the refund sheet is open over a SETTLED bill. */
  /*
   * Unit C — the drawer dialog, opened from the settle sheet's own warning and
   * layered OVER it. Held here rather than inside the sheet because the refund
   * sheets need the same door, and a sheet that could open a drawer would be a
   * money screen with a second job.
   */
  const [openDrawerAsked, setOpenDrawerAsked] = useState(false);
  const [refundAsked, setRefundAsked] = useState(false);
  /**
   * P4.1 — the refund whose retry came back a replay over a bill that moved
   * since (`isChangedReplay`): its slip never printed, because the answer is
   * not this refund's outcome. The recovery card offers THAT refund's own copy
   * off the refreshed bill — its own lines and amount, never the bill's whole
   * refund total.
   */
  const [replayedRefundCommandId, setReplayedRefundCommandId] = useState<
    string | null
  >(null);
  /** B43 — the split sheet is open over an OPEN or CLOSED bill. */
  const [partyAsked, setPartyAsked] = useState(false);
  // B107 — correcting who an off-premise bill is for.
  const [customerAsked, setCustomerAsked] = useState(false);
  // T6 — correcting what an OPEN off-premise bill charges (fee, platform, channel).
  const [chargesAsked, setChargesAsked] = useState(false);
  // T16 — the off-premise close confirmation: who the bag left with.
  const [handOverAsked, setHandOverAsked] = useState(false);
  /** Round 5 — the pre-bill check dialog is open over an OPEN bill. */
  const [checkAsked, setCheckAsked] = useState(false);
  /* 2026-10-20 — the printer asks: the whole check, or chosen items (a part). */
  const [printChoiceAsked, setPrintChoiceAsked] = useState(false);
  /* Gate F4 — one part print at a time: two taps inside the busy-key gap must
     not queue a second `assignParty` / `printCheck`. */
  const printingPart = useRef(false);
  /** D146 — the discount-at-the-bill sheet. */
  const [discountAsked, setDiscountAsked] = useState(false);
  const checkPrinting = usePreBillCheckPrinting();
  /** B3 — the split-tender sheet is open over a CLOSED bill. */
  const [partialAsked, setPartialAsked] = useState(false);
  /*
   * The tender the cashier had already chosen on the whole-bill sheet when they
   * tapped "take part of it". Carried so the split sheet opens on the same
   * answer instead of its own CASH default — see `PartialSettleSheet`'s
   * `initialMethod`.
   */
  const [partialMethod, setPartialMethod] = useState<SettlementMethod>("CASH");
  /** B89 — the give-it-back dialog is open over an over-paid bill. */
  const [overpaidAsked, setOverpaidAsked] = useState(false);
  /*
   * T8 — the three settle sheets close through these, so the floor's settle
   * hand-off (which holds a sheet open until it is dealt with) ends on ANY
   * close: cancel, success, a conflict, a stale screen. A sheet that closed
   * on its own flag alone would re-open on the next render.
   */
  const closeSettleSheet = () => {
    setSettleAsked(false);
    setHandoffSettleDone(true);
  };
  const closePaperSettleSheet = () => {
    setPaperSettleAsked(false);
    setHandoffSettleDone(true);
  };
  const closePartialSheet = () => {
    setPartialAsked(false);
    setHandoffSettleDone(true);
  };
  /** The basket group whose kitchen note is being edited. */
  const [noteTarget, setNoteTarget] = useState<PlateGroup | null>(null);
  /**
   * `null` at mount, ALWAYS — never seeded from the hand-off.
   *
   * Seeding it here meant deciding "does this dish need a picker" in the same
   * tick the screen mounted, before the addon groups could possibly have
   * arrived. The hand-off effect below opens it instead, once there is an
   * answer to decide on.
   */
  const [addonTarget, setAddonTarget] = useState<MenuItem | null>(null);
  /**
   * Whether the hand-off's own picker has been dealt with.
   *
   * The hand-off picker is DERIVED, not stored (see `pickerItem`): opening it
   * meant `setState` inside an effect, which is a cascading render and what
   * `react-hooks/set-state-in-effect` exists to stop. So the effect decides
   * nothing about the picker at all — the render does — and this flag, set
   * only from the picker's own cancel/confirm, is what closes it.
   */
  const [handoffPickerDone, setHandoffPickerDone] = useState(false);
  /** The item the amount dialog is open for (D46, S9). */
  const [overrideTarget, setOverrideTarget] = useState<MenuItem | null>(null);
  /**
   * PAPER — the light till's register (2026-10-03): the picker asks the size
   * first and carries the dish's quantity, and the price override and the
   * swap moved off the dish tile into it. When the picker steps aside for one
   * of those dialogs, what it had chosen waits here and is handed back to it.
   */
  const paperTill = usePaperTill();
  // Phones and small tablets (below `lg`): the bill is the OTHER pane there,
  // so a plate added in one tap lands somewhere the waiter cannot see or undo.
  const isMobile = useIsMobile();
  /** Plates ticked on the bill and sent to the Settle window (founder, 2026-10-07). */
  const [settlePicked, setSettlePicked] = useState<string[] | null>(null);
  /**
   * D133 — a diner's even share (or "the rest") handed to the Settle window:
   * the amount it opens on and the method the diner said they would pay by.
   */
  const [settleSeed, setSettleSeed] = useState<{
    amount: number;
    method: SettlementMethod;
    /** D133 fix — the claim the window was opened from; sent with its tender. */
    dinerPaymentId: string;
  } | null>(null);
  const [pickerResume, setPickerResume] = useState<AddonPickerResume | null>(
    null,
  );
  /**
   * PAPER (founder, 2026-10-03): the NEW line being changed through the
   * picker. There is no amend command for a draft — `updateDraftItems` only
   * removes plates or sets notes — so Update takes that line's plates off
   * (`updateDraftItems.removeItemIds`) and adds the changed line back
   * (`addItems`), one act from the waiter's side. Only ever a DRAFT.
   */
  const [editTarget, setEditTarget] = useState<PlateGroup | null>(null);
  // B6: covers, answered when the waiter actually knows rather than only in the
  // instant before the first menu tap. Open/closed only — the value lives on
  // the invoice, so there is no draft to keep in sync with it here.
  const [isCountingGuests, setCountingGuests] = useState(false);
  /*
   * B117/Q6 — the covers prompt has been offered for THIS bill and will not be
   * offered again. Latched rather than derived from `guestCount`, because a
   * cashier who deliberately dismissed it answered "nobody counted", and asking
   * again on the next tap would turn a skippable prompt into an unskippable one
   * with extra steps.
   */
  const [coversAsked, setCoversAsked] = useState(false);
  /*
   * B118 — THE TAP THE COVERS PROMPT INTERRUPTED WAS A CLOSE, AND IT IS OWED
   * BACK.
   *
   * The prompt opened, took the count, saved it and stopped: the bill stayed
   * OPEN with no toast and nothing on screen admitting the close had not
   * happened, so the cashier had to tap «اقفل الفاتورة» a second time — and on
   * a busy pass she finds out at the door, with the table still lit on the
   * floor grid and no cheque printed. A prompt that eats the action it
   * interrupted is the defect; the count was never the thing being asked for.
   *
   * Set only when the CLOSE opened the dialog, never when the covers row did:
   * answering the row is an answer to itself, and closing a bill off the back
   * of it would be a second surprise in the other direction.
   */
  const [closeAfterCovers, setCloseAfterCovers] = useState(false);
  /**
   * The confirmed amount, carried from the price dialog into the addon
   * picker for an item that needs BOTH — the two dialogs are never open
   * together, so one slot is enough. Cleared the moment it is spent.
   */
  /**
   * The hand-typed price waiting on the addon picker — amount AND the reason
   * it owes (founder decision 2026-09-03). One object rather than two states:
   * the note is required with the price, and two independent slots could get
   * out of step across the picker detour and send a price with no reason,
   * which the server would refuse mid-gesture.
   */
  const [pendingOverride, setPendingOverride] = useState<{
    unitPrice: number;
    note: string;
  } | null>(null);
  /**
   * The dish whose swap dialog is open, and what came back for it (D63).
   *
   * The options are fetched when the dialog opens rather than with the menu:
   * the pool is a whole-store read that almost every order never needs, and
   * loading it up front would put a costing read behind every menu tap.
   */
  const [swapTarget, setSwapTarget] = useState<MenuItem | null>(null);
  const [swapOptions, setSwapOptions] = useState<SubstitutionOptions | null>(
    null,
  );
  const [swapLoadFailed, setSwapLoadFailed] = useState(false);
  /**
   * The plate whose swaps are being CHANGED (B19), as opposed to a dish whose
   * swaps are being chosen before it is added.
   *
   * A separate piece of state from `swapTarget` rather than a mode flag on it,
   * because the two dialogs end in completely different places: the add path
   * may detour through the option picker and finish as an `addItems`, while
   * this one is a single `substituteItem` on a plate that already exists. One
   * state carrying both would have to be untangled in every handler.
   *
   * It holds the GROUP so the dialog can say how many identical plates the row
   * stands for — the change lands on ONE of them (see `amendSwaps`).
   */
  const [amendTarget, setAmendTarget] = useState<PlateGroup | null>(null);
  /**
   * A confirmed swap waiting for the addon picker (D61).
   *
   * Exactly the shape `pendingOverride` above uses, and for the same reason:
   * the two dialogs are sequential, never both on screen, so the plate keeps
   * its swap across the detour. Without this the swap path sent an EMPTY addon
   * list, and any dish with a required group — a size, a doneness, a bread
   * choice, i.e. most of a real menu — was refused server-side by
   * `assertAddonSelectionValid` after the waiter had already told the guest
   * yes. It also made "Negresco with penne, extra cheese" unexpressible.
   */
  const [pendingSwap, setPendingSwap] = useState<
    { removesIngredientId: string; addsIngredientId: string }[] | null
  >(null);
  /**
   * WHAT THE SWAP DIALOG QUOTED, carried across the option detour (D108).
   *
   * The option picker states the line total, and it is the LAST number a
   * waiter reads before committing the plate. Without this it quoted base +
   * options and silently dropped the surcharge the swap dialog had just
   * promised — invisible while a surcharge was a few pounds, and not invisible
   * once a rate on a 300 g line makes it tens.
   */
  const [pendingSwapCharge, setPendingSwapCharge] = useState(0);
  /**
   * What THIS dish's options take off the plate, kept across the detour (B21).
   *
   * Captured when the swap is confirmed rather than read off `swapOptions`,
   * because that state is cleared in the same handler — and the picker that
   * needs it opens afterwards. Without it the option picker cannot tell that
   * "Penne (replaces fettuccine)" collides with the fettuccine swap the waiter
   * just made, and the whole append is refused by the server once the guest has
   * been told yes.
   */
  const [pendingOptionRemovals, setPendingOptionRemovals] = useState<
    OptionRemoval[]
  >([]);
  /**
   * Whether the handoff item has already been sent.
   *
   * A ref, not state: nothing renders from it, and it must flip SYNCHRONOUSLY
   * so StrictMode's double-invoked effect cannot slip a second add through
   * before a state update commits — which is exactly the double-add it exists
   * to prevent.
   */
  const handoffSent = useRef(false);
  /**
   * The 86 confirm (§5.8, D32): the item whose availability is being flipped,
   * and the one whose write is in flight.
   *
   * Confirmed rather than instant, in BOTH directions. 86'ing takes a dish off
   * every till and off the diners' QR menu store-wide, and un-86'ing puts it
   * back on all of them — neither is a mistake to make by brushing a tile in a
   * grid held in one hand mid-service. It is a two-tap act on purpose, the same
   * posture the settle sheet's write-off row takes for the same reason.
   */
  const availability = useMenuAvailability();
  /**
   * Which pane is on screen below `lg` (see the screen comment above).
   *
   * `null` means "nobody has asked for a pane yet", and the answer then comes
   * from `initialPane(status)` (UX-round D18): the menu for an OPEN bill, the bill for
   * a cheque that is out or already paid. It used to be a flat `"menu"` for
   * every status, so a cashier arriving to take money met a dish grid and the
   * figures she came for were behind a tap.
   *
   * An OVERRIDE rather than a seeded state, and deliberately: the invoice is
   * not loaded on the first render, so a `useState` initialiser could not read
   * its status anyway — and a person who taps the toggle must not have the
   * screen taken back off them by the next revision-driven refetch.
   */
  const [paneOverride, setPaneOverride] = useState<TillPane | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  /**
   * Chits waiting to print, oldest first. One `ChitPrintJob` runs at a time
   * because `window.print()` blocks — a fire that spans three stations prints
   * three chits back to back, each recorded on the server before its paper
   * exists (the job's own ordering rule).
   */
  const [printQueue, setPrintQueue] = useState<
    Array<{
      ticketId: string;
      action?: PrintArtifactAction;
      atFire?: boolean;
      /** Gate P7 rework 2 (R3) — the till's reprint printed here. */
      reason?: ReprintReason;
      seenPrintCount?: number;
    }>
  >([]);
  /**
   * D172 — the ticket whose «طباعة التذكرة» is being asked about («لم تصل
   * التذكرة» / «نسخة إضافية»); `ReprintQuestion` asks only when it printed.
   */
  const [reprintAsk, setReprintAsk] = useState<string | null>(null);
  /**
   * T11 / D173 — an 86'd dish in what is about to be sent: the dishes, and
   * the send to make if the cashier answers «إرسال رغم ذلك».
   */
  const [fireWarning, setFireWarning] = useState<{
    items: OutOfStockDraft[];
    proceed: () => void;
  } | null>(null);
  /**
   * T6 / D173 — an off-premise close while food is still cooking: the plates
   * the warning names and what its «إغلاق الفاتورة» goes on to, or null.
   */
  const [stillCooking, setStillCooking] = useState<{
    plates: InvoiceItem[];
    proceed: () => void;
  } | null>(null);
  /* Station names for that warning — read only while it is up. */
  const stationsQuery = useApiQuery<KitchenStation[]>(
    queryKeys.kitchenStations,
    endpoints.restaurant.kitchenStations,
    { enabled: stillCooking !== null, retry: false },
  );
  const localName = useLocalName();
  const isBusy = busyKey !== null;
  /**
   * Covers rework Phase 2b — the STEPPER's own optimistic value.
   *
   * `null` means "no pending local override, read `invoice.guestCount`
   * straight off the last fetch". Set the moment a +/− tap fires so the
   * number on screen moves instantly rather than waiting a round trip, and
   * cleared once `guestCountQueueRef`'s write queue is fully drained — see
   * that ref below and `createGuestCountWriteQueue`'s `onIdle`.
   */
  const [coversDisplayOverride, setCoversDisplayOverride] = useState<
    number | null
  >(null);

  /**
   * Menu sections for the artboard's category filter.
   *
   * This used to be gated behind `CATEGORY_VIEW`, which POS roles did not hold
   * — so the filter simply never appeared for the staff who use this screen,
   * and the request 403'd on every load. The permission is now part of WAITER
   * and CASHIER (`system-role.catalog.ts`): a menu SECTION is a category (D2),
   * and reading it is part of taking an order for the same reason reading the
   * items is. Creating and editing them stays a back-office act.
   */
  const categoriesQuery = useApiQuery<Category[]>(
    queryKeys.categories,
    endpoints.seller.categories,
  );

  /**
   * Shares the floor's cache rather than adding a request; it feeds the
   * panel's table row AND — since covers rework Phase 2c — the sanity hint's
   * seat counts. `TableGridEntry.seats` rides on THIS read on purpose:
   * `GET /restaurant/tables` (the back office's own table list) is
   * `RESTAURANT_FLOOR_MANAGE`-gated, which almost no one actually working
   * this screen holds, while the grid is `RESTAURANT_POS_USE` — the same
   * permission this whole screen requires. Duplicating one column onto a read
   * every waiter and cashier already makes is what lets the hint work for
   * them, rather than only for an owner.
   */
  const gridQuery = useApiQuery<TableGridArea[]>(
    queryKeys.grid,
    endpoints.restaurant.grid,
  );

  /**
   * The tier list, for its PERCENTAGE — the invoice snapshots only the tier's
   * id, and the addon picker has to quote what a plate will cost on this bill.
   * Same query key the floor's tier picker uses, so a till that just opened the
   * table reads it from cache rather than asking again, and it is not asked for
   * at all on an untiered bill (the common case).
   */
  const tiersQuery = useApiQuery<PosPriceTier[]>(
    queryKeys.posPriceTiers,
    endpoints.restaurant.posPriceTiers,
    { enabled: Boolean(invoiceQuery.data?.priceTierId), retry: false },
  );

  const settingsQuery = useApiQuery<RestaurantSettings>(
    queryKeys.settings,
    endpoints.restaurant.settings,
    // Receipt header/footer only; a failure here must not block billing.
    { retry: false },
  );
  /*
   * 2026-10-20 — the hand-over made explicit. Once the bag's driver ACCEPTED
   * it, the driver app waits for the cashier to hand it over, and that act is
   * the bill's close (T16): the close reads «تسليم إلى المندوب» instead of
   * «إغلاق الفاتورة». The board is the one the bill's dispatch strip reads
   * (same key, one request), for a session that may read it, own delivery only.
   */
  const dispatchAccess = useDispatchAccess();
  const loadedInvoice = invoiceQuery.data;
  const ownDeliveryLoaded =
    loadedInvoice?.channel === "DELIVERY" &&
    loadedInvoice.aggregatorId === null &&
    (loadedInvoice.status === "OPEN" || loadedInvoice.status === "CLOSED");
  const dispatchBoardQuery = useApiQuery<DispatchBoardResponse>(
    dispatchQueryKeys.board,
    endpoints.restaurant.dispatchBoard,
    { enabled: ownDeliveryLoaded && dispatchAccess.canSeeBoard, refetchOnWindowFocus: true },
  );
  const liveDispatch =
    ownDeliveryLoaded && dispatchAccess.canSeeBoard
      ? (dispatchByInvoice(dispatchBoardQuery.data).get(invoiceId)?.current ?? null)
      : null;
  const handOverDriverName =
    loadedInvoice?.status === "OPEN" && liveDispatch?.status === "ACCEPTED"
      ? liveDispatch.driverName
      : null;
  /* The Delivery tab's «تسليم» (`InvoiceHandoff.handOver`): captured at mount,
     acted on once by `HandOverHandoff` below, which runs the page's own
     `requestClose` once the bill and its workflow are known. */
  const [handoffHandOver] = useState<boolean>(
    () => (location.state as InvoiceHandoff | null)?.handOver === true,
  );
  const [handoffHandOverDone, setHandoffHandOverDone] = useState(false);
  /* An accepted online order's chits (`InvoiceHandoff.printFired`): captured at
     mount, queued once by `FiredChitHandoff` below once the bill and the
     settings are known. The ref keeps StrictMode's double effect to one queue. */
  const [handoffPrintFired] = useState<boolean>(
    () => (location.state as InvoiceHandoff | null)?.printFired === true,
  );
  const [handoffPrintFiredDone, setHandoffPrintFiredDone] = useState(false);
  const handoffPrintFiredQueued = useRef(false);
  /* The foot's «إسناد إلى مندوب» panel, and its command ids (kept across openings, D-10). */
  const [assigningDriver, setAssigningDriver] = useState(false);
  const [assignIds] = useState(() => new CommandIds());
  /*
   * D156 — the manager's password on the exception acts. The switch is read
   * off the settings this screen already holds (`MENU_VIEW`, which every till
   * session carries); while they load the till assumes OFF and the server's
   * own `RESTAURANT_APPROVAL_REQUIRED` opens the sheet instead.
   */
  const approvalGate = useManagerApproval({
    switchOn: settingsQuery.data?.managerApprovalRequired === true,
  });

  /*
   * F2 — the platform this bill came through, for the settle sheet's pill.
   *
   * `enabled` on a bill that HAS one, so an ordinary dine-in ticket never asks:
   * the same rule `tiersQuery` above follows, and the same one
   * `OffPremiseDialog` follows for the same list. Read is `RESTAURANT_MENU_VIEW`,
   * which both presets that can settle a bill carry.
   *
   * FAILS SOFT, deliberately. On an error the pill falls back to the generic
   * word rather than disappearing — a platform order that cannot be settled AS
   * a platform order is the defect this closes, and it must not come back
   * because a name lookup 500'd.
   */
  const aggregatorsQuery = useApiQuery<AggregatorChoice[]>(
    queryKeys.aggregatorChoices,
    endpoints.restaurant.aggregatorChoices,
    {
      // ...and for the charges correction (T6), which offers the list: read
      // when the dialog is open, so an ordinary ticket still never asks.
      enabled: Boolean(invoiceQuery.data?.aggregatorId) || chargesAsked,
      retry: false,
    },
  );
  /*
   * T16 — the strip's CLOSED bags, read only while the hand-over dialog is
   * open, for its "recent names" chips: who tonight's bags left with, off
   * the same read the floor draws, and stored nowhere else. Fails soft — a
   * close with no chips is still a close.
   */
  const offPremiseStripQuery = useApiQuery<OffPremiseGridEntry[]>(
    [...queryKeys.offPremiseOpen, "grid"],
    endpoints.restaurant.gridOffPremise,
    // Only a delivery asks who it left with (founder, 2026-10-08).
    { enabled: handOverAsked && invoiceQuery.data?.channel === "DELIVERY", retry: false },
  );
  const recentHandedTo = [
    ...new Set(
      (offPremiseStripQuery.data ?? [])
        .filter((row) => row.status === "CLOSED" && row.handedTo)
        // The server lists CLOSED oldest first; the chips want newest first.
        .reverse()
        .map((row) => row.handedTo as string),
    ),
  ].slice(0, 6);
  /* F2/T15 — the bill's platform, by NAME, for the settle sheets and the
     hand-over; the rate and base come off the BILL's snapshot, never the row. */
  const platformName =
    invoiceQuery.data?.aggregatorId
      ? (aggregatorsQuery.data?.find(
          (row) => row.id === invoiceQuery.data?.aggregatorId,
        )?.name ?? t((m) => m.pos.aggregatorMethod))
      : null;

  /**
   * D25's till hint: "is a drawer open", read from the same endpoint the
   * server refusal is keyed on. `retry: false` because a 401/403 here is an
   * answer (this route is `POS_USE`, which every till role holds, so a
   * failure means something is actually wrong) and retrying it only delays
   * the hint. `data === null` — not falsy — is the "no shift" case; a still-
   * loading query must not flash the warning before the answer is in.
   */
  const shiftCurrentQuery = useApiQuery<{
    id: string;
    openedAt: string;
  } | null>(
    queryKeys.shiftCurrent,
    endpoints.restaurant.shiftCurrent,
    // Enabled only once settings SAY tracking is on (D25: toggle off means
    // nothing changes anywhere — including no extra request on the app's
    // highest-traffic screen for every store that never opted in).
    {
      retry: false,
      enabled: settingsQuery.data?.shiftTrackingEnabled === true,
    },
  );

  /**
   * A diner's own claims to have paid this bill (D44).
   *
   * Refreshed by the same floor revisions as the invoice (and on focus),
   * because the claim arrives from a phone at the table with nothing on this
   * screen to announce it — a cashier who has to reload to find out that the
   * customer said they paid will settle in cash beside an unread transfer.
   *
   * Only for a holder of `RESTAURANT_INVOICE_SETTLE`, which is what the route
   * requires: asking with a waiter's session would 403 on every refresh.
   */
  const paymentsQuery = useApiQuery<InvoicePayment[]>(
    queryKeys.invoicePayments(invoiceId),
    endpoints.restaurant.invoicePayments(invoiceId),
    {
      /* Inlined rather than the `canSettle` below, which is declared after the
         early returns and so cannot be read by a hook. */
      enabled: hasPermission(session, PERMISSIONS.INVOICE_SETTLE),
      retry: false,
      refetchOnWindowFocus: true,
    },
  );

  /*
   * D153 — the «الكابتن» sheet's chips. Read only by a session that may
   * CHANGE the captain (`INVOICE_CLOSE`, the rung `setCaptain` checks; inlined
   * for the reason `paymentsQuery` above gives): the bill head shows the
   * captain's name off the bill itself and needs no team list.
   */
  const staffQuery = useApiQuery<StaffMember[]>(
    queryKeys.restaurantStaff,
    endpoints.restaurant.staff,
    { enabled: hasPermission(session, PERMISSIONS.INVOICE_CLOSE) },
  );
  const [choosingCaptain, setChoosingCaptain] = useState(false);

  useEffect(() => {
    const onFocus = () =>
      void queryClient.invalidateQueries({
        queryKey: queryKeys.invoice(invoiceId),
      });
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [queryClient, invoiceId]);

  const invoice = invoiceQuery.data;
  /* B6 · P26 — an own delivery's inline customer fields (the design's `cust`). */
  const contact = useDeliveryContactDraft(invoice);
  const contactSave = useRef<{ key: string; promise: Promise<Invoice | null | false> } | null>(null);
  const refundPrinting = useInvoiceRefundPrinting(invoice);
  const invoiceStatus = invoice?.status ?? null;

  /**
   * Two things must never happen behind a pane the cashier cannot see: a
   * stale-close recovery card that carries the only refresh button, and a bill
   * that has left OPEN (closed, settled, cancelled — each of which replaces the
   * panel with a state and an action). Either one pulls the bill forward at
   * narrow widths.
   *
   * Adjusted DURING RENDER on the edge, not in an effect. React's own
   * "adjusting state when a prop changes" pattern: an effect would render the
   * wrong pane once and then correct it — a visible flash of the menu over a
   * bill that has just been settled — and it is the cascading-render shape the
   * lint rule refuses. `wasDemandingBill` makes it an EDGE rather than a lock:
   * it fires when the demand appears and never again, so one tap on the summary
   * bar goes back to the menu and stays there, which is what the cashier
   * ringing up the next table wants.
   */
  const demandsBill =
    conflictKind !== null ||
    (invoiceStatus !== null && invoiceStatus !== "OPEN");
  // Seeded FALSE, not from `demandsBill`: an invoice that is already settled
  // when this screen mounts (opened from history, or served from cache) must
  // still trip the edge once and land on the bill.
  const [wasDemandingBill, setWasDemandingBill] = useState(false);
  if (demandsBill !== wasDemandingBill) {
    setWasDemandingBill(demandsBill);
    // The OVERRIDE, not a derived pane: `initialPane` already answers for a
    // bill that arrives closed or settled (UX-round D18), and this edge is what pulls
    // a person who has deliberately toggled to the menu forward when the bill
    // leaves OPEN under them, or when the recovery card appears.
    if (demandsBill) setPaneOverride("bill");
  }

  /**
   * Hidden sections do not exist at the till.
   *
   * Filtered here rather than server-side because `GET /seller/categories` is
   * the back-office listing and has to keep returning hidden sections — the
   * Categories screen is the only place they can be turned back on. The public
   * storefront listing filters on the server, where there is no such need.
   */
  const visibleCategories = useMemo(
    () => (categoriesQuery.data ?? []).filter((category) => category.isActive),
    [categoriesQuery.data],
  );

  const groupsById = useMemo(
    () =>
      new Map((addonGroupsQuery.data ?? []).map((group) => [group.id, group])),
    [addonGroupsQuery.data],
  );

  /**
   * The concession this bill was opened on, 0 when it carries no tier. Falls
   * back to a line the server already priced when the tier list is not in hand
   * — see `invoiceDiscountPercent`.
   */
  const discountPercent = invoice
    ? invoiceDiscountPercent(invoice, tiersQuery.data ?? [])
    : 0;

  /**
   * WHICH dish the addon picker is open for.
   *
   * Either one the waiter tapped on this screen (`addonTarget`), or the dish
   * handed over from the table view, which opens its picker as soon as both
   * caches have answered. DERIVED rather than synced into state by an effect:
   * the hand-off's picker is a function of the hand-off, and writing it into
   * state from an effect is the cascading render the lint rule forbids.
   */
  const pickerItem =
    addonTarget ??
    (handoff === "PICKER" && !handoffPickerDone ? handoffItem : null);

  /**
   * "I cannot tell whether this dish needs its picker" — the read failed, or
   * it has not come back yet. Both mean the same thing to every caller: do not
   * stage a plate, and do not offer to.
   */
  const addonGroupsUnknown = addonGroupsQuery.data === undefined;

  /**
   * Add one of `item` to the basket. Anything carrying an addon group asks
   * first — the server enforces min/max too, but a UUID-naming 400 is not
   * something to put in front of a waiter mid-service.
   */
  /**
   * B18 — the till's warning, and the reason it is a gate on the TAP rather
   * than on the append.
   *
   * The append is never refused: the server records what the kitchen had
   * called finished and puts the plate on the bill regardless, because the
   * founder's answer is that this warns. So the only thing the till owes the
   * waiter is the chance to know BEFORE they tell the guest yes, which is
   * exactly one dialog between the tap and the staging.
   *
   * Not remembered per dish. A waiter adding four of something the kitchen has
   * run out of is four separate promises to four separate guests, and a
   * "don't ask again" would silence the second through fourth.
   */
  const [warnTarget, setWarnTarget] = useState<MenuItem | null>(null);

  const stageFromMenu = (item: MenuItem) => {
    /*
     * Belt to the board's disabled state: with no addon groups in hand a
     * required-modifier check passes vacuously, so nothing may be staged at
     * all until the read SUCCEEDS.
     *
     * `addonGroupsUnknown`, not `isError`. The sentence above was already the
     * right rule and the guard did not implement it: a read still IN FLIGHT
     * also leaves no groups in hand, `resolveGroupsForItem` returned `[]` for
     * a dish that has a required group, and the plate was staged with no
     * selections. The server refused it — correctly — and the waiter got a
     * refusal for tapping a dish a moment too early.
     */
    if (addonGroupsUnknown) return;
    // Gated on the RESOLVED groups, not on the raw attachment list: a dish can
    // legitimately be left offering nothing from a group it carries, and
    // opening the picker on that read as a FAILED LOAD — "addons didn't load,
    // try again" — for a dish whose data was perfectly fine and a retry that
    // could never help.
    // On a phone every dish opens the picker (founder, 2026-10-06): the bill is
    // the other pane there, so a one-tap add bumped a count the waiter could
    // not see or take back. The picker shows what is being added, with its
    // quantity, and Cancel undoes it.
    if (
      resolveGroupsForItem(item, groupsById).length > 0 ||
      (paperTill && isMobile)
    ) {
      setAddonTarget(item);
      return;
    }
    // One tap, for every role (founder, 2026-10-03): the PAPER tile's other
    // acts are on its press-and-hold sheet, not on a forced picker. Wide
    // screens only, where the bill is on screen beside the menu.
    void addPlates(item.id, []);
  };

  const addFromMenu = (item: MenuItem) => {
    // The warning comes FIRST, before the addon picker: a waiter who is going
    // to swap the finished ingredient out should learn that before spending a
    // dialog choosing a size for a plate they may not order.
    if ((item.ingredientsMarkedOut ?? []).length > 0) {
      setWarnTarget(item);
      return;
    }
    stageFromMenu(item);
  };

  /**
   * "سعر مختلف" confirmed (D46, S9): if the item needs addons too, the
   * amount waits in `pendingOverride` for the addon picker's own confirm —
   * the two dialogs are sequential, never both on screen, so the item never
   * loses its price between them.
   */
  const confirmOverride = async (unitPrice: number, note: string) => {
    const item = overrideTarget;
    if (!item) return;
    // PAPER: an override asked FROM the picker goes back to it.
    if (pickerResume !== null || resolveGroupsForItem(item, groupsById).length > 0) {
      setPendingOverride({ unitPrice, note });
      setAddonTarget(item);
      setOverrideTarget(null);
      return;
    }
    const ok = await addPlates(item.id, [], unitPrice, note);
    if (ok) setOverrideTarget(null);
  };

  /**
   * 86 / un-86 the tapped item (§5.8, D32).
   *
   * A plain REST write beside the other menu-item routes, NOT an invoice
   * command: it changes the MENU, not this invoice — the no-CRUD rule governs
   * invoice state. It also writes `restaurant_menu_availability` and never
   * `Product.isActive`, so a dish that ran out tonight is not delisted from the
   * public storefront as a side effect of a decision made at 8pm.
   *
   * The menu query is invalidated on success, which is what flips the tile: the
   * board renders `item.isAvailable` straight from that cache.
   */
  /**
   * Puts a plate in the basket — on the SERVER, as a DRAFT (D17).
   *
   * This used to be React state, which is why the screen needed a `useBlocker`
   * and a `beforeunload` handler: a waiter who tapped back lost the order they
   * had just built. Both are gone. The cost is a round trip per tap, which the
   * command envelope makes safe to retry.
   */
  const addPlates = (
    productId: string,
    addonIds: string[],
    /**
     * D46 (S9): the price a `RESTAURANT_INVOICE_PRICE_OVERRIDE` holder typed
     * by hand, replacing the catalogue LIST price on this line. `undefined`
     * on every ordinary add — the server gates the field behind the same
     * permission, so sending it unconditionally would fail for everyone else.
     */
    unitPrice?: number,
    /**
     * WHY that price (founder decision 2026-09-03). Required by the server
     * whenever `unitPrice` is present — the dialog will not confirm without
     * it, so the pair always travels together from here.
     */
    unitPriceNote?: string,
    /**
     * Open ingredient swaps for this plate (D61). Omitted entirely on an
     * ordinary add — the server gates the field behind
     * `RESTAURANT_INVOICE_SUBSTITUTE`, so sending `[]` unconditionally would
     * be harmless but sending it populated from a till without the rung is a
     * 403, which is why the button is hidden rather than disabled.
     */
    substitutions?: {
      removesIngredientId: string;
      addsIngredientId: string;
    }[],
    /**
     * PAPER: how many identical plates (the picker's quantity stepper). The
     * line expands to that many plates on the server, one per unit.
     */
    quantity = 1,
    /** The kitchen note every plate of the line carries (kept on an Update). */
    note?: string | null,
  ) =>
    run(
      {
        type: "addItems",
        invoiceId,
        payload: {
          items: [
            {
              productId,
              quantity,
              // The wire shape is `addons: [{ addonId }]`, NOT a bare id list.
              // Sending `addonIds` silently dropped every modifier: the line
              // showed its addon locally and the server saw none.
              //
              // Repeats are MEANINGFUL here (S10): "extra chicken x2" is the
              // same id twice, and the server sums repeated ids in both the
              // price and the depletion fold.
              addons: addonIds.map((addonId) => ({ addonId })),
              ...(unitPrice !== undefined ? { unitPrice, unitPriceNote } : {}),
              ...(substitutions?.length ? { substitutions } : {}),
              ...(note ? { note } : {}),
            },
          ],
        },
      },
      { key: `add-${productId}` },
    );

  /**
   * Opens the swap dialog for a dish, fetching its pool.
   *
   * The failure is SHOWN rather than swallowed: an empty picker and a broken
   * read look identical to a waiter standing at a table, and one of them means
   * "this dish has no recipe" while the other means "try again".
   */
  const openSwap = async (menuItem: MenuItem) => {
    setSwapTarget(menuItem);
    setSwapOptions(null);
    setSwapLoadFailed(false);
    try {
      setSwapOptions(
        await apiFetch<SubstitutionOptions>(
          endpoints.restaurant.posSubstitutionOptions(menuItem.id),
        ),
      );
    } catch {
      setSwapLoadFailed(true);
    }
  };

  /**
   * What the amend dialog must say before the waiter confirms — or `null`.
   *
   * NULL and not an empty fragment: `<>{null}{null}</>` is a perfectly truthy
   * ReactNode, so a `notice ? … : null` test in the dialog rendered an EMPTY
   * paragraph on every ordinary amendment. Caught in the browser round, where
   * it looked like a stray gap above the buttons.
   *
   * Two sentences, either or both:
   *
   * - **Paper mode, and only once paper EXISTS** — in one of two strengths
   *   (B40/D85). The chit in the cook's hand cannot be updated, and the
   *   founder's decision (2026-09-10) is that the waiter walks to the pass and
   *   says so rather than the software refusing. On a `QUEUED` plate the chit
   *   is printed but nobody has claimed it, so the sentence is "tell the
   *   kitchen". On a `PREPARING` one the printer is what moved it there and a
   *   cook may genuinely have begun, so the sentence says so and asks them to
   *   speak to the kitchen FIRST.
   *
   *   Gated on the plate carrying a `kitchenTicketId` rather than on the
   *   store's mode alone: a basket plate in a printing kitchen has no chit yet,
   *   and telling a waiter to go and warn the kitchen about a plate the kitchen
   *   has never heard of is a sentence that teaches them to ignore the next
   *   one. A SCREEN kitchen needs nothing at all: the board re-reads the plate,
   *   and the server refuses `PREPARING` there anyway.
   * - **A row standing for several plates.** The change lands on ONE of them,
   *   which is almost always what was meant — one of the three people who
   *   ordered the same drink changed their mind — but it is not guessable from
   *   a row reading "×3".
   */
  const amendNotice = (group: PlateGroup) => {
    const printed =
      Boolean(settingsQuery.data?.kitchenPrintEnabled) &&
      group.plates[0].kitchenTicketId !== null;
    const lines = [
      printed
        ? group.fulfillmentState === "PREPARING"
          ? t((m) => m.pos.swapAmendKitchenMayHaveStarted)
          : t((m) => m.pos.swapAmendTellKitchen)
        : null,
      group.quantity > 1
        ? t(
            countedKey(group.quantity, {
              one: (m) => m.pos.swapAmendOnePlateOne,
              two: (m) => m.pos.swapAmendOnePlateTwo,
              few: (m) => m.pos.swapAmendOnePlateFew,
              many: (m) => m.pos.swapAmendOnePlate,
            }),
            { count: group.quantity },
          )
        : null,
    ].filter((line): line is string => line !== null);
    if (lines.length === 0) return null;
    return (
      <>
        {lines.map((line) => (
          <span key={line} className="mt-1 block first:mt-0">
            {line}
          </span>
        ))}
      </>
    );
  };

  /**
   * Opens the swap dialog on a plate that is already on the bill (B19).
   *
   * The same read as `openSwap` — the pool is per PRODUCT and a plate carries
   * its product id — so the two paths cannot drift about what may be swapped
   * onto a dish. What differs is only where the confirmation goes.
   */
  const openAmend = async (group: PlateGroup) => {
    setAmendTarget(group);
    setSwapOptions(null);
    setSwapLoadFailed(false);
    try {
      setSwapOptions(
        await apiFetch<SubstitutionOptions>(
          endpoints.restaurant.posSubstitutionOptions(group.productId),
        ),
      );
    } catch {
      setSwapLoadFailed(true);
    }
  };

  /**
   * The swaps a plate already carries, as the picker's own chips.
   *
   * Rebuilt from the plate's SNAPSHOT and only then looked up in the freshly
   * read pool, in that order: an ingredient retired since the plate was sold is
   * absent from the pool, and a swap that vanished from the dialog would be
   * silently dropped by the confirm — the payload is the plate's whole set, so
   * "not shown" would mean "removed". The snapshot always has the name and the
   * id; the pool only adds the unit and the live price when it still has them.
   */
  const seededSwaps = (
    group: PlateGroup,
    options: SubstitutionOptions | null,
  ): [SubstitutionRemovable, SubstitutionIngredient][] => {
    /*
     * POOL FIRST, REMOVABLE LAST, AND THE ORDER IS THE WHOLE POINT (D108).
     *
     * `Map` keeps the LAST entry for a duplicate key, and every removable row
     * IS a pool row with `recipeQuantity` added — the endpoint builds it by
     * spreading the pool entry. Spread the other way round and the pool copy
     * wins every lookup, the quantity is silently dropped, and a rate-priced
     * side resolves to `price × 0`: the amend dialog then quotes "free" on a
     * swap the bill has already charged 45 for. That shipped and was caught in
     * review.
     */
    const known = new Map<string, SubstitutionRemovable>(
      [...(options?.pool ?? []), ...(options?.removable ?? [])].map((row) => [
        row.ingredientId,
        row,
      ]),
    );
    const asIngredient = (
      ingredientId: string,
      name: string,
    ): SubstitutionRemovable =>
      known.get(ingredientId) ?? {
        ingredientId,
        name,
        // Unknown, and deliberately not guessed: the unit only filters the
        // ADD list, and this pair is already settled. `null` price shows no
        // figure rather than a wrong one — and with no price there is no rate
        // to multiply, so the missing `recipeQuantity` changes nothing here.
        unit: "",
        sellPrice: null,
      };
    return (group.plates[0].substitutions ?? []).map((substitution) => [
      asIngredient(substitution.removedIngredientId, substitution.removedName),
      asIngredient(substitution.addedIngredientId, substitution.addedName),
    ]);
  };

  /**
   * The item the waiter picked on the table view, which is what opened this
   * invoice. It is added HERE rather than there because adding needs this
   * screen's menu, addon groups and pricing.
   *
   * `handoffSent` guards the double-add that StrictMode's double effect would
   * otherwise cause; the router state is cleared in the same pass so a refresh
   * cannot replay it either.
   */
  useEffect(() => {
    if (!handoffProductId || handoffSent.current) return;
    /*
     * WAIT for an answer. `null` means the addon groups are still in flight,
     * and acting on "not yet known" is what staged an addon dish with no
     * selections and had the server refuse the waiter's first order of the
     * shift. `handoffSent` is not set in this pass, so the effect runs again
     * when the query settles — which is the point.
     */
    if (handoff === "WAIT") return;
    handoffSent.current = true;
    // The search too: the PAPER register's section (`?section=`) rides in it.
    navigate(location.pathname + location.search, { replace: true, state: null });
    // The PICKER case is handled by the render, through `pickerItem` — an
    // effect that opened it would be setState-in-effect. Only the plain add
    // belongs here.
    if (handoff === "PICKER") return;
    // The add-ons chosen on the table screen ride along; `[]` on a plain dish.
    void addPlates(
      handoffProductId,
      handoffAddonIds,
      undefined,
      undefined,
      undefined,
      handoffQuantity,
    );
    // addPlates is intentionally absent: the ref guard above already makes this
    // one-shot, and re-running on its identity would only risk a second add.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    handoffProductId,
    handoffAddonIds,
    handoffQuantity,
    handoff,
    handoffItem,
    navigate,
    location.pathname,
    location.search,
  ]);

  const refresh = async () => {
    setConflictKind(null);
    setReplayedRefundCommandId(null);
    await queryClient.invalidateQueries({
      queryKey: queryKeys.invoice(invoiceId),
    });
  };

  /*
   * One dispatch for every mutation on this screen, and the ONLY place that
   * mints a command id here.
   *
   * It prepares the command rather than calling `dispatchCommand` so the id can
   * come back to the caller: the refund slip prints a short form of it as the
   * refund's own reference (business finding 4), and that reference is only
   * citable because it is the primary key of `restaurant_invoice_commands`
   * rather than a number this screen invented.
   */
  const recoverableCommandSender = useRef<ReturnType<
    typeof createRecoverableCommandSender
  > | null>(null);
  if (recoverableCommandSender.current === null) {
    recoverableCommandSender.current = createRecoverableCommandSender();
  }
  const run = async (
    command: InvoiceCommand,
    options: {
      key: string;
      success?: string;
      /** D156 — a manager's approval for THIS send; see `runApproved`. */
      approval?: CommandApproval;
      /**
       * D156 — this send goes through the approval gate, so an approval
       * refusal is THROWN back to the sheet (which stays open on a wrong
       * password) instead of becoming a toast.
       */
      gated?: boolean;
      /**
       * B6 · P26 — a background save (the inline contact, on leaving a
       * field): it does not mark the screen busy, so the tap that moved the
       * focus — on Send, say — is not swallowed by a control disabled for it.
       */
      quiet?: boolean;
      /**
       * P4.1 — told when the answer is a replay over a bill that moved since
       * (`run` then shows the card and returns `null`), for a caller that
       * owes the cashier something for a command that DID go through — the
       * refund's slip.
       */
      onChangedReplay?: (result: CommandResult & { commandId: string }) => void;
    },
  ): Promise<(CommandResult & { commandId: string }) | null> => {
    if (!options.quiet) setBusyKey(options.key);
    try {
      const result = await recoverableCommandSender.current!(command, {
        approval: options.approval,
      });
      if (isChangedReplay(result)) {
        /*
         * P4.1 — this tap's command had already gone through (its first answer
         * was lost) and the bill has moved since. The bill in the answer is the
         * bill as it stands, not this command's outcome, so nothing here may
         * chain from it: not the next command's version (D9's guard would be
         * skipped), not a refund slip's figure, not the settle sheet's amount
         * taken, not the staff meal's comps. Same treatment as a stale bill —
         * the inline card, a refetch, the sheets closed — and `null`, which
         * every caller already reads as "stop here".
         */
        setConflictKind("replayed");
        options.onChangedReplay?.(result);
        await queryClient.invalidateQueries({
          queryKey: queryKeys.invoice(invoiceId),
        });
        await queryClient.invalidateQueries({ queryKey: queryKeys.grid });
        closeSettleSheet();
        closePaperSettleSheet();
        setOpenTakeawaySettleFor(null);
        // The refund sheet too, or the card renders behind it (finding 7).
        setRefundAsked(false);
        return null;
      }
      await invalidateInvoiceMutation(queryClient, result.invoice?.id ?? invoiceId);
      if (options.success) {
        toast.success(options.success);
      }
      return result;
    } catch (failure) {
      if (options.gated && isApprovalRefusal(failure)) {
        throw failure;
      }
      if (isRecoverableConflict(failure)) {
        // Not a generic failure: someone else moved this invoice (D22 — either
        // refusal). This one stays INLINE rather than becoming a toast — it is
        // a recovery state with an action, and it must still be on screen after
        // a toast would have auto-dismissed.
        // P9 — an expired replay went through long ago: the replayed card.
        setConflictKind(
          isExpiredCommand(failure)
            ? "replayed"
            : hasPendingItemsConflict(failure)
              ? "pending"
              : "version",
        );
        // The panel behind the card must show what actually happened, or the
        // cashier reads "this invoice changed" over the very data that is
        // wrong. Invalidation used to run on success only, so the recovery
        // card sat on top of the stale figures it was warning about.
        await queryClient.invalidateQueries({
          queryKey: queryKeys.invoice(invoiceId),
        });
        await queryClient.invalidateQueries({ queryKey: queryKeys.grid });
        // The settle sheet is a modal over the whole screen: leaving it open
        // means the recovery card renders BEHIND it and the cashier sees a
        // sheet that simply refused, with no explanation anywhere. The dark
        // till's direct-takeaway sheet is its own state (P4a review, found
        // with the replay card): closed here too.
        closeSettleSheet();
        closePaperSettleSheet();
        setOpenTakeawaySettleFor(null);
        // The refund sheet is a modal too (P4a review, finding 7).
        setRefundAsked(false);
      } else if (isItemNotReturnableRefusal(failure)) {
        // D141 — the server's ledger says a plate this screen called cooked
        // was not (or was already returned). Say which, in the server's words,
        // and refetch so the open sheet re-reads the plates' states; the sheet
        // stays open, so "wasted" and Confirm is one more tap, not a restart.
        toast.error(errorMessage(failure));
        await queryClient.invalidateQueries({
          queryKey: queryKeys.invoice(invoiceId),
        });
      } else if (isAlreadySettledRefusal(failure)) {
        // A settle refused because the bill is ALREADY paid: this screen is
        // provably stale (it offered Settle on a settled bill), so it gets
        // the conflict treatment, not just a toast — refetch so the paid
        // overlay renders, and close the sheet it renders under.
        toast.error(errorMessage(failure));
        await queryClient.invalidateQueries({
          queryKey: queryKeys.invoice(invoiceId),
        });
        await queryClient.invalidateQueries({ queryKey: queryKeys.grid });
        closeSettleSheet();
        closePaperSettleSheet();
      } else {
        toast.error(errorMessage(failure));
      }
      return null;
    } finally {
      if (!options.quiet) setBusyKey(null);
    }
  };

  /**
   * D156 — `run` through the approval gate: a void, a comp, a discount at the
   * bill or a refund returning stock opens «اعتماد المدير» first when this
   * session needs a manager's password, and is sent once with it attached.
   * Anything else passes straight through. `null` = cancelled or refused.
   */
  const runApproved = (
    command: InvoiceCommand,
    options: {
      key: string;
      success?: string;
      onChangedReplay?: (result: CommandResult & { commandId: string }) => void;
    },
  ) =>
    approvalGate.gate(command, (approval) =>
      run(command, { ...options, approval, gated: true }),
    );

  /**
   * Covers rework Phase 2b — ONE `setGuestCount` write in flight at a time.
   *
   * Lazily constructed on first render and kept for the life of this screen
   * (a `useRef`, not a `useState`: nothing about the queue itself is ever
   * rendered). It holds only the pump's own in-flight/pending STATE, never a
   * dispatcher closure — `request` takes `send` on every call instead, so
   * each tap always fires through the CURRENT render's `run`, exactly the way
   * an ordinary `onClick` does, with no ref-mirrors-latest-value indirection
   * needed to keep it fresh (and none of the stale-closure risk that
   * indirection would carry). See `createGuestCountWriteQueue`.
   */
  const guestCountQueueRef = useRef<ReturnType<
    typeof createGuestCountWriteQueue
  > | null>(null);
  if (guestCountQueueRef.current === null) {
    guestCountQueueRef.current = createGuestCountWriteQueue(() =>
      setCoversDisplayOverride(null),
    );
  }

  /**
   * A cashier's verdict on a diner's claim (D44).
   *
   * NOT through `run`: that dispatches an invoice COMMAND, and this is not one
   * — the bill does not move, its version does not change, and there is no
   * command id to be idempotent about. Reviewing a claim is a decision about a
   * claim; settling the bill afterwards is the command.
   */
  const reviewPayment = async (paymentId: string, verified: boolean) => {
    setBusyKey("payment-review");
    try {
      /* The OBJECT, not a string: `apiFetch` stringifies and sets the content
         type itself, so a pre-stringified body arrives as a JSON *string* and
         the endpoint rejects it. */
      await apiFetch(
        endpoints.restaurant.reviewInvoicePayment(invoiceId, paymentId),
        { method: "POST", body: { verified } },
      );
      await queryClient.invalidateQueries({
        queryKey: queryKeys.invoicePayments(invoiceId),
      });
    } catch (failure) {
      toast.error(errorMessage(failure));
    } finally {
      setBusyKey(null);
    }
  };

  /**
   * D133 — a diner's CASH tip into the cashier's own drawer, as the existing
   * `TIP_IN` movement. Keyed on the CLAIM's id: the movement id is its
   * idempotency key, so a second tap — or a second till — records nothing more.
   */
  const recordCashTip = async (payment: InvoicePayment): Promise<boolean> => {
    try {
      await apiFetch(endpoints.restaurant.shiftMovements, {
        method: "POST",
        body: {
          id: payment.id,
          kind: "PAID_IN",
          category: "TIP_IN",
          amount: payment.tipAmount,
          reason: t((m) => m.pos.dinerPaymentTipReason),
        },
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.shiftSummary });
      return true;
    } catch (failure) {
      toast.error(errorMessage(failure));
      return false;
    }
  };

  /**
   * D133 — open the Settle window on exactly the share a diner claimed: their
   * own dishes picked ("by item"), or the even share as a part payment. The
   * cashier still confirms the tender; nothing is settled by the claim.
   */
  const settleDinerShare = (payment: InvoicePayment) => {
    const method: SettlementMethod =
      payment.method === "INSTAPAY"
        ? "INSTAPAY"
        : payment.method === "CARD"
          ? "CARD"
          : "CASH";
    // The claim's id rides along (D133 fix): the server stamps the claim and
    // confirms it when the tender is the share unchanged.
    const seed = { amount: payment.amount, method, dinerPaymentId: payment.id };
    if (payment.split?.mode === "ITEMS" && canAppend) {
      setSettleSeed(seed);
      setSettlePicked(payment.split.itemIds);
    } else {
      setSettlePicked(null);
      setSettleSeed(seed);
    }
    requestPaperSettle();
  };

  /* BACK, at the start edge and before the title.
     An arrow, not an X: this returns to the floor the invoice was opened
     from — one step up the same tree — where the floor's own control
     LEAVES the till for the back office and is the X. Two different
     moves, two different glyphs.

     Mirrored under RTL by `rtl:-scale-x-100` rather than by swapping the
     glyph: "back" points at the start edge, right in Arabic and left in
     English.

     Hoisted so EVERY branch of this screen carries it. The error branch used
     to render a `Screen` with no leading at all, which on a till with no
     browser chrome is a dead end: an invoice that failed to load left the
     waiter with a retry button and no way back to the floor. */
  const backLink = (
    <Link
      to={`/${locale}/pos`}
      aria-label={t((m) => m.pos.backToTables)}
      title={t((m) => m.pos.backToTables)}
      data-ui="icon-button"
      className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[var(--radius-tile)] border border-[color:var(--border)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--surface-alt)] hover:text-[color:var(--text)]"
      data-testid="invoice-back-to-tables"
    >
      <svg
        width="17"
        height="17"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="rtl:-scale-x-100"
      >
        <path d="M19 12H5M12 19l-7-7 7-7" />
      </svg>
    </Link>
  );

  if (invoiceQuery.isLoading) {
    return (
      <Screen tone="dark" title={t((m) => m.common.loading)} leading={backLink}>
        <div
          aria-busy="true"
          aria-live="polite"
          aria-label={t((m) => m.common.loading)}
        >
          <LoadingSkeleton variant="detail" rows={4} />
        </div>
      </Screen>
    );
  }
  if (!invoice) {
    return (
      <Screen tone="dark" title={t((m) => m.common.error)} leading={backLink}>
        <ErrorState
          // Through `errorMessage`, never `error.message`: a dropped
          // connection otherwise puts the browser's own English "Failed to
          // fetch" in front of an Arabic-speaking waiter.
          message={
            invoiceQuery.error
              ? errorMessage(invoiceQuery.error)
              : t((m) => m.common.error)
          }
          retryLabel={t((m) => m.common.retry)}
          onRetry={() => void invoiceQuery.refetch()}
        />
      </Screen>
    );
  }

  const canAppend = hasPermission(session, PERMISSIONS.INVOICE_APPEND);
  // D121 — with the store's "orders go through the cashier" switch on, only
  // the cashier's rung sends; everyone else gets "Send to cashier" (D120).
  const canFire = canSendToKitchen(session, settingsQuery.data?.ordersViaCashier);
  // D121 — answering a diner's QR request sends food to the kitchen too.
  const canAnswerRequests = canAnswerQrRequests(
    session,
    settingsQuery.data?.ordersViaCashier,
  );
  const canServe = hasPermission(session, PERMISSIONS.ITEM_SERVE);
  /*
   * The kitchen's two transitions, reachable from the till (round 8).
   * `startPreparing`/`markReady` used to be issued from the kitchen board
   * ALONE, so a store whose display-mode board is dead — or a cashier who
   * holds ITEM_PREPARE and no board to open — had QUEUED and PREPARING plates
   * with no actuator anywhere, and D22 refuses to close an invoice over a
   * QUEUED plate. The permission is the same one the board checks: this is a
   * second surface for the same rung, not a new authority.
   */
  const canPrepare = hasPermission(session, PERMISSIONS.ITEM_PREPARE);
  const canPrint = hasPermission(session, PERMISSIONS.KITCHEN_PRINT);
  const canRemake = hasPermission(session, PERMISSIONS.INVOICE_REMAKE);
  const canVoid = hasPermission(session, PERMISSIONS.INVOICE_VOID);
  const canClose = hasPermission(session, PERMISSIONS.INVOICE_CLOSE);
  /* The acts CLOSE and VOID carried until 2026-11-02, each its own rung now,
     so every control below is gated on exactly what its command requires. */
  const canComp = hasPermission(session, PERMISSIONS.INVOICE_COMP);
  const canDiscount = hasPermission(session, PERMISSIONS.INVOICE_DISCOUNT);
  const canCancelBill = hasPermission(session, PERMISSIONS.INVOICE_CANCEL);
  const canReopen = hasPermission(session, PERMISSIONS.INVOICE_REOPEN);
  const canEditCharges = hasPermission(session, PERMISSIONS.INVOICE_CHARGES_EDIT);
  const canWriteOff = hasPermission(session, PERMISSIONS.INVOICE_WRITE_OFF);
  const canSettle = hasPermission(session, PERMISSIONS.INVOICE_SETTLE);
  /*
   * A DIRECT TAKEAWAY — the counter's own bag, no delivery app (founder,
   * 2026-10-08). It always leaves with the customer, who pays as they take it,
   * so it is paid and handed over in one act like a table, with no hand-over
   * dialog and no name. See `requestClose`.
   */
  const directTakeaway = invoice.channel === "TAKEAWAY" && invoice.aggregatorId === null;
  /*
   * An OPEN direct takeaway the customer has ALREADY PAID IN FULL — paid
   * upfront while the kitchen cooked (D126). Nothing is owed, so there is no
   * money left to take: the one act left is the close, which the server lands
   * on SETTLED. `outstandingOnBill` reads the live total on an open bill, the
   * same figure the settle sheet quotes.
   */
  const openTakeawayPaid =
    directTakeaway &&
    invoice.status === "OPEN" &&
    invoice.tenderedTotal > 0 &&
    outstandingOnBill(invoice) <= 0;
  /*
   * The dark till's form of that one act (founder, 2026-10-08: "make the dark
   * till behave like the light one"): the close control reads Settle and opens
   * the whole-bill pay sheet ON THE OPEN BILL. Like PAPER's Settle it does not
   * wait for the kitchen — with plates still unsent or at the kitchen the money
   * is taken and the bill stays open (D126); otherwise the sheet closes and
   * settles in one act. PAPER does it through its own window
   * (`paperSettlesOpenBill`); without both rungs it is a plain close.
   */
  const darkSettlesOpenTakeaway =
    !paperTill &&
    directTakeaway &&
    invoice.status === "OPEN" &&
    canClose &&
    canSettle &&
    !openTakeawayPaid;
  /*
   * Who can actually answer "no shift is open". `RESTAURANT_SHIFT_MANAGE` is
   * in NO preset role (D24), so this is false for the ordinary cashier — and
   * the settle warning has to say something they can act on.
   */
  const canManageShift = hasPermission(session, PERMISSIONS.SHIFT_MANAGE);
  /**
   * Manual price at the till (D46, S9). In NO preset role — see
   * `PERMISSIONS.PRICE_OVERRIDE`'s own comment — so this is false for every
   * ordinary waiter and cashier, and `MenuBoard` is not given `onOverride` at
   * all when it is.
   */
  const canOverride = hasPermission(session, PERMISSIONS.PRICE_OVERRIDE);
  /*
   * R9 — the refund rung. Its OWN permission, in NO preset role below OWNER,
   * because refunding a settled bill and pocketing the cash is the most direct
   * theft path in a restaurant POS and the drawer still balances afterwards.
   * A store grants it per person through a custom role, which is why almost
   * every cashier reading this screen will not see the control at all. The
   * server checks the same rung before it claims the command id; hiding the
   * button is the courtesy, not the gate.
   */
  const canRefund = hasPermission(session, PERMISSIONS.INVOICE_REFUND);

  // Hidden, not disabled: the rung is in no preset below OWNER, so for almost
  // every till the swap button is an action they will never have. A disabled
  // control would be permanent furniture advertising an authority they cannot
  // get from this screen.
  // Amending a plate's swaps (B19) is the SAME rung and not a new one: a
  // waiter who may swap at order time may swap thirty seconds later, and the
  // server requires exactly this permission for `substituteItem`.
  const canSubstitute = hasPermission(session, PERMISSIONS.INVOICE_SUBSTITUTE);

  /*
   * Three groups, because a waiter can do three different things with them.
   * `drafts` are theirs to edit freely; `sentItems` are on the bill and behind
   * the void dialog; `pendingItems` are a diner's request awaiting a decision.
   */
  const drafts = invoice.items.filter(
    (item) => item.fulfillmentState === "DRAFT",
  );

  /*
   * D141 — the plates a void of `count` acts on (the newest N, the same slice
   * the command sends), each with its state as the LIVE bill now has it, or
   * as it was when the sheet opened for a plate the latest read no longer
   * lists. Eligibility for "returned to stock" is read from this.
   */
  const voidPlates = (
    count: number,
  ): { id: string; fulfillmentState: InvoiceItemFulfillment }[] => {
    if (!voidTarget) return [];
    const offset = Math.max(0, voidTarget.plateIds.length - count);
    return voidTarget.plateIds.slice(offset).map((id, index) => ({
      id,
      fulfillmentState:
        invoice.items.find((item) => item.id === id)?.fulfillmentState ??
        voidTarget.fulfillmentStates[offset + index],
    }));
  };
  const sentItems = invoice.items.filter(
    (item) =>
      item.fulfillmentState !== "PENDING" && item.fulfillmentState !== "DRAFT",
  );
  const pendingItems = invoice.items.filter(
    (item) => item.fulfillmentState === "PENDING",
  );
  const isOpen = invoice.status === "OPEN";
  /* "Guest 1", "Guest 2"… in order of first request, over EVERY plate on the
     bill so the numbers do not shift as requests are accepted. Shown only when
     more than one phone has ordered here: with one there is nobody to tell
     apart, and a lone "Guest 1" on every row is noise. */
  const guestNumbers = new Map<string, number>();
  for (const item of invoice.items) {
    if (item.guestId && !guestNumbers.has(item.guestId)) {
      guestNumbers.set(item.guestId, guestNumbers.size + 1);
    }
  }
  const guestNumberOf = (item: InvoiceItem): number | null =>
    guestNumbers.size > 1 && item.guestId
      ? (guestNumbers.get(item.guestId) ?? null)
      : null;

  /*
   * Everything D22 refuses to close over: an unanswered request, an unfired
   * basket, and food the kitchen was told about but never started. Hoisted out
   * of the panel's props because the narrow-width summary bar has to enable its
   * own close button on exactly the same answer.
   */
  const queuedPlates = sentItems.filter((item) => item.fulfillmentState === "QUEUED");
  /* T6 / D173 — the same three, by kind, so a greyed Close can say which. */
  const closeBlocking: CloseBlocking = {
    pending: pendingItems.length,
    drafts: drafts.length,
    queued: queuedPlates.length,
  };
  const blockingCount =
    closeBlocking.pending + closeBlocking.drafts + closeBlocking.queued;
  /* D126 — what PAPER's window is told as `keepOpen`: with anything the close
     would refuse over, a payment is taken without closing the bill. */
  const payKeepsOpen = invoice.status === "OPEN" && blockingCount > 0;

  /* The two figures the summary bar states, and the act it offers — all three
     from the same pure helpers the order panel reads, so a bar under the panel
     can never disagree with the panel. */
  const { runningTotal, lineCount } = tillTotals({
    invoiceTotal: invoice.total,
    status: invoice.status,
    sentItems,
    drafts,
    charges: invoiceChargeRates(invoice),
    storedCharges: invoice,
    deliveryFee: invoice.deliveryFee,
    deliveryFeeVat: invoice.deliveryFeeVat,
    // D146 — the bill bar quotes the same discounted total as the panel.
    billDiscount: billDiscountPricingOf(invoice),
  });
  const tillPrimary = tillPrimaryAction({
    status: invoice.status,
    draftCount: drafts.length,
    canFire,
    canSettle,
    canRequestCashier: canAppend,
  });
  /** D120 — the waiter's "Send to cashier": the order is ready for the cashier. */
  const requestCashier = () =>
    void run(
      { type: "requestCashier", invoiceId: invoice.id, payload: {} },
      { key: "request-cashier", success: t((m) => m.pos.cashierRequestedToast) },
    );
  /* The close is its own control now (UX-round D15), so it is its own question — and
     `tillCloseAction` is the single copy of the D22 gate that the panel's foot
     and the narrow bar both read. */
  const tillClose = tillCloseAction({
    status: invoice.status,
    blocking: closeBlocking,
    canClose,
  });
  /* D164 — the store's kitchen workflow, as the settings read states it. */
  const kitchenWorkflow = settingsWorkflow(settingsQuery.data);
  /*
   * Until the settings read answers, the workflow is not known: `settingsWorkflow`
   * would read «بدون شاشة أو طابعة» and offer a start in a store whose paper is
   * the actuator. Nothing that starts food is offered until it is known — and a
   * FAILED read is not known either (P7 re-gate #3): no line «بدء», no tap
   * beside the greyed Close, which still says why it is greyed.
   */
  const workflowKnown = settingsQuery.data !== undefined;
  /* T6 / D173 — the one tap beside a greyed Close, by workflow and rung. */
  const closeUnblock = workflowKnown
    ? closeUnblockAction({
        reason: tillClose?.reason ?? null,
        workflow: kitchenWorkflow,
        canPrepare,
        canClose,
        canPrint,
      })
    : null;
  /* The pane the person asked for, or the one this bill's state asks for. */
  // A session that cannot add to the bill gets no menu at all (founder,
  // 2026-10-06: "if the action is not doable, remove it"), so it opens on the
  // bill and stays there.
  const narrowPane = !canAppend
    ? "bill"
    : (paneOverride ?? initialPane(invoice.status));

  /*
   * The server's cancel gate (D22's cousin), mirrored so the button only
   * appears where the command would land: cancel means "nothing happened at
   * this table", so any plate the kitchen was told about — whatever its
   * billing — kills it, and so does an unanswered buyer request. Drafts and
   * already-VOIDED plates don't: nobody cooked either. `sentItems` excludes
   * PENDING and DRAFT by construction, so non-VOIDED sent === committed.
   * Its own rung, `INVOICE_CANCEL` (it rode on `INVOICE_CLOSE` until
   * 2026-11-02).
   */
  const canCancel =
    isOpen &&
    canCancelBill &&
    pendingItems.length === 0 &&
    sentItems.every((item) => item.fulfillmentState === "VOIDED");

  /**
   * Menu-tile badge: what this product already has on the order.
   *
   * Counts drafts too — the waiter who just tapped it wants to see it land,
   * and whether the kitchen has heard about it yet is not what the badge is
   * answering.
   */
  const countsByProductId = new Map<string, number>();
  for (const item of invoice.items) {
    if (item.fulfillmentState !== "DRAFT" && !isChargeable(item)) continue;
    countsByProductId.set(
      item.productId,
      (countsByProductId.get(item.productId) ?? 0) + 1,
    );
  }

  /**
   * D172 — a reprint as a DURABLE job on the station's printer, with the
   * till's answer as its reason — but only while a LIVE print computer serves
   * the station (gate P7 rework 2, R3: `liveDeviceOnly`). Otherwise the server
   * answers 409 (`RESTAURANT_STATION_NO_PRINTER` with no active printer,
   * `RESTAURANT_STATION_PRINTER_OFFLINE` with every computer switched off),
   * nothing is written, and the browser chit prints it HERE, as an ORIGINAL
   * for a paper that never arrived and a REPRINT («نسخة مكررة») for an extra
   * copy, carrying the answer so the server can mark a second paper and close
   * the device jobs it fulfils — then says «طُبعت التذكرة على هذا الجهاز».
   * «أُرسلت التذكرة إلى طابعة المطبخ» only when a live computer took the job.
   * Any other refusal is a toast.
   */
  const reprintChit = async (ticketId: string, reason: ReprintReason, seenPrintCount: number) => {
    setBusyKey(`reprint-${ticketId}`);
    try {
      await recoverableCommandSender.current!({
        type: "reprintTicket",
        invoiceId: invoice.id,
        // What the till read before asking: a paper confirmed after it was
        // vouched for by nobody, so the server marks the reprint (D172).
        payload: { ticketId, reason, seenPrintCount, liveDeviceOnly: true },
      });
      toast.success(t((m) => m.reprintQuestion.sentToPrinter));
    } catch (failure) {
      if (isStationWithoutPrinter(failure)) {
        setPrintQueue((current) => [
          ...current,
          { ticketId, action: reprintFallbackAction(reason), reason, seenPrintCount },
        ]);
      } else {
        toast.error(errorMessage(failure));
      }
    } finally {
      setBusyKey(null);
    }
  };

  /**
   * Send the basket to the kitchen (D18).
   *
   * An empty `itemIds` means "everything in the basket", which is what this
   * button is; naming ids is for coursing a subset later. Where no kitchen is
   * configured the server starts the food itself, so this one tap is the whole
   * flow for a small cafe.
   */
  const fire = async (itemIds?: string[]) => {
    const result = await run(
      {
        type: "fireItems",
        invoiceId: invoice.id,
        // Empty payload = the whole basket; naming ids is the coursing path
        // (a group's "send these" chip).
        payload: itemIds ? { itemIds } : {},
      },
      { key: "fire", success: t((m) => m.pos.firedToast) },
    );
    if (!result?.invoice) return result;

    /*
     * In print mode the chit IS the actuator (D18): nothing starts cooking
     * until paper exists, and the person holding this screen is the only one
     * with a fire button. So a successful fire hands every still-queued
     * ticket straight to the print queue rather than leaving the food
     * stranded behind a control that lives on a board this role cannot open.
     * A plate already PREPARING (display mode, or no kitchen at all) needs no
     * paper to start, so nothing prints there unless asked.
     *
     * `atFire`: a station whose printer a paired print computer serves gets
     * its ORIGINAL from that device, so this chit stands down there
     * (`ChitPrintJob`, `deviceServed`) — never two papers for one fire.
     */
    queueFireChits(result.invoice.items);
    return result;
  };
  /**
   * The at-fire chits for every ticket still holding a QUEUED plate — after
   * the till's own fire, and after an accepted online order whose food the
   * server fired (`InvoiceHandoff.printFired`). Only where printing is on and
   * this tab is not itself the print computer; a ticket already waiting in the
   * queue is not queued twice, and a station a live device serves stands down
   * in `ChitPrintJob` (`deviceServed`).
   */
  const queueFireChits = (items: readonly InvoiceItem[]) => {
    // A seat without KITCHEN_PRINT can fire or accept but cannot read or print a
    // ticket: queueing would only end in a 403 toast.
    if (!canPrint || !settingsQuery.data?.kitchenPrintEnabled || ownsAutomaticPrinting) return;
    const ticketIds = [
      ...new Set(
        items
          .filter((item) => item.fulfillmentState === "QUEUED" && item.kitchenTicketId !== null)
          .map((item) => item.kitchenTicketId as string),
      ),
    ];
    if (ticketIds.length === 0) return;
    setPrintQueue((current) => [
      ...current,
      ...ticketIds
        .filter((ticketId) => !current.some((job) => job.ticketId === ticketId && job.atFire))
        .map((ticketId) => ({ ticketId, atFire: true })),
    ]);
  };

  /**
   * D154 — «ابعت وجبة الطاقم»: the crew's meal in one tap, through the SAME
   * commands a cashier would send by hand, in their order and each through the
   * envelope: fire the basket (with this screen's print hand-off), comp every
   * plate the kitchen now holds as `STAFF_MEAL`, then — only when D22 allows
   * it (no plate still queued for a cook) — close and settle at 0.00, the
   * honest end of an all-comped bill. Any refusal stops the chain where it
   * happened, said by `run`; what already landed stays landed, and the banner
   * stays on the bill so the next tap picks up from there.
   */
  const sendStaffMeal = async () => {
    const fired = drafts.length > 0 ? await fire() : null;
    if (drafts.length > 0 && !fired?.invoice) return;
    let latest = fired?.invoice ?? invoice;
    const compIds = staffMealPlatesToComp(latest.items);
    if (compIds.length > 0) {
      // D156 — the chain asks once, here, at its comp step.
      const comped = await runApproved(
        {
          type: "compItem",
          invoiceId: invoice.id,
          payload: { itemIds: compIds, reasonCode: STAFF_MEAL_REASON },
        },
        { key: "staffMeal" },
      );
      if (!comped?.invoice) return;
      latest = comped.invoice;
    }
    if (!staffMealCanCloseNow(latest.items)) {
      toast.success(t((m) => m.pos.staffMealSentOpenToast));
      return;
    }
    const closed = await run(
      {
        type: "closeInvoice",
        invoiceId: invoice.id,
        payload: { expectedVersion: latest.version },
      },
      { key: "staffMeal" },
    );
    if (!closed?.invoice) return;
    if (closed.invoice.status === "CLOSED") {
      const settled = await run(
        {
          type: "settleInvoice",
          invoiceId: invoice.id,
          payload: {
            expectedVersion: closed.invoice.version,
            settlementMethod: "CASH",
            settlementAmount: 0,
          },
        },
        { key: "staffMeal" },
      );
      if (!settled?.invoice) return;
    }
    toast.success(t((m) => m.pos.staffMealSentToast));
  };

  /**
   * T11 / D173 — every door that fires asks first when an 86'd dish is in
   * what it sends (`outOfStockDrafts`), and sends it anyway on «إرسال رغم
   * ذلك»: stock never blocks a sale (D3).
   */
  const warnOutOfStock = (pool: readonly InvoiceItem[], proceed: () => void) => {
    const out = outOfStockDrafts(pool, menuQuery.data);
    if (out.length === 0) {
      proceed();
      return;
    }
    setFireWarning({ items: out, proceed });
  };

  /**
   * CLOSE THE BILL — one definition, because the close now has two ways in
   * (B118).
   *
   * The covers prompt interrupts a close and has to be able to FINISH it (see
   * the order panel's `onClose`), so the command can no longer live only in
   * the button that used to be its only caller.
   *
   * `expectedVersion` is a parameter and not `invoice.version` read from the
   * closure, and that is the whole reason this takes an argument: the covers
   * answer is itself a command, `setGuestCount` increments the bill's version,
   * and a close chained behind it with the version this render captured would
   * be refused as a stale write — the cashier would answer the question and
   * then be shown a conflict card for her trouble.
   */
  const closeBill = async (
    expectedVersion: number,
    /** T16 — who the bag left with, on an off-premise close; absent otherwise. */
    handedTo: string | null = null,
    /** `undo: false` when a settle follows at once (platform hand-over). */
    options: { undo: boolean } = { undo: true },
  ) => {
    const result = await run(
      {
        type: "closeInvoice",
        invoiceId: invoice.id,
        // The version THIS device observed. The server rejects it if the
        // invoice has moved since.
        payload: { expectedVersion, ...(handedTo ? { handedTo } : {}) },
      },
      // No `success` here: this close raises its own toast, because the toast
      // IS the undo (UX-round D17) and it has to carry the version the close returned.
      { key: "close" },
    );
    if (!result) return result;

    /*
     * THE UNDO, AND WHY IT IS NOT A TOAST THAT LIES (UX-round D17, ruling E2).
     *
     * Three things make it a mechanism rather than a promise:
     *
     * 1. It dispatches `reopenInvoice` through `run`, i.e. through the COMMAND
     *    ENVELOPE, with an id, idempotency and the version guard — the same
     *    road the permanent «فتح تاني» takes. An undo that reached past the
     *    envelope would be a boundary violation, not a UX detail, and the
     *    ledger would show a mutation where a command belongs.
     * 2. The version it guards with is the one the CLOSE returned, not the one
     *    this render captured — the bill moved when it closed. Without that,
     *    every undo would be refused as a stale write.
     * 3. It can FAIL, and both ways of failing are spoken. Past the window:
     *    `pos.closeUndoExpired`, naming where the permanent reopen lives. A
     *    refusal from the server (already settled elsewhere, version moved,
     *    grant withdrawn mid-shift): `run` shows the reason and this adds what
     *    the person actually needs to know — the bill is still closed, and
     *    where the other door is.
     *
     * The window is only offered where it can be honoured: reopening is
     * INVOICE_REOPEN server-side, which is exactly `canReopen`.
     */
    const closedVersion = result.invoice?.version ?? null;
    if (closedVersion === null || !canReopen || !options.undo) {
      toast.success(t((m) => m.pos.closedToast));
      return result;
    }
    const undoDeadline = closeUndoDeadline();
    toast.success(
      t((m) => m.pos.closedToast),
      undefined,
      {
        durationMs: CLOSE_UNDO_WINDOW_MS,
        action: {
          label: t((m) => m.pos.reopen),
          onClick: () => {
            // The card's timer pauses under a resting pointer, so this button
            // can still be on screen after the window it promised. It says so
            // instead of quietly reopening a bill minutes later.
            if (Date.now() > undoDeadline) {
              toast.error(t((m) => m.pos.closeUndoExpired));
              return;
            }
            void (async () => {
              const undone = await run(
                {
                  type: "reopenInvoice",
                  invoiceId: invoice.id,
                  payload: { expectedVersion: closedVersion },
                },
                { key: "reopen", success: t((m) => m.pos.reopenedToast) },
              );
              if (!undone) toast.error(t((m) => m.pos.closeUndoFailed));
            })();
          },
        },
      },
    );
    return result;
  };

  /**
   * THE ONE WAY IN TO A CLOSE, whichever control was tapped (UX-round D15).
   *
   * The panel's foot and the narrow summary bar both end up here: the bar used
   * to dispatch `closeInvoice` itself, which meant the covers prompt below
   * existed on one surface and not the other — a bill closed from the phone
   * bar lost its cover count in silence.
   */
  const requestClose = (fresh?: Invoice | null) => closeFrom(fresh ?? invoice);
  /**
   * `live` is the bill to close as the server last answered it: this render's
   * `invoice` for a tap on Close, and the bill `startPreparingAtTill` returned
   * for «بدء التحضير والإغلاق» (T6) — whose version and plates moved after
   * this render was drawn.
   */
  const closeFrom = (live: Invoice, options: { cookingWarned?: boolean } = {}) => {
    /*
     * B117 round table 2, Q6 — ASK ONCE FOR COVERS, AND LET IT BE DISMISSED.
     *
     * A bill closed with covers unset loses that count for good: after the
     * close it is a supervisor's correction, and nobody goes back for a number
     * they never had. The head waiter asked for the prompt; every persona
     * independently insisted it be skippable, in the same words — a number
     * invented under pressure at 23:50 is worse than a blank, and the blank is
     * a real answer the nullable column exists to record ("nobody was asked").
     *
     * ONCE, on the first attempt only: `coversAsked` latches, so a cashier who
     * dismissed it and tapped close again is not asked a second time. A prompt
     * that keeps coming back IS an unskippable prompt with extra taps.
     *
     * B118 — AND THE CLOSE IS RESUMED AFTERWARDS, not dropped. The dialog's
     * confirm carries it through on the version the covers write returns; see
     * `closeAfterCovers`.
     *
     * Covers rework Phase 1 — DINE_IN ONLY. A takeaway bag and a delivery
     * order carry no table and no cover count at all (`guestCountLabel`
     * itself never renders on those channels below), so interrupting a
     * takeaway close with "how many people were at the table?" would ask a
     * question the bill structurally cannot answer. See `shouldPromptForCovers`.
     */
    if (
      shouldPromptForCovers({
        channel: live.channel,
        guestCount: live.guestCount,
        coversAsked,
      })
    ) {
      setCoversAsked(true);
      setCloseAfterCovers(true);
      setCountingGuests(true);
      return;
    }
    /*
     * T16 — an OFF-PREMISE close is the moment the bag leaves. THREE CASES
     * (founder, 2026-10-08):
     *
     *  - a DIRECT takeaway always leaves with the customer at the counter, who
     *    pays as they take it — so no hand-over and no name: it is paid and
     *    handed over in ONE act, like a table. For a holder of both rungs that
     *    is the pay sheet on the OPEN bill (PAPER's window, or the dark till's
     *    whole-bill sheet), which takes the money while the kitchen cooks; once
     *    it is paid in full it is a plain close that lands SETTLED; without the
     *    settle rung a plain close. Nothing is written to `handedTo` — the
     *    channel already says it.
     *  - a takeaway a delivery app brought: the hand-over without the name
     *    question, offering only "Close & hand to ⟨platform⟩".
     *  - a DELIVERY: the whole hand-over — who it left with, recent riders,
     *    and the platform's one tap on a platform bill.
     *
     * The dialog's own confirm calls `closeBill`.
     */
    if (live.channel !== "DINE_IN") {
      // An EMPTY bag is cancel-only (founder, 2026-10-07): the server refuses
      // the close with RESTAURANT_INVOICE_EMPTY, and the till says so before
      // any dialog or sheet opens.
      if (!live.items.some((item) => isRungOnTheBill(item))) {
        toast.error(t((m) => m.pos.offPremiseEmptyClose));
        return;
      }
      if (directTakeaway) {
        if (paperTill && paperSettlesOpenBill) {
          requestPaperSettle();
          return;
        }
        if (darkSettlesOpenTakeaway) {
          setOpenTakeawaySettleFor(invoice.id);
          return;
        }
        // Paid in full while it was open: the close lands SETTLED (D126), so
        // there is nothing an undo could reopen — no undo toast.
        void closeBill(live.version, null, { undo: !openTakeawayPaid });
        return;
      }
      /*
       * T6 / D173 — food still cooking for a bag that is about to leave: warn,
       * never block, naming dish and station. Only where the workflow records
       * READY (in a paper workflow nobody taps «جاهز», so every bag would
       * warn); a direct takeaway returned above, so it never warns.
       */
      const cooking = live.items.filter((item) => item.fulfillmentState === "PREPARING");
      if (!options.cookingWarned && recordsReady(kitchenWorkflow) && cooking.length > 0) {
        setStillCooking({ plates: cooking, proceed: () => setHandOverAsked(true) });
        return;
      }
      setHandOverAsked(true);
      return;
    }
    void closeBill(live.version);
  };

  /**
   * T6 / D173 — THE ONE TAP THAT CLEARS A GREYED CLOSE. D22 is untouched: the
   * till never closes over a QUEUED plate, it clears the plate first.
   *
   *  - `startAndClose` (screen workflows): `startPreparingAtTill` for every
   *    QUEUED plate — its own command type, so the server flags the start as
   *    the till's and the speed report leaves it out (no client boolean) — and
   *    then the ordinary close (`closeFrom`, the path `requestClose` takes:
   *    covers, hand-over, undo) on the bill the start returned.
   *    From the PAPER settle window (`closeAfter: false`) only the start is
   *    sent: the window's own Pay closes and settles the bill.
   *  - `resendTicket` (paper workflows): `reprintTicket NOT_RECEIVED` for each
   *    ticket holding QUEUED plates; a station with no printer prints the
   *    browser chit (`reprintChit`'s 409 path). The paper starts the food —
   *    the till never does it silently.
   */
  const startThenClose = async (closeAfter: boolean, cookingWarned = false) => {
    const started = await run(
      {
        type: "startPreparingAtTill",
        invoiceId: invoice.id,
        payload: { itemIds: queuedPlates.map((item) => item.id) },
      },
      { key: "close-unblock" },
    );
    if (!started?.invoice || !closeAfter) return;
    closeFrom(started.invoice, { cookingWarned });
  };
  /**
   * Gate P7 ruling (review #5) — ONE confirm, BEFORE acting. A bag that is
   * about to leave in a READY-recording workflow would warn «ما زال الطعام في
   * التحضير» the moment its plates turned PREPARING; asked after the start,
   * that is a second prompt behind a tap the cashier already made, over food
   * the till just started. So the warning comes first and names every plate
   * that will be cooking (the QUEUED ones about to start and those already
   * cooking); its «إغلاق الفاتورة» starts them and goes straight on to the
   * ordinary hand-over, and «انتظار» sends nothing.
   */
  const startAndCloseWarned = () => {
    const leavesCooking =
      invoice.channel !== "DINE_IN" && !directTakeaway && recordsReady(kitchenWorkflow);
    if (!leavesCooking) return startThenClose(true);
    setStillCooking({
      plates: invoice.items.filter(
        (item) => item.fulfillmentState === "QUEUED" || item.fulfillmentState === "PREPARING",
      ),
      proceed: () => void startThenClose(true, true),
    });
    return Promise.resolve();
  };
  const resendQueuedTickets = () =>
    queuedTicketIds(queuedPlates).reduce<Promise<void>>(
      // `seenPrintCount: 0` — the seam asked nobody whether a paper arrived,
      // so a paper the server knows printed makes this one «نسخة مكررة»
      // (D172 I1); only a ticket nothing of which reached prints unmarked.
      (previous, ticketId) => previous.then(() => reprintChit(ticketId, "NOT_RECEIVED", 0)),
      Promise.resolve(),
    );
  const unblockClose = (closeAfter: boolean) =>
    closeUnblock === "startAndClose"
      ? closeAfter
        ? startAndCloseWarned()
        : startThenClose(false)
      : closeUnblock === "resendTicket"
        ? resendQueuedTickets()
        : Promise.resolve();


  /*
   * The dark till's direct takeaway, paid from the whole-bill sheet on the OPEN
   * bill (founder, 2026-10-08). The same command sequence the PAPER window
   * sends, through the same sequencer (`runSettle`): with something still
   * unsent or waiting at the kitchen, one `settlePartial` for the live due that
   * leaves the bill open — a close would pull cooking dishes off the board;
   * otherwise close then settle, in one act. The close raises no undo toast: a
   * settle follows at once, and an undo racing it would offer a reopen of a
   * bill about to be SETTLED.
   *
   * If the close lands and the settle is refused (no drawer open, say), the
   * sheet stays up on the now-CLOSED bill and a second confirm settles it.
   */
  const settleOpenTakeaway = async (
    method: SettlementMethod,
    amount: number,
    reference: string,
  ) => {
    const outcome = await runSettle(
      invoice,
      method === "WRITE_OFF"
        ? { kind: "writeOff" }
        : {
            kind: "full",
            method,
            // Only cash keeps the typed figure (what was handed over, so the
            // change survives); any other tender is charged what is owed, the
            // SERVER's figure once the bill is closed.
            amount: method === "CASH" ? amount : null,
            // D163 — `runSettle` sends it with CARD/INSTAPAY only.
            reference,
          },
      async (command, key) => {
        const result = await run(command, { key });
        return result ? { ok: true, invoice: result.invoice } : { ok: false };
      },
      {
        keepOpen: payKeepsOpen,
        liveDue: outstandingOnBill(invoice),
      },
    );
    if (outcome.status !== "done") return;
    toast.success(
      outcome.invoice.status === "SETTLED"
        ? t((m) => m.pos.settledToast)
        : t((m) => m.pos.partialTakenToast),
    );
    setOpenTakeawaySettleFor(null);
  };

  /**
   * PAPER (2026-10-03) — open the redesign's Settle window.
   *
   * On an OPEN bill the window's Pay closes the bill before it settles, so the
   * one question a close asks first is asked here too: covers, once, skippable
   * (B117 Q6). The window waits behind the covers dialog and opens when it is
   * answered or dismissed — the answer is its own `setGuestCount`, and the
   * close inside the window then runs on the version that write returned.
   */
  /*
   * PAPER + OFF-PREMISE (cashier persona, 2026-10-07): the redesign's footer
   * settles an OPEN bill in one window — close, then pay — which is right for
   * a table and wrong for a bag: it skipped the hand-over (T16/T18) entirely,
   * so a cashier on the light till could never record who the bag left with
   * and never saw the CLOSED "out with riders" state (T8). An OPEN off-premise
   * bill therefore closes through the hand-over dialog on both tills; the pay
   * window stays one tap away on the CLOSED bill (and on its tile).
   *
   * EXCEPT A DIRECT TAKEAWAY (founder, 2026-10-08): it leaves with the customer
   * who pays for it, so there is no one to record and nothing to hand over —
   * the footer offers Settle and the window closes then pays, exactly as on a
   * seated bill (D126's keep-open included).
   */
  const paperSettlesOpenBill =
    paperTill &&
    canClose &&
    canSettle &&
    !(invoice.status === "OPEN" && invoice.channel !== "DINE_IN" && !directTakeaway);

  const requestPaperSettle = () => {
    // The empty-bag rule on this door too (founder, 2026-10-07): an OPEN
    // off-premise bill with nothing chargeable is cancel-only, said before the
    // window opens rather than by the close the window would send.
    if (
      invoice.status === "OPEN" &&
      invoice.channel !== "DINE_IN" &&
      !invoice.items.some((item) => isRungOnTheBill(item))
    ) {
      toast.error(t((m) => m.pos.offPremiseEmptyClose));
      return;
    }
    if (
      invoice.status === "OPEN" &&
      shouldPromptForCovers({
        channel: invoice.channel,
        guestCount: invoice.guestCount,
        coversAsked,
      })
    ) {
      setCoversAsked(true);
      setCountingGuests(true);
    }
    setPaperSettleAsked(true);
  };

  /*
   * THE PRINTER'S CHOICE (user, 2026-10-20). The «تقسيم الفاتورة» row left the
   * bill's foot and the split moved behind the printer: whole check, or chosen
   * items. Asked only when there is something to choose — a bill with nothing
   * sent and nothing split goes straight to today's print.
   */
  const hasPrintChoice =
    invoice.parties.length > 0 ||
    (outstandingOnBill(invoice) > 0 &&
      markablePlates(invoice).some((item) => !paidPlateIds(invoice).has(item.id)));
  const printWhole = () => {
    setPrintChoiceAsked(false);
    if (invoice.status === "OPEN") setCheckAsked(true);
    else window.print();
  };
  /*
   * A check for SOME plates. A pick (no `partyId`) becomes a PART of the bill
   * first — B43's `assignParty`, on the rung the split sheet uses — unless an
   * unpaid part already holds exactly these plates, so the part is paid the
   * way a split part is (the PAPER window's "by item" finds it through
   * `matchingParty`; the dark till's part-payment sheet offers its chip). A
   * session without APPEND prints without marking. On an OPEN bill the check is
   * recorded through `printCheck { itemIds }` (not stamped as the bill's last
   * check); `printCheck` takes an OPEN bill only, so a CLOSED bill's part
   * prints from the browser, as that bill's own cheque does.
   */
  const printPart = async (plateIds: string[], partyId: string | null) => {
    if (plateIds.length === 0 || printingPart.current) return;
    printingPart.current = true;
    try {
      await printPartOnce(plateIds, partyId);
    } finally {
      printingPart.current = false;
    }
  };
  /*
   * Gate F2: a pick never moves a plate out of another part. A new part is
   * marked only when NONE of the plates is in one; exactly one unpaid part's
   * plates reuse it (`matchingParty`); anything else — the PAPER ticks across a
   * part, say — prints those lines and marks nothing.
   */
  const printPartOnce = async (plateIds: string[], partyId: string | null) => {
    let bill = invoice;
    const inAPart = new Set(
      bill.items.filter((item) => item.partyId !== null).map((item) => item.id),
    );
    if (
      partyId === null &&
      canAppend &&
      (bill.status === "OPEN" || bill.status === "CLOSED") &&
      matchingParty(bill, plateIds) === null &&
      plateIds.every((id) => !inAPart.has(id))
    ) {
      const marked = await run(
        {
          type: "assignParty",
          invoiceId: bill.id,
          payload: {
            itemIds: plateIds,
            label: nextPartyLabel(bill, (number) => t((m) => m.paperSettle.guest, { number })),
          },
        },
        { key: "check" },
      );
      if (!marked) return;
      bill = marked.invoice ?? bill;
    }
    // `assignParty` never moves the status: the bill the cashier tapped on says
    // which paper this is.
    if (invoice.status !== "OPEN") {
      setPrintChoiceAsked(false);
      checkPrinting.print({ invoice: invoiceForPlates(bill, plateIds), splitGuests: null });
      return;
    }
    const queued = settingsQuery.data?.receiptPrinterConfigured === true;
    const result = await run(
      { type: "printCheck", invoiceId: bill.id, payload: { itemIds: plateIds } },
      queued
        ? { key: "check", success: t((m) => m.preBillCheck.sentToPrinterToast) }
        : { key: "check" },
    );
    if (!result) return;
    setPrintChoiceAsked(false);
    // With a receipt printer the server queued the paper (§11).
    if (queued) return;
    checkPrinting.print({
      invoice: invoiceForPlates(result.invoice ?? bill, plateIds),
      splitGuests: null,
    });
  };

  /*
   * T8 — the floor's settle hand-off holds this bill's settle sheet open: the
   * PAPER window, else the split-tender sheet on a part-paid bill, else the
   * whole-bill sheet — the SAME sheet the panel's own Settle button opens for
   * this state, so the hand-off adds no second path to the money. Only while
   * the bill is CLOSED: one that was reopened, or already settled on another
   * device, by the time it loads simply opens as itself.
   */
  const handoffSettleOpen =
    handoffSettle && !handoffSettleDone && invoice.status === "CLOSED";
  /* The dark till's whole-bill sheet is up over a direct takeaway that was
     OPEN when it was asked for (see `settleOpenTakeaway`). */
  const darkOpenTakeawaySheet =
    !paperTill &&
    openTakeawaySettleFor === invoice.id &&
    canSettle &&
    (invoice.status === "OPEN" || invoice.status === "CLOSED");

  /* P4.1 — the replayed refund, as the refreshed bill records it: absent
     until the refetch lands, so the offer never prints a stale bill's paper. */
  const replayedRefundSlip = replayedRefundCommandId
    ? refundPrinting.slips.find((slip) => slip.commandId === replayedRefundCommandId)
    : undefined;
  const conflictBlock = conflictKind ? (
    <ListCard tone="warning" role="alert">
      <h2 className="text-sm font-semibold">{t((m) => m.pos.conflictTitle)}</h2>
      <p className="mt-1 text-sm">
        {conflictKind === "pending"
          ? t((m) => m.pos.conflictPendingBody)
          : conflictKind === "replayed"
            ? t((m) => m.pos.conflictReplayedBody)
            : t((m) => m.pos.conflictBody)}
      </p>
      {conflictKind === "replayed" && replayedRefundSlip ? (
        <Button
          variant="outline"
          size="touch"
          className="mt-3 me-2"
          onClick={() => refundPrinting.printCopy(replayedRefundSlip.commandId)}
          data-testid="invoice-conflict-refund-slip"
        >
          {t((m) => m.pos.refundPrintReceiptOne, {
            reference: replayedRefundSlip.reference,
            amount: money(replayedRefundSlip.amount),
          })}
        </Button>
      ) : null}
      <Button
        variant="outline"
        size="touch"
        className="mt-3"
        onClick={() => void refresh()}
        data-testid="invoice-conflict-refresh"
      >
        {t((m) => m.pos.refresh)}
      </Button>
    </ListCard>
  ) : null;

  /*
   * Covers, on the bill rather than only at the moment it opened (B6, D45).
   *
   * At the top of the order panel because that is where the waiter looks when
   * they come back from the table having actually counted — the one moment the
   * question can be answered honestly. It states the current answer rather than
   * only offering to change it: "covers —" is the reading that gets a bill
   * counted, while a bare button would be read as a setting nobody owns.
   *
   * Same authority as putting food on the bill (`canAppend`): counting heads is
   * floor work and moves no money, and requiring a cashier would put the
   * question in front of the one person who was never at the table.
   */
  /*
   * THE VALUE SURVIVES THE CLOSE; ONLY THE CONTROL GOES (B117/A4).
   *
   * This whole row used to be gated on `isOpen && canAppend`, so the moment a
   * bill CLOSED the covers vanished — the figure with it — while `guest_count`
   * was still 4 in the database. Between close and settle is exactly when
   * somebody says "wait, we were six", and it is also when a cashier is
   * checking a cover charge or a per-head split against a cheque that no longer
   * admits it knows how many people ate.
   *
   * So the row is rendered whenever the bill HAS a count, and it is the BUTTON
   * that is conditional: editable on an open bill by whoever may put food on it
   * (`canAppend` — counting heads is floor work and moves no money), a plain
   * read-out otherwise. The unset case is still the offer to set one, which
   * only makes sense while the bill is open.
   */
  /*
   * B118 — CORRECTABLE UNTIL THE MONEY LANDS, which is what the server has
   * enforced since Q6's founder ruling and this screen never offered. A waiter
   * who typed 2 for a party of 4 had no remedy the moment the cheque printed,
   * and no report can detect a covers figure that is merely WRONG: an unset one
   * is excluded from the per-cover average and published as coverage, a wrong
   * one is counted and believed.
   *
   * The rungs mirror the command exactly rather than guessing at it: while the
   * bill is OPEN the waiter who seated the table fixes his own count on the
   * `INVOICE_APPEND` rung he already had; after the close it is a supervisor's
   * correction on `INVOICE_VOID` — the rung the floor already uses to strike a
   * plate or comp one, which a cashier and a manager hold and a waiter does
   * not. Once SETTLED it is a fact about a night that has been paid for, and
   * the server refuses it too.
   */
  /*
   * Covers rework Phase 1 — DINE_IN ONLY. A takeaway bag and a delivery order
   * sit at no table and are never asked for a cover count (`resolveOpenInvoicePayload`
   * never sends one off DINE_IN, and the server has nothing to store); showing
   * this row on either channel would offer a control over a fact the bill
   * cannot hold.
   */
  const isDineIn = invoice.channel === "DINE_IN";
  const coversEditable =
    isDineIn && invoice.status !== "SETTLED" && (isOpen ? canAppend : canVoid);
  const coversShape =
    "pos-line-control mb-2 flex w-full items-center justify-between gap-2 rounded-lg border border-[color:var(--border-strong)] px-2.5 py-[7px] text-[length:var(--font-size-2xs)] font-extrabold text-[color:var(--text-muted)]";
  const coversBody = (
    <>
      <span className="uppercase tracking-[0.06em]">
        {t((m) => m.pos.guestCountLabel)}
      </span>
      <span className="tabular-nums text-[color:var(--text)]">
        {invoice.guestCount === null
          ? t((m) => m.pos.guestCountUnset)
          : invoice.guestCount}
      </span>
    </>
  );
  /**
   * Covers rework Phase 2b — the number this render shows, ahead of a round
   * trip: `coversDisplayOverride` while a stepper write is in flight or
   * queued, the server's own figure otherwise.
   */
  const coversValue = coversDisplayOverride ?? invoice.guestCount;
  /**
   * One +/− tap. Always computed off what is ON SCREEN right now
   * (`coversValue`), never off `invoice.guestCount` directly — a second tap
   * that landed while the first write is still in flight has to step from the
   * number the waiter is looking at, or three quick taps up would only ever
   * reach "+1". `createGuestCountWriteQueue` is what makes firing on every
   * tap safe: it coalesces to the latest value rather than racing the server
   * with one request per tap.
   */
  const adjustCovers = (delta: number) => {
    const base =
      guestCountQueueRef.current?.latest() ?? coversValue ?? GUEST_COUNT_MIN;
    const next = nextGuestCountValue(base, delta);
    if (next === base) return;
    setCoversDisplayOverride(next);
    guestCountQueueRef.current?.request(next, (value) =>
      run(
        { type: "setGuestCount", invoiceId, payload: { guestCount: value } },
        { key: "guest-count-stepper" },
      ),
    );
  };
  /**
   * Covers rework Phase 2b — the stepper itself, «− N أفراد +».
   *
   * The group follows the page direction, NOT an LTR island: the plate
   * quantity stepper a few pixels below it on the same bill runs «+ N −» in
   * Arabic, and two steppers on one screen pointing opposite ways is a mistap
   * waiting to happen. Only the count's own digits are LTR (via `tabular-nums`
   * in the label), which is what the number convention actually asks for.
   *
   * Tapping the NUMBER opens the same `GuestCountDialog` the unset control
   * below opens (keeps «امسح العدد»); only +/− are new controls. The +/−
   * buttons stay tappable while THIS row's own write is in flight
   * (`busyKey === "guest-count-stepper"`), which is what lets rapid taps
   * queue instead of being silently swallowed — they are disabled only when
   * some OTHER command on this screen is running.
   */
  const coversStepper = (
    <div className={coversShape} data-testid="invoice-guest-count-stepper">
      <span className="uppercase tracking-[0.06em]">
        {t((m) => m.pos.guestCountLabel)}
      </span>
      <span className="flex items-center gap-1.5">
        <button
          type="button"
          aria-label={t((m) => m.pos.guestCountDecrement)}
          disabled={
            (isBusy && busyKey !== "guest-count-stepper") ||
            (coversValue ?? GUEST_COUNT_MIN) <= GUEST_COUNT_MIN
          }
          data-testid="invoice-guest-count-decrement"
          onClick={() => adjustCovers(-1)}
          className="flex h-11 w-11 items-center justify-center rounded-[var(--radius-control)] border border-[color:var(--border-strong)] text-sm font-extrabold text-[color:var(--text)] transition-colors hover:border-[color:var(--accent)] disabled:opacity-40"
        >
          −
        </button>
        <button
          type="button"
          disabled={isBusy}
          data-testid="invoice-guest-count"
          onClick={() => setCountingGuests(true)}
          className="min-w-[56px] text-center tabular-nums text-[color:var(--text)] disabled:opacity-40"
        >
          {t(
            countedKey(coversValue ?? 0, {
              one: (m) => m.pos.guestCountWordOne,
              two: (m) => m.pos.guestCountWordTwo,
              few: (m) => m.pos.guestCountWordFew,
              many: (m) => m.pos.guestCountWord,
            }),
            { count: coversValue ?? 0 },
          )}
        </button>
        <button
          type="button"
          aria-label={t((m) => m.pos.guestCountIncrement)}
          disabled={
            (isBusy && busyKey !== "guest-count-stepper") ||
            (coversValue ?? GUEST_COUNT_MIN) >= GUEST_COUNT_MAX
          }
          data-testid="invoice-guest-count-increment"
          onClick={() => adjustCovers(1)}
          className="flex h-11 w-11 items-center justify-center rounded-[var(--radius-control)] border border-[color:var(--border-strong)] text-sm font-extrabold text-[color:var(--text)] transition-colors hover:border-[color:var(--accent)] disabled:opacity-40"
        >
          +
        </button>
      </span>
    </div>
  );
  const coversRow = !isDineIn ? null : coversEditable ? (
    invoice.guestCount !== null ? (
      coversStepper
    ) : (
      <button
        type="button"
        disabled={isBusy}
        data-testid="invoice-guest-count"
        onClick={() => setCountingGuests(true)}
        className={`${coversShape} transition-colors hover:text-[color:var(--text)] disabled:opacity-40`}
      >
        {coversBody}
      </button>
    )
  ) : invoice.guestCount !== null ? (
    // Read-only: a closed bill's covers are a FACT about the night, and
    // a fact is not a control. `div`, not a disabled button — a button
    // nobody may press reads as something broken.
    <div className={coversShape} data-testid="invoice-guest-count-readonly">
      {coversBody}
    </div>
  ) : null;

  /**
   * Covers rework Phase 2c — the sanity hint. Inline and non-blocking: a
   * genuinely huge party is a real bill, and the common slip this catches is
   * a waiter typing the TABLE NUMBER into the covers field out of habit, not
   * an actual party twice the table's capacity. Silent unless every one of
   * the bill's own tables has a recorded seat count — see
   * `shouldShowCoversSanityHint`.
   *
   * Seats come off `gridQuery` (above), which this screen already reads for
   * the table switcher — NOT a second, FLOOR_MANAGE-gated request. A table
   * this device's grid has not been refreshed with yet (or one outside that
   * read's reach for any other reason) reads as `undefined` here, and
   * `?? null` folds that into the same "unknown" `shouldShowCoversSanityHint`
   * already treats as silence.
   */
  const gridTables = (gridQuery.data ?? []).flatMap((area) => area.tables);
  const billTableSeats = invoice.tableIds.map(
    (tableId) =>
      gridTables.find((table) => table.tableId === tableId)?.seats ?? null,
  );
  const coversSanityHint = shouldShowCoversSanityHint({
    channel: invoice.channel,
    guestCount: coversValue,
    tableSeats: billTableSeats,
  }) ? (
    <p
      role="status"
      data-testid="invoice-guest-count-sanity-hint"
      className="mb-2 text-[length:var(--font-size-2xs)] font-bold text-[color:var(--warning)]"
    >
      {t((m) => m.pos.guestCountSanityHint)}
    </p>
  ) : null;

  /* D154 — the crew's meal. On the comp rung only: the button ends in a
     STAFF_MEAL comp, and a session that cannot comp is told nothing it cannot
     act on. */
  const staffMealStep = staffMealNextStep(invoice.items, drafts.length);
  const staffMealBlock =
    isStaffMealBill(invoice) && canComp ? (
      <div data-testid="staff-meal-banner">
        <ListCard className="mb-2">
          <h2 className="text-sm font-semibold">
            {t((m) => m.pos.staffMealBannerTitle)}
          </h2>
          <p className="mt-1 text-xs text-[color:var(--text-secondary)]">
            {t((m) => m.pos.staffMealBannerBody)}
          </p>
          <Button
            type="button"
            size="touch"
            className="mt-2 w-full"
            disabled={isBusy || staffMealStep === null}
            isLoading={busyKey === "staffMeal" || busyKey === "fire"}
            data-testid="staff-meal-send"
            onClick={() => warnOutOfStock(drafts, () => void sendStaffMeal())}
          >
            {staffMealStep === "finish"
              ? t((m) => m.pos.staffMealFinish)
              : t((m) => m.pos.staffMealSend)}
          </Button>
        </ListCard>
      </div>
    ) : null;

  /* Buyer QR requests (decision D8): staff decide, always. Kept at the top of
     the order panel — it blocks the close, so it belongs beside the close.
     Drawn as the amber request card (Trioty canvas, "Table panel · guest
     requests"): who asked and since when, each plate with its price, what it
     adds to the bill, and the answer as the card's own buttons. */
  const stampLocale = locale === "en" ? "en" : "ar";
  const sinceStamp = (instant: string | null | undefined) =>
    instant
      ? t((m) => m.serviceRequests.since, {
          time: formatSectionStamp(instant, settingsQuery.data?.timezone, stampLocale),
        })
      : null;
  const answerPending = (
    type: "acceptPendingItems" | "rejectPendingItems",
    itemIds: string[],
    key: string,
  ) =>
    void run({ type, invoiceId: invoice.id, payload: { itemIds } }, { key });
  const pendingSince = pendingItems
    .map((item) => item.createdAt)
    .filter((at): at is string => Boolean(at))
    .sort()[0];
  const loneRequest = pendingItems.length === 1 ? pendingItems[0] : null;
  const pendingBlock =
    pendingItems.length > 0 ? (
      <RequestCard
        tone="guest"
        badge="servicebell"
        testId="pending-requests"
        title={
          loneRequest
            ? t((m) => m.pos.pendingAskedOne)
            : t((m) => m.pos.pendingAskedMany, { count: pendingItems.length })
        }
        subtitle={t((m) => m.pos.pendingFromQr)}
        aside={sinceStamp(pendingSince)}
        footer={
          <>
            <div className="flex items-baseline justify-between gap-2 text-[13px] text-[color:var(--text-secondary)]">
              <span>{t((m) => m.pos.pendingAddsToBill)}</span>
              <span className="font-semibold tabular-nums text-[color:var(--text)]">
                {money(pendingItems.reduce((sum, item) => sum + item.lineTotal, 0))}
              </span>
            </div>
            {/* Accepting or rejecting a diner's request IS adding to the
                bill: without that grant the request shows, with no buttons
                (founder, 2026-10-06). With the D121 switch on it is the
                cashier's answer. */}
            {canAnswerRequests && loneRequest ? (
              <div className="flex gap-2">
                <Button
                  className="flex-1"
                  variant="primary"
                  size="touch"
                  disabled={isBusy}
                  isLoading={busyKey === `accept-${loneRequest.id}`}
                  data-testid={`accept-pending-${loneRequest.id}`}
                  onClick={() =>
                    answerPending("acceptPendingItems", [loneRequest.id], `accept-${loneRequest.id}`)
                  }
                >
                  {t((m) => m.pos.acceptPendingSend)}
                </Button>
                <Button
                  variant="secondary"
                  size="touch"
                  disabled={isBusy}
                  isLoading={busyKey === `reject-${loneRequest.id}`}
                  data-testid={`reject-pending-${loneRequest.id}`}
                  onClick={() =>
                    answerPending("rejectPendingItems", [loneRequest.id], `reject-${loneRequest.id}`)
                  }
                >
                  {t((m) => m.pos.rejectPending)}
                </Button>
              </div>
            ) : null}
            {/* One command, not a loop: `acceptPendingItems` already takes an
                `itemIds` array, so accepting a table's whole request is a
                single idempotent envelope — all of it lands or none of it
                does. Past one plate it is the card's main answer, and each
                row keeps its own pair for the plate somebody wants to judge
                apart. */}
            {canAnswerRequests && !loneRequest ? (
              <Button
                variant="primary"
                size="touch"
                disabled={isBusy}
                isLoading={busyKey === "accept-all"}
                data-testid="accept-pending-all"
                onClick={() =>
                  answerPending(
                    "acceptPendingItems",
                    pendingItems.map((item) => item.id),
                    "accept-all",
                  )
                }
              >
                {t((m) => m.pos.acceptPendingAllSend)}
              </Button>
            ) : null}
            <p className="text-xs text-[color:var(--text-muted)]">
              {t((m) => m.pos.closeBlockedByPending)}
            </p>
          </>
        }
      >
        {pendingItems.map((item) => (
          <RequestCardRow
            key={item.id}
            price={money(item.lineTotal)}
            actions={
              canAnswerRequests && !loneRequest ? (
                <span className="flex gap-1.5">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={isBusy}
                    isLoading={busyKey === `accept-${item.id}`}
                    data-testid={`accept-pending-${item.id}`}
                    onClick={() =>
                      answerPending("acceptPendingItems", [item.id], `accept-${item.id}`)
                    }
                  >
                    {t((m) => m.pos.acceptPending)}
                  </Button>
                  <Button
                    variant="danger-outline"
                    size="sm"
                    disabled={isBusy}
                    isLoading={busyKey === `reject-${item.id}`}
                    data-testid={`reject-pending-${item.id}`}
                    onClick={() =>
                      answerPending("rejectPendingItems", [item.id], `reject-${item.id}`)
                    }
                  >
                    {t((m) => m.pos.rejectPending)}
                  </Button>
                </span>
              ) : null
            }
          >
            {/* One plate per row (D15), so there is no quantity to print. The
                SAME name every other staff screen draws (D59): the size rides
                with the dish and only the extras sit under it — a request is a
                stranger's order judged from the row alone. */}
            <span className="text-sm font-semibold">{plateNameAr(item)}</span>
            {guestNumberOf(item) !== null ? (
              <span
                className="text-xs font-semibold text-[color:var(--text-muted)]"
                data-testid={`pending-guest-${item.id}`}
              >
                {t((m) => m.pos.pendingGuest, { guestNumber: guestNumberOf(item) ?? 0 })}
              </span>
            ) : null}
            {/* What is INSIDE the plate: a meal deal is not judged from its
                name alone. */}
            {comboLabel(item) ? (
              <span
                className="text-xs text-[color:var(--text-secondary)]"
                data-testid={`pending-combo-${item.id}`}
              >
                {comboLabel(item)}
              </span>
            ) : null}
            {/* `+ ` is the till's convention for an extra (`OrderPanel`, the
                chit, the board). */}
            {addonLabel(item) ? (
              <span
                className="text-xs text-[color:var(--text-secondary)]"
                data-testid={`pending-addons-${item.id}`}
              >
                {`+ ${addonLabel(item)}`}
              </span>
            ) : null}
            {/* The diner's own words — "no peanuts" is an allergy, and this is
                the moment somebody is judging the request. */}
            {item.note ? (
              <span
                className="text-xs font-semibold text-[color:var(--text-muted)]"
                data-testid={`pending-note-${item.id}`}
              >
                {item.note}
              </span>
            ) : null}
          </RequestCardRow>
        ))}
      </RequestCard>
    ) : null;

  const panelTables: PanelTable[] = (gridQuery.data ?? []).flatMap((area) =>
    area.tables.map((table) => ({
      tableId: table.tableId,
      name: table.name,
      // B8: a table can carry several open bills. The switcher offers the
      // FIRST — the party that arrived first — and a tap on a table with more
      // than one lands on the table screen instead, where they are listed.
      invoiceId: table.openInvoices[0]?.invoiceId ?? null,
      openBillCount: table.openInvoices.length,
      chequeOut: table.closedInvoices.length > 0,
    })),
  );

  /**
   * Switch to another table. A free one goes to its pre-order screen — the
   * invoice is opened there, by "Start order", never by a chip tap here.
   */
  const pickTable = (table: PanelTable) => {
    if (table.openBillCount > 1) {
      // Never pick one of several: opening a stranger's ticket is the failure
      // B8's cardinality introduces.
      navigate(`/${locale}/pos/tables/${table.tableId}`);
      return;
    }
    if (table.invoiceId) {
      navigate(`/${locale}/pos/invoices/${table.invoiceId}`);
      return;
    }
    navigate(`/${locale}/pos/tables/${table.tableId}`);
  };

  /**
   * The table's name for the cheque, from the floor grid this screen already
   * holds. `null` when the grid read failed or the table is not in it.
   */
  const receiptTableName = billPlaceLabel(
    invoice,
    (id) => panelTables.find((table) => table.tableId === id)?.name ?? id,
    t,
  );

  /**
   * What this screen could NOT read, said out loud.
   *
   * Every one of these used to be a `?? []`: the query failed, the list came
   * back empty, and the till rendered a plausible screen built on nothing. The
   * addon one is the dangerous member of the set — an empty groups list makes
   * every required-modifier check pass vacuously, so a plate could reach the
   * kitchen without the size or the choice it is defined to carry. That one
   * therefore BLOCKS adding rather than warning about it; the rest degrade a
   * capability and say which.
   */
  const notices: {
    key: string;
    tone: "warning" | "danger";
    title?: string;
    body: string;
    onRetry?: () => void;
  }[] = [];
  if (addonGroupsQuery.isError) {
    notices.push({
      key: "addons",
      tone: "danger",
      title: t((m) => m.pos.addonsFailedTitle),
      body: t((m) => m.pos.addonsFailedBody),
      onRetry: () => void addonGroupsQuery.refetch(),
    });
  }
  if (menuQuery.isError) {
    notices.push({
      key: "menu",
      tone: "danger",
      title: t((m) => m.pos.menuFailedTitle),
      body: t((m) => m.pos.menuFailedBody),
      onRetry: () => void menuQuery.refetch(),
    });
  }
  if (categoriesQuery.isError) {
    notices.push({
      key: "sections",
      tone: "warning",
      body: t((m) => m.pos.sectionsFailedNote),
      onRetry: () => void categoriesQuery.refetch(),
    });
  }
  if (gridQuery.isError) {
    notices.push({
      key: "grid",
      tone: "warning",
      body: t((m) => m.pos.gridFailedNote),
      onRetry: () => void gridQuery.refetch(),
    });
  } else if (receiptTableName === null) {
    // The cheque still prints — a bill nobody can hand over is worse than a
    // bill missing a table name — but the cashier is told BEFORE they print,
    // while writing the table on the paper is still an option.
    notices.push({
      key: "receipt-table",
      tone: "warning",
      body: t((m) => m.pos.receiptTableUnknownNote),
    });
  }

  const dataNotice =
    notices.length > 0 ? (
      <div className="flex flex-col gap-2">
        {notices.map((entry) => (
          <ListCard key={entry.key} tone={entry.tone} role="alert">
            {entry.title ? (
              <h2 className="text-sm font-semibold">{entry.title}</h2>
            ) : null}
            <p className={entry.title ? "mt-1 text-sm" : "text-sm"}>
              {entry.body}
            </p>
            {entry.onRetry ? (
              <Button
                variant="outline"
                size="touch"
                className="mt-3"
                onClick={entry.onRetry}
              >
                {t((m) => m.common.retry)}
              </Button>
            ) : null}
          </ListCard>
        ))}
      </div>
    ) : null;

  /*
   * B6 · P24b + P26 — A BILL AT NO TABLE IS A STEP INSIDE THE DELIVERY TAB
   * (the design's register for `fromDel`). On PAPER: the register's tabs light
   * Delivery & takeaway, its crumbs and Back tile lead there, the panel's head
   * says Delivery / Pickup with the switch between them, and an OWN delivery
   * takes its name, phone and address inline — refused at Send while the
   * phone or the address is blank (D-39, the server's 409 behind it).
   */
  const offPremisePlace = invoice.channel !== "DINE_IN" && invoice.tableIds.length === 0;
  const ownDelivery = invoice.channel === "DELIVERY" && invoice.aggregatorId === null;
  const inlineContact = paperTill && offPremisePlace && ownDelivery;
  const contactEditable =
    canAppend && invoice.status !== "SETTLED" && invoice.status !== "CANCELLED";
  const deliveryHref = `/${locale}/pos?view=delivery`;
  /**
   * Saves the inline fields when they say something the bill does not.
   * Answers the bill the save returned, `null` when there was nothing to save,
   * `false` when the save was refused. Saves run ONE AT A TIME (G24b L4: an
   * earlier payload landing last would write a field back); the same payload
   * already queued is awaited, not sent twice (a field left by tapping Send).
   */
  const saveContact = (): Promise<Invoice | null | false> => {
    if (!inlineContact || !contactEditable || !contact.dirty) {
      // Still wait for a save already on its way: the caller's next command
      // carries a version that save moves.
      return contactSave.current?.promise ?? Promise.resolve(null);
    }
    const payload = contactPayload(contact.draft);
    const key = JSON.stringify(payload);
    const last = contactSave.current;
    if (last?.key === key) return last.promise;
    const promise = (last?.promise ?? Promise.resolve(null)).then(async () => {
      const saved = await run(
        { type: "editCustomer", invoiceId: invoice.id, payload },
        { key: "customer", quiet: true },
      );
      return saved ? (saved.invoice ?? null) : (false as const);
    });
    contactSave.current = { key, promise };
    void promise.finally(() => {
      if (contactSave.current?.promise === promise) contactSave.current = null;
    });
    return promise;
  };
  /**
   * G24b M2 — Close, Settle and the pre-bill check wait for the inline save:
   * it bumps the bill's version, and those commands carry `expectedVersion`,
   * so racing it would put the "this invoice changed" card on the cashier's
   * own edit. `next` gets the bill the save returned (or `null`).
   */
  const afterContact = (next: (fresh: Invoice | null) => void) => {
    void (async () => {
      const saved = await saveContact();
      if (saved === false) return;
      next(saved);
    })();
  };
  const closeWithContact = () => afterContact((fresh) => requestClose(fresh));
  const paperSettleWithContact = () => afterContact(() => requestPaperSettle());
  /** D-39 client half: an own delivery with no phone or address is not sent. */
  const sendWithContact = async (send: () => void) => {
    if (ownDelivery) {
      const blocked = inlineContact
        ? contact.missing
        : deliveryContactMissing(invoice).length > 0;
      if (blocked) {
        toast.error(t((m) => m.dispatch.contactMissingToast));
        return;
      }
      if ((await saveContact()) === false) return;
    }
    send();
  };
  /* D120 — a waiter handed this bill's unsent plates to the cashier. The
     plates themselves are the basket below; this blue card says who is waiting
     on whom and offers the same send the basket's own button does. Shown only
     to someone who may send (the cashier's rung under D121). */
  const waiterHandoffBlock =
    invoice.cashierRequestedAt && drafts.length > 0 && canFire && isOpen ? (
      <RequestCard
        tone="waiter"
        badge="waiter"
        testId="waiter-handoff"
        title={t((m) => m.pos.waiterHandoffTitle)}
        subtitle={t((m) => m.pos.cashierRequested, {
          time: formatSectionStamp(
            invoice.cashierRequestedAt,
            settingsQuery.data?.timezone,
            stampLocale,
          ),
        })}
        footer={
          <Button
            variant="primary"
            size="touch"
            disabled={isBusy}
            isLoading={busyKey === "fire"}
            data-testid="waiter-handoff-send"
            onClick={() =>
              void sendWithContact(() => warnOutOfStock(drafts, () => void fire()))
            }
          >
            {t((m) => m.pos.sendToKitchen)}
          </Button>
        }
      />
    ) : null;

  /** 2026-10-20 — the bill's own dispatch (`BillDispatchStrip`), head and body parts. */
  const billDispatchProps = {
    bill: {
      invoiceId: invoice.id,
      dailySequence: invoice.dailySequence,
      customerName: inlineContact ? contact.draft.name : invoice.customerName,
      customerPhone: inlineContact ? contact.draft.phone : invoice.customerPhone,
      // What the bag still owes — the panel's chip and total.
      amountDue: Math.max(0, Math.round((runningTotal - invoice.tenderedTotal) * 100) / 100),
      runningTotal,
      channel: invoice.channel,
      status: invoice.status,
      aggregatorId: invoice.aggregatorId,
      handedTo: invoice.handedTo ?? null,
    },
    contactMissing: inlineContact ? contact.missing : deliveryContactMissing(invoice).length > 0,
    nothingSent: invoice.status === "OPEN" && !invoice.items.some((item) => isRungOnTheBill(item)),
    look: paperTill ? ("paper" as const) : ("classic" as const),
  };
  /*
   * 2026-10-20 (user) — an own delivery's foot follows its dispatch: nobody
   * holds the bag → the primary is «إسناد إلى مندوب» (the tab's panel, its
   * guards), with the close a small secondary for a bag leaving without an
   * app driver; an offer out → no primary (the head's chip counts down);
   * accepted → the close reads «تسليم إلى المندوب» (`handOver`). Only for
   * `DISPATCH_MANAGE`, on an OPEN bill, once the board answered.
   */
  const assignBlock = billDispatchProps.contactMissing
    ? ("contact" as const)
    : billDispatchProps.nothingSent
      ? ("notSent" as const)
      : null;
  const dispatchFoot =
    ownDelivery && invoice.status === "OPEN" && dispatchAccess.canManage && dispatchBoardQuery.data
      ? !liveDispatch
        ? {
            kind: "assign" as const,
            onAssign: () => setAssigningDriver(true),
            assignDisabled: assignBlock !== null,
            // The contact's reason is the head's own line on PAPER (pointed at,
            // not repeated); the dark till has no such line, so it says it here.
            assignReason:
              assignBlock === "notSent"
                ? t((m) => m.dispatch.billAssignNotSent)
                : assignBlock === "contact" && !inlineContact
                  ? t((m) => m.dispatch.contactMissing)
                  : null,
            assignTitle:
              assignBlock === "notSent"
                ? t((m) => m.dispatch.billAssignNotSent)
                : assignBlock === "contact"
                  ? t((m) => m.dispatch.contactMissing)
                  : null,
            describedBy:
              assignBlock === "contact" && inlineContact
                ? ORDER_CONTACT_MISSING_ID
                : assignBlock
                  ? "invoice-assign-reason"
                  : undefined,
          }
        : liveDispatch.status === "OFFERED"
          ? {
              kind: "offered" as const,
              onAssign: () => setAssigningDriver(true),
              assignDisabled: true,
              assignReason: null,
            }
          : undefined
      : undefined;
  /* D-46 — the switch, OPEN and direct only, on the cashier's rung (the same
     gates as `ChargesDialog` and the server); hidden on a platform's bill. */
  const canSwitchChannel =
    paperTill &&
    offPremisePlace &&
    canEditCharges &&
    invoice.status === "OPEN" &&
    invoice.aggregatorId === null;
  const switchChannel = async () => {
    const target = invoice.channel === "DELIVERY" ? "TAKEAWAY" : "DELIVERY";
    if ((await saveContact()) === false) return;
    await run(
      {
        type: "editCharges",
        invoiceId: invoice.id,
        // Direct stays direct; the fee goes back to the store's default (a
        // pickup zeroes it server-side); the address is dropped by the server
        // when the bill leaves DELIVERY.
        payload: { channel: target, deliveryFee: null, aggregatorId: null, platformOrderRef: null },
      },
      {
        key: "charges",
        success:
          target === "TAKEAWAY"
            ? t((m) => m.dispatch.switchedToPickup)
            : t((m) => m.dispatch.switchedToDelivery),
      },
    );
  };

  return (
    <Screen
      tone="dark"
      bleed
      title={
        // PAPER: the till's one bar — "Store / Floor", the exit to the back
        // office — as in the design, where the register is a step inside the
        // floor. The bill's number heads the order panel.
        paperTill
          ? t((m) => m.navPaper.floor)
          : t((m) => m.pos.invoiceNumber, { sequence: invoice.dailySequence })
      }
      leading={paperTill ? <PaperTillBarLeading /> : backLink}
      phoneHead={paperTill}
      /* Unit C — in the top bar (2026-09-21). The settle sheet still carries its
         own warning: it is the moment money actually moves, and the sheet has to
         be readable on its own. But by the time the cashier reaches it they have
         been shown the badge on two screens, which is the whole point of moving
         the prompt off the settle sheet and onto the floor. */
      action={<DrawerStrip />}
    >
      {/* `flex-col` and not `flex-col-reverse`: below `lg` only one child is
          displayed at a time (see the screen comment), so a reversal has
          nothing left to reverse and would only make the DOM order read
          backwards to anyone maintaining this. */}
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        {/* The menu stays mounted for every invoice status, which is what the
            artboard does: settling covers the ORDER PANEL and leaves the menu
            readable beside it. Unmounting it on close emptied two thirds of
            the screen and threw away the scroll position and search the
            cashier had just been using. It is disabled rather than hidden —
            a closed invoice cannot take items, and the server refuses the
            append regardless. */}
        {canAppend ? (
        <MenuBoard
          menu={menuQuery.data ?? []}
          categories={visibleCategories}
          countsByProductId={countsByProductId}
          // The same tier the picker and the totals block price through, so all
          // three say one number for one dish.
          discountPercent={discountPercent}
          // A failed addon-groups read disables the whole board, not just the
          // items that carry modifiers: the list that says WHICH items carry
          // them is the list that failed.
          // Disabled while the groups are UNKNOWN — in flight as well as
          // failed. A tile that cannot yet be added safely must not look
          // tappable; the guard in `addFromMenu` is the belt to this brace.
          disabled={!isOpen || !canAppend || isBusy || addonGroupsUnknown}
          onAdd={addFromMenu}
          onOverride={canOverride ? setOverrideTarget : undefined}
          // D63: fetched on OPEN, not with the menu — the pool is a whole-store
          // read almost every order never needs.
          onSubstitute={
            canSubstitute ? (menuItem) => void openSwap(menuItem) : undefined
          }
          // Independent of the invoice's own state and of `canAppend`: a bill
          // that is closed, or a waiter who may not append, does not change
          // whether the kitchen has run out of fish.
          {...availability.boardProps}
          notice={dataNotice}
          narrow={narrowPane === "menu" ? "show" : "hide"}
          phoneLead={
            <TillPhoneLead
              name={receiptTableName ?? t((m) => m.pos.invoiceNumber, { sequence: invoice.dailySequence })}
              meta={t((m) => m.pos.invoiceNumber, { sequence: invoice.dailySequence })}
            />
          }
          // PAPER: the register's tabs and crumbs, and "from" on a dish that
          // asks for a size or extras first.
          paperHead={
            paperTill ? (
              offPremisePlace ? (
                <PaperRegisterTabs place="offPremise" />
              ) : (
                <PaperRegisterTabs />
              )
            ) : undefined
          }
          tablesHref={paperTill && offPremisePlace ? deliveryHref : undefined}
          backLabel={
            paperTill && offPremisePlace ? t((m) => m.dispatch.backToDelivery) : undefined
          }
          paperCrumbs={
            paperTill && offPremisePlace
              ? {
                  root: t((m) => m.dispatch.tabDelivery),
                  // «New …» only while nothing has reached the kitchen (G24b L2).
                  place: sentItems.length > 0
                    ? invoice.channel === "DELIVERY"
                      ? t((m) => m.dispatch.crumbDeliveryNumber, { sequence: invoice.dailySequence })
                      : t((m) => m.dispatch.crumbPickupNumber, { sequence: invoice.dailySequence })
                    : invoice.channel === "DELIVERY"
                      ? t((m) => m.dispatch.crumbNewDelivery)
                      : t((m) => m.dispatch.crumbNewPickup),
                }
              : undefined
          }
          fromPrice={(item) =>
            quoteFromPrice(
              item.price,
              discountPercent,
              resolveGroupsForItem(item, groupsById),
            )
          }
        />
        ) : null}

        <OrderPanel
          invoice={invoice}
          sentItems={sentItems}
          drafts={drafts}
          pending={
            (!paperTill && coversRow) ||
            coversSanityHint ||
            pendingBlock ||
            waiterHandoffBlock ||
            staffMealBlock ? (
              <>
                {staffMealBlock}
                {/* PAPER: the covers live in the panel head instead
                    (`headGuests`, founder 2026-10-03). */}
                {paperTill ? null : coversRow}
                {coversSanityHint}
                {pendingBlock}
                {waiterHandoffBlock}
              </>
            ) : null
          }
          /*
           * The diner's claim (D44). Rendered only for a holder of
           * `RESTAURANT_INVOICE_SETTLE` — the same rung that may act on it, and
           * the same rung the read route requires, so a waiter sees nothing
           * rather than an empty card they could not use.
           *
           * The LATEST claim only. A bill can accumulate a refused transfer and
           * the retry that followed, and the till's question is always "what am
           * I being asked to believe right now"; the history belongs on the
           * invoice's audit trail, not above the cashier's settle button.
           */
          dinerPayment={
            <>
            {/* D132 — what this bill's table has asked the floor for, with
                Done. Above the claim: any floor role can act on it. */}
            <TableServiceRequests tableIds={invoice.tableIds} />
            {canSettle && paymentsQuery.data && paymentsQuery.data.length > 0 ? (
              <div>
                <DinerPaymentCard
                  key={paymentsQuery.data[0].id}
                  payment={paymentsQuery.data[0]}
                  /*
                   * THE RUNNING TOTAL, NOT `invoice.total` (QA round 5).
                   *
                   * `invoice.total` is the STORED total, and its own type says
                   * so: totals are written on CLOSED/SETTLED/CANCELLED bills and
                   * the till computes them live on an OPEN one. So on an open
                   * bill it is ZERO — and an open bill is the ordinary case for
                   * a diner's claim, because D44's whole flow is paying at the
                   * table before the cheque is closed.
                   *
                   * The card therefore read «الفاتورة دلوقتي 0 جنيه — اللي
                   * اتبعت أكتر بـ 210.67» over a panel printing 242.59 three
                   * lines above it: the wrong figure AND the wrong direction,
                   * sending a cashier to fetch a manager about an overpayment
                   * that does not exist while saying nothing about the real
                   * shortfall. It read correctly once the bill was closed, which
                   * is what proved the copy right and the input wrong.
                   *
                   * `runningTotal` is what the panel and the summary bar print,
                   * from the same pure helper, so the card cannot disagree with
                   * the two figures beside it.
                   */
                  /* D133 — what is still OWED: the running total less what
                     has been paid toward it, since a claim is for that (or a
                     share of it) and not for money already in. */
                  invoiceTotal={Math.max(
                    0,
                    Math.round((runningTotal - invoice.tenderedTotal) * 100) /
                      100,
                  )}
                  isSubmitting={busyKey === "payment-review"}
                  onReview={(verified) =>
                    void reviewPayment(paymentsQuery.data![0].id, verified)
                  }
                  onSettleShare={
                    paperTill &&
                    !paymentsQuery.data[0].shareSettled &&
                    invoice.status !== "SETTLED" &&
                    invoice.status !== "CANCELLED" &&
                    (invoice.status !== "OPEN" || canClose) &&
                    runningTotal - invoice.tenderedTotal > 0
                      ? () => settleDinerShare(paymentsQuery.data![0])
                      : undefined
                  }
                  onRecordCashTip={
                    canManageShift && shiftCurrentQuery.data
                      ? () => recordCashTip(paymentsQuery.data![0])
                      : undefined
                  }
                />
              </div>
            ) : null}
            </>
          }
          conflict={conflictBlock}
          // Everything D22 refuses to close over (see `blockingCount` above).
          // Leaving QUEUED out of this sum is how the button used to enable,
          // get tapped, and bounce off the server in English.
          closeBlocking={closeBlocking}
          closeUnblock={closeUnblock}
          onCloseUnblock={() => void unblockClose(true)}
          narrow={narrowPane === "bill" ? "show" : "hide"}
          bindLabel={t((m) => m.pos.gridTitle)}
          /* WHAT TO CALL THIS BILL'S PLACE (F1/B44).

             Found by reading a DELIVERY bill in a browser: the panel header
             said "Invoice #2 · Tables · 1 items" — the `bindLabel` fallback,
             because this prop was never passed at all. So the one screen a
             cashier works a delivery on called it "Tables", which is the
             reading `billPlaceLabel` exists to prevent, and on a dine-in bill
             it named the section rather than the table the food is going to. */
          tableName={receiptTableName ?? undefined}
          // PAPER: a new line wears its dish's photo or badge, as on the tile.
          dishThumb={(productId) => {
            const dish = (menuQuery.data ?? []).find((row) => row.id === productId);
            return dish ? <PaperDishThumb item={dish} size={28} /> : null;
          }}
          /* The next party on THIS table — settling is what freed it, so that
             is the likeliest next act and the button finally says so. */
          onNewOrder={() =>
            navigate(
              // An off-premise bill sits on no table, so "the next party on
              // THIS table" has no table to go to; the floor is the honest
              // destination.
              invoice.tableIds[0]
                ? `/${locale}/pos/tables/${invoice.tableIds[0]}`
                : paperTill
                  ? deliveryHref
                  : `/${locale}/pos`,
            )
          }
          onBackToTables={() =>
            navigate(paperTill && offPremisePlace ? deliveryHref : `/${locale}/pos`)
          }
          tables={panelTables}
          currentTableId={invoice.tableIds[0] ?? null}
          onPickTable={pickTable}
          /* PAPER head: the covers as a button under Move (founder,
             2026-10-03) — the same `GuestCountDialog` and `setGuestCount`
             write, the same rungs (`coversEditable`), DINE_IN only. A count
             nobody may change is a read-out; none at all is no row. */
          headGuests={
            !isDineIn
              ? undefined
              : coversEditable
                ? {
                    count: coversValue,
                    onOpen: () => setCountingGuests(true),
                    disabled: isBusy,
                  }
                : invoice.guestCount !== null
                  ? { count: invoice.guestCount }
                  : undefined
          }
          /* D153 — «الكابتن» on a DINE-IN bill's head: the first name, or
             «من غير كابتن». A control only for `INVOICE_CLOSE` on an OPEN
             bill (the rung and the state `setCaptain` requires); a read-out
             for everyone else, and after the bill leaves OPEN. */
          headCaptain={
            isDineIn
              ? {
                  name: invoice.captain?.name ?? null,
                  onOpen:
                    isOpen && canClose
                      ? () => setChoosingCaptain(true)
                      : undefined,
                  disabled: isBusy,
                }
              : undefined
          }
          billDispatch={
            ownDelivery
              ? {
                  head: <BillDispatchStrip bill={billDispatchProps.bill} look={billDispatchProps.look} />,
                  body: null,
                }
              : undefined
          }
          offPremiseHead={
            paperTill && offPremisePlace
              ? {
                  channel: invoice.channel === "DELIVERY" ? "DELIVERY" : "TAKEAWAY",
                  platformName: invoice.aggregatorId
                    ? (platformName ?? t((m) => m.pos.aggregatorMethod))
                    : null,
                  onSwitch: canSwitchChannel ? () => void switchChannel() : undefined,
                  contact: inlineContact
                    ? {
                        draft: contact.draft,
                        onChange: contact.setField,
                        onCommit: () => void saveContact(),
                        missing: contact.missing,
                        disabled: !contactEditable,
                      }
                    : undefined,
                }
              : undefined
          }
          busyKey={busyKey}
          isBusy={isBusy}
          canAppend={canAppend}
          canFire={canFire}
          canServe={canServe}
          canPrepare={canPrepare}
          // Not in a paper workflow: the ticket starts the food (D18).
          canStartLine={canPrepare && workflowKnown && !printStartsFood(kitchenWorkflow)}
          canPrint={
            canPrint && Boolean(settingsQuery.data?.kitchenPrintEnabled)
          }
          canVoid={canVoid}
          canComp={canComp}
          canReopen={canReopen}
          canRemake={canRemake}
          canSubstitute={canSubstitute}
          kitchenPrintsChits={Boolean(settingsQuery.data?.kitchenPrintEnabled)}
          canClose={canClose}
          canSettle={canSettle}
          canCancel={canCancel}
          onCancel={() => setCancelAsked(true)}
          canRefund={canRefund}
          onRefund={() => {
            // A fresh sheet, and the previous refund's ORIGINAL dropped: a
            // second refund on the same bill must never be able to print the
            // first one's slip as if it were its own. Every refund already made
            // stays reprintable as its OWN copy, off the invoice read.
            refundPrinting.clear();
            setRefundAsked(true);
          }}
          onReopen={() =>
            void run(
              {
                type: "reopenInvoice",
                invoiceId: invoice.id,
                payload: { expectedVersion: invoice.version },
              },
              { key: "reopen", success: t((m) => m.pos.reopenedToast) },
            )
          }
          canTransfer={canAppend}
          /* B107 — the same rung that puts food on the bill, and the bill
             states the command can land on. SETTLED is excluded here as well as
             at the server: the money is in, the order is over, and editing then
             is a restatement rather than a correction. */
          canEditCustomer={
            canAppend &&
            invoice.status !== "SETTLED" &&
            // G13 — and not on a CANCELLED bill: nothing about a bill that
            // never happened is worth correcting, and the server refuses it.
            invoice.status !== "CANCELLED"
          }
          onEditCustomer={() => setCustomerAsked(true)}
          /* T6 — the fee, the platform and the channel: the CASHIER's rung
             (`RESTAURANT_INVOICE_CLOSE`, the one that already decides what a
             bill costs), OPEN only, off-premise only — the same three gates
             the server applies, so the control and the refusal agree. */
          platformName={platformName}
          canEditCharges={
            canEditCharges &&
            invoice.status === "OPEN" &&
            invoice.channel !== "DINE_IN"
          }
          onEditCharges={() => setChargesAsked(true)}
          /* B89 — the act that finishes a bill shortened after payment. Gated
             by the panel on `canRefund`, which is R9's rung. */
          onRefundOverpayment={() => setOverpaidAsked(true)}
          onTransfer={() => setTransferAsked(true)}
          onEditNote={(group) => setNoteTarget(group)}
          onFireGroup={(group) =>
            void sendWithContact(() =>
              warnOutOfStock(group.plates, () => void fire(group.plates.map((plate) => plate.id))),
            )
          }
          onDraftPlus={(group) =>
            void addPlates(
              group.productId,
              group.plates[0].addons.map((addon) => addon.addonId),
            )
          }
          onDraftMinus={(group) =>
            void run(
              {
                type: "updateDraftItems",
                invoiceId: invoice.id,
                // The LAST plate added, so tapping minus undoes the tap the
                // waiter most likely regrets.
                payload: {
                  removeItemIds: [group.plates[group.plates.length - 1].id],
                },
              },
              { key: `draft-minus-${group.key}` },
            )
          }
          // PAPER: a NEW line reopens the picker, prefilled, to change it.
          onDraftEdit={
            paperTill && canAppend && isOpen
              ? (group) => {
                  const dish = (menuQuery.data ?? []).find(
                    (row) => row.id === group.productId,
                  );
                  if (!dish) return;
                  const plate = group.plates[0];
                  const counts: Record<string, number> = {};
                  for (const addon of plate.addons) {
                    counts[addon.addonId] = (counts[addon.addonId] ?? 0) + 1;
                  }
                  const swaps = plate.substitutions ?? [];
                  setEditTarget(group);
                  setPickerResume({ counts, quantity: group.quantity });
                  setPendingOverride(
                    plate.unitPriceOverride !== null
                      ? {
                          unitPrice: plate.unitPriceOverride,
                          note: plate.unitPriceOverrideNote ?? "",
                        }
                      : null,
                  );
                  setPendingSwap(
                    swaps.length > 0
                      ? swaps.map((swap) => ({
                          removesIngredientId: swap.removedIngredientId,
                          addsIngredientId: swap.addedIngredientId,
                        }))
                      : null,
                  );
                  setPendingSwapCharge(
                    swaps.reduce((sum, swap) => sum + swap.price, 0),
                  );
                  setAddonTarget(dish);
                }
              : undefined
          }
          onDraftRemove={(group) =>
            void run(
              {
                type: "updateDraftItems",
                invoiceId: invoice.id,
                payload: {
                  removeItemIds: group.plates.map((plate) => plate.id),
                },
              },
              { key: `draft-remove-${group.key}` },
            )
          }
          onSentPlus={(group) =>
            void addPlates(
              group.productId,
              group.plates[0].addons.map((addon) => addon.addonId),
            )
          }
          onSentVoid={(group, comp) =>
            setVoidTarget({
              // The whole group goes to the DIALOG; how many actually void is
              // the count the cashier picks there, defaulting to one. Per-plate
              // rows (D15) are what make "2 of 3" possible; the count picker is
              // what stops it costing three dialog rounds.
              plateIds: group.plates.map((plate) => plate.id),
              fulfillmentStates: group.plates.map(
                (plate) => plate.fulfillmentState,
              ),
              comp,
            })
          }
          onAmendSwaps={(group) => void openAmend(group)}
          /* B117/Q4 — a remake asks WHY before it fires. The kitchen gets the
             answer on the chit, so the cook reads an instruction rather than
             guessing what to do differently the second time. */
          onRemake={(group) =>
            setRemakeTarget({ itemId: group.plates[0].id, key: group.key })
          }
          /* B117/RT-C — the same sentence the server would answer with
             (`RESTAURANT_ITEM_NOT_STARTED`), said here, because the till
             already knows the plate's state and a round trip to be told what
             is on screen is a refusal it can answer itself. `info`, not
             `error`: nothing went wrong, the act is simply not available yet. */
          onRemakeBlocked={() =>
            toast.info(t((m) => m.serverErrors.itemNotStarted))
          }
          onServe={(group) =>
            void run(
              {
                type: "markServed",
                invoiceId: invoice.id,
                // The whole group: its plates share a state by construction,
                // and delivering three teas is one trip, not three taps.
                payload: { itemIds: group.plates.map((plate) => plate.id) },
              },
              { key: `serve-${group.key}` },
            )
          }
          /*
           * D165 — the TILL's start: its own command type, so the server
           * flags `started_at_till` and the speed report leaves queue and cook
           * untimed rather than timing them from a cashier's tap. The payload
           * is group-wide like the serve chip's: plates in one group share a
           * state by construction (`groupPlates`), so three teas start
           * together rather than costing three taps.
           */
          onStartPreparing={(group) =>
            void run(
              {
                type: "startPreparingAtTill",
                invoiceId: invoice.id,
                payload: { itemIds: group.plates.map((plate) => plate.id) },
              },
              { key: `start-${group.key}` },
            )
          }
          onMarkReady={(group) =>
            void run(
              {
                type: "markReady",
                invoiceId: invoice.id,
                payload: { itemIds: group.plates.map((plate) => plate.id) },
              },
              { key: `ready-${group.key}` },
            )
          }
          onPrintChit={(ticketId) => setReprintAsk(ticketId)}
          onFire={() =>
            void sendWithContact(() => warnOutOfStock(drafts, () => void fire()))
          }
          onRequestCashier={requestCashier}
          // Both surfaces end up in `requestClose` — covers prompt, gate,
          // command and undo window all in one place (UX-round D15).
          onClose={closeWithContact}
          /* Founder, 2026-10-08 — the dark till's direct takeaway is paid
             from its close control, which reads Settle and does not wait for
             the kitchen (D126). Paid in full, it reads Close again, and says
             what it waits on while the kitchen still has plates. */
          closeAs={darkSettlesOpenTakeaway ? "settle" : "close"}
          handOver={Boolean(handOverDriverName)}
          dispatchFoot={dispatchFoot}
          closeNote={
            !paperTill && openTakeawayPaid && blockingCount > 0
              ? t((m) => m.paperSettle.footPaidWaiting)
              : undefined
          }
          // The sheet, not a dispatch: method and tendered amount are the
          // cashier's answers, not constants. The old exact-cash hardcode is
          // what made the CARD label unreachable and the change line dead.
          /*
           * B3 — ON A BILL MONEY IS ALREADY IN, SETTLE MEANS THE OTHER SHEET.
           *
           * `settleInvoice` demands the FULL total and writes a row for it, so
           * on a bill with 300 of 500 already tendered it would take 500 more.
           * The server refuses that now, and this is the half of the same fence
           * that keeps a cashier from ever meeting the refusal: the ordinary
           * Settle button on a partly paid bill opens the sheet that prices the
           * remainder, which is what they wanted anyway.
           */
          onSettle={() =>
            /* PAPER: one window for the whole bill and for part of it. */
            afterContact(() =>
              paperTill
                ? requestPaperSettle()
                : invoice.tenderedTotal > 0
                  ? setPartialAsked(true)
                  : setSettleAsked(true),
            )
          }
          /* PAPER (2026-10-03) — the redesign's footer settles an OPEN bill
             directly: the window closes it, then settles. Offered only to a
             session that may do both (`INVOICE_CLOSE` + `INVOICE_SETTLE`);
             anyone else keeps the Close they had. */
          onSettleOpen={paperSettlesOpenBill ? paperSettleWithContact : undefined}
          /* A CLOSED bill's «طباعة» asks the same question as the open
             bill's printer (2026-10-20) — B43 marks plates on a CLOSED bill
             too, and this is where its split lives now. */
          onPrint={() =>
            invoice.status === "CLOSED" && hasPrintChoice
              ? setPrintChoiceAsked(true)
              : window.print()
          }
          /* The pre-bill check: `RESTAURANT_INVOICE_CLOSE`, the rung the
             server puts `printCheck` behind since 2026-10-06 — the cashier
             shows the guest the bill, the waiter takes the order. Offered on
             an OPEN bill with something on it. */
          canCheck={canClose}
          onCheck={() =>
            afterContact(() =>
              // 2026-10-20 — a stuck ticket always asks: «طباعة التذكرة» is the
              // printer's first choice while it holds the close.
              hasPrintChoice || closeUnblock === "resendTicket"
                ? setPrintChoiceAsked(true)
                : setCheckAsked(true),
            )
          }
          /* D146 — the discount at the bill: `RESTAURANT_INVOICE_VOID`, the
             rung the server puts both discount commands behind (cashier,
             manager, owner). The remove is its own logged command. */
          canDiscount={canDiscount}
          onDiscount={() => setDiscountAsked(true)}
          onDiscountRemove={() =>
            void run(
              { type: "removeBillDiscount", invoiceId: invoice.id, payload: {} },
              {
                key: "bill-discount",
                success: t((m) => m.billDiscount.removedToast),
              },
            )
          }
          /* Founder, 2026-10-07: tick lines, then a bill for just those —
             recorded through the same command, printed from a copy of the bill
             holding only them — or settle just those, in the Settle window's
             "by item" with them already picked. */
          onPrintItems={
            paperTill && canClose && invoice.status === "OPEN"
              ? (plateIds) => void printPart(plateIds, null)
              : undefined
          }
          onSettleItems={
            paperTill && canSettle && canAppend && (invoice.status !== "OPEN" || canClose)
              ? (plateIds) => {
                  setSettlePicked(plateIds);
                  requestPaperSettle();
                }
              : undefined
          }
          storeTimezone={settingsQuery.data?.timezone}
          refundReceipts={refundPrinting.slips}
          onPrintRefundReceipt={
            refundPrinting.slips.length > 0 ? refundPrinting.printCopy : undefined
          }
        />
      </div>

      {/* The narrow-width shape's other half (QA §6). A flex sibling of the
          pane row, so it is always on screen and never over the lists. */}
      <TillSummaryBar
        pane={narrowPane}
        placeName={receiptTableName}
        onTogglePane={() =>
          setPaneOverride(narrowPane === "menu" ? "bill" : "menu")
        }
        runningTotal={runningTotal}
        lineCount={lineCount}
        primary={tillPrimary}
        /* Where the bar reads Settle — PAPER's Settle window (a seated bill or a
           direct takeaway) or the dark direct takeaway's sheet — it does not
           wait for the kitchen (D126): only busy state holds it, as on the
           panel's foot. Close keeps the D22 gate. */
        close={
          (paperSettlesOpenBill || darkSettlesOpenTakeaway) && tillClose
            ? { ...tillClose, enabled: true }
            : tillClose
        }
        isBusy={isBusy}
        busyKey={busyKey}
        onPrimary={() => {
          switch (tillPrimary.kind) {
            case "fire":
              // The phone's bar sends through the same D-39 gate as the panel.
              void sendWithContact(() => warnOutOfStock(drafts, () => void fire()));
              return;
            case "settle":
              if (
                paperTill &&
                invoice.status === "OPEN" &&
                invoice.channel !== "DINE_IN" &&
                !directTakeaway
              )
                closeWithContact();
              else if (paperTill) paperSettleWithContact();
              else setSettleAsked(true);
              return;
            case "requestCashier":
              requestCashier();
              return;
            case "newOrder":
              navigate(`/${locale}/pos`);
          }
        }}
        // The SAME close the panel's foot calls — covers prompt included. This
        // used to be a second dispatch written out here, which is how the bar
        // came to close bills without ever asking for covers.
        //
        // PAPER: where the panel's foot offers Settle in Close's place (a
        // session holding both rungs), so does this bar — the same act the
        // wide screen offers the same person for the same bill.
        closeAs={paperSettlesOpenBill || darkSettlesOpenTakeaway ? "settle" : "close"}
        handOver={Boolean(handOverDriverName)}
        dispatchFoot={dispatchFoot}
        onClose={paperSettlesOpenBill ? paperSettleWithContact : closeWithContact}
        closeUnblock={closeUnblock}
        onCloseUnblock={() => void unblockClose(true)}
        unsentCount={drafts.length}
        // Waiter phone (2026-10-28): sent everything → the floor, one tap.
        onBackToFloor={
          paperTill
            ? () => navigate(offPremisePlace ? deliveryHref : `/${locale}/pos`)
            : undefined
        }
      />

      {swapTarget ? (
        <SubstitutionPicker
          item={swapTarget}
          removable={swapOptions?.removable ?? []}
          pool={swapOptions?.pool ?? []}
          loadFailed={swapLoadFailed}
          onCancel={() => {
            const dish = swapTarget;
            setSwapTarget(null);
            setSwapOptions(null);
            setSwapLoadFailed(false);
            // PAPER: the swap was asked FROM the picker; backing out returns
            // there, keeping whatever swap was already set on the plate.
            if (paperTill && pickerResume) {
              setAddonTarget(dish);
              return;
            }
            setPendingSwap(null);
            setPendingSwapCharge(0);
            setPendingOptionRemovals([]);
          }}
          onConfirm={(substitutions, quotedCharge) => {
            const dish = swapTarget;
            // B21: read BEFORE `swapOptions` is cleared, two lines down.
            const optionRemovals = swapOptions?.optionRemovals ?? [];
            setSwapTarget(null);
            setSwapOptions(null);
            setSwapLoadFailed(false);
            // Same detour the price override already takes: a dish carrying
            // option groups must still be asked its options, or the server
            // refuses the whole append. PAPER: a swap asked FROM the picker
            // goes back to it.
            if (pickerResume !== null || resolveGroupsForItem(dish, groupsById).length > 0) {
              setPendingSwap(substitutions);
              setPendingSwapCharge(quotedCharge);
              setPendingOptionRemovals(optionRemovals);
              setAddonTarget(dish);
              return;
            }
            void addPlates(dish.id, [], undefined, undefined, substitutions);
          }}
        />
      ) : null}

      {amendTarget ? (
        <SubstitutionPicker
          /*
           * REMOUNT WHEN THE OPTIONS LAND, so the seeds are re-read (D108).
           *
           * The dialog opens the instant the plate is tapped, before the
           * options read resolves, so its first `initialSwaps` are the
           * snapshot's stubs: a name and an id, no price, no basis, no
           * recipe quantity. The picker copies `initialSwaps` into state ONCE,
           * on mount, and never looks again — so without this key the real
           * rows arrive a moment later and change nothing: the button reads
           * "no difference" on a swap the bill already charged 27.50 for,
           * while taking the same pair off and putting it back by hand quotes
           * the right figure. Caught in a browser on 2026-09-17, after the
           * review that fixed `seededSwaps`' merge order had made the seeds
           * correct — correct, and read too early.
           *
           * A key rather than an effect inside the picker: re-seeding from a
           * prop would also overwrite a waiter's edits on every options
           * refresh, and the only edit this can drop is one made inside the
           * read's own few hundred milliseconds.
           */
          key={swapOptions === null ? "seeding" : "seeded"}
          // The plate as it was SOLD, not as the menu reads today (S16/D33).
          item={{ nameAr: amendTarget.plateNameAr }}
          removable={swapOptions?.removable ?? []}
          pool={swapOptions?.pool ?? []}
          loadFailed={swapLoadFailed}
          initialSwaps={seededSwaps(amendTarget, swapOptions)}
          // An empty set is a real instruction here: "take the swaps off".
          allowEmpty
          title={t((m) => m.pos.swapAmendTitle)}
          confirmLabel={t((m) => m.pos.swapAmendConfirm)}
          notice={amendNotice(amendTarget)}
          onCancel={() => {
            setAmendTarget(null);
            setSwapOptions(null);
            setSwapLoadFailed(false);
          }}
          onConfirm={(substitutions) => {
            const group = amendTarget;
            setAmendTarget(null);
            setSwapOptions(null);
            setSwapLoadFailed(false);
            void run(
              {
                type: "substituteItem",
                invoiceId: invoice.id,
                payload: {
                  /*
                   * ONE plate, even when the row stands for several. A row is
                   * "the identical plates on this bill", and the guest who
                   * changed their mind is one of the three people who ordered
                   * the same drink — changing all three would be a different,
                   * larger claim than anyone made. The amended plate splits
                   * onto its own row afterwards, because the grouping key
                   * includes the swap set, which is what makes the outcome
                   * visible rather than merely correct.
                   */
                  itemId: group.plates[0].id,
                  substitutions,
                },
              },
              {
                key: `swap-amend-${group.key}`,
                success: t((m) => m.pos.swapAmendSaved),
              },
            );
          }}
        />
      ) : null}

      {pickerItem ? (
        <AddonPicker
          item={pickerItem}
          // Resolved for THIS dish before the picker sees them: an option's
          // price is a catalogue default the item may override, and an option
          // the item does not offer is dropped. The picker stays ignorant of
          // overrides so there is one place this can be got wrong, not one per
          // surface.
          groups={resolveGroupsForItem(pickerItem, groupsById)}
          discountPercent={discountPercent}
          // B21: the options this dish's pending swap has ruled out. Empty on
          // an ordinary tap, which is every tap that reached this picker before
          // swaps existed.
          blockedAddonIds={blockedAddonIds(pendingOptionRemovals, pendingSwap)}
          // The surcharge the swap dialog quoted a moment ago, so this picker's
          // total is the whole plate rather than the part that has no swap in
          // it (D108).
          substitutionCharge={pendingSwapCharge}
          // PAPER: the dish's quantity, the size first, and the acts that left
          // the dish tile — each only for the rung that may use it.
          withQuantity={paperTill}
          resume={pickerResume}
          unitPrice={pendingOverride?.unitPrice}
          optionsOnly={resolveGroupsForItem(pickerItem, groupsById).length === 0}
          swapSet={(pendingSwap?.length ?? 0) > 0}
          onOverride={
            paperTill && canOverride
              ? (resume) => {
                  setPickerResume(resume);
                  setAddonTarget(null);
                  setHandoffPickerDone(true);
                  setOverrideTarget(pickerItem);
                }
              : undefined
          }
          onSwap={
            paperTill && canSubstitute && !pickerItem.isCombo
              ? (resume) => {
                  setPickerResume(resume);
                  setAddonTarget(null);
                  setHandoffPickerDone(true);
                  void openSwap(pickerItem);
                }
              : undefined
          }
          // 86 at the till (§5.8): the picker closes, as on a cancel, and the
          // shared confirm asks before the dish goes out store-wide.
          onMarkSoldOut={
            paperTill && availability.boardProps.onToggleAvailability
              ? () => {
                  const toggle = availability.boardProps.onToggleAvailability;
                  setAddonTarget(null);
                  setHandoffPickerDone(true);
                  setPickerResume(null);
                  setPendingOverride(null);
                  setPendingSwap(null);
                  setPendingSwapCharge(0);
                  setPendingOptionRemovals([]);
                  toggle?.(pickerItem);
                }
              : undefined
          }
          onCancel={() => {
            setAddonTarget(null);
            setHandoffPickerDone(true);
            setPickerResume(null);
            setEditTarget(null);
            setPendingOverride(null);
            // Dropped with the rest: a cancelled plate keeps no half-applied
            // swap waiting to attach itself to the next dish the waiter taps.
            setPendingSwap(null);
            setPendingSwapCharge(0);
            setPendingOptionRemovals([]);
          }}
          updating={editTarget !== null}
          onConfirm={(addonIds, quantity) => {
            const add = () =>
              addPlates(
                pickerItem.id,
                addonIds,
                pendingOverride?.unitPrice,
                pendingOverride?.note,
                // A swap confirmed a moment ago, carried across the detour so
                // the plate arrives with both halves in ONE append (D61).
                pendingSwap ?? undefined,
                quantity,
                editTarget?.note,
              );
            const edited = editTarget;
            if (edited) {
              // Update a NEW line: its plates off, the changed line on. The
              // add waits for the removal, so a refused removal adds nothing.
              void (async () => {
                const removed = await run(
                  {
                    type: "updateDraftItems",
                    invoiceId: invoice.id,
                    payload: { removeItemIds: edited.plates.map((plate) => plate.id) },
                  },
                  { key: `draft-edit-${edited.key}` },
                );
                if (removed) await add();
              })();
            } else {
              void add();
            }
            setEditTarget(null);
            setAddonTarget(null);
            setHandoffPickerDone(true);
            setPickerResume(null);
            setPendingOverride(null);
            setPendingSwap(null);
            setPendingSwapCharge(0);
            setPendingOptionRemovals([]);
          }}
        />
      ) : null}

      {choosingCaptain && isOpen && canClose ? (
        <CaptainSheet
          chips={captainChips(staffQuery.data ?? [])}
          current={invoice.captain?.sellerId ?? null}
          isSubmitting={busyKey === "captain"}
          onCancel={() => setChoosingCaptain(false)}
          onPick={async (captainSellerId) => {
            const result = await run(
              {
                type: "setCaptain",
                invoiceId: invoice.id,
                payload: { captainSellerId },
              },
              {
                key: "captain",
                success:
                  captainSellerId === null
                    ? t((m) => m.captain.cleared)
                    : t((m) => m.captain.saved),
              },
            );
            if (!result) return;
            setChoosingCaptain(false);
            // The chip order is "most recently captained first".
            void queryClient.invalidateQueries({
              queryKey: queryKeys.restaurantStaff,
            });
          }}
        />
      ) : null}

      {isCountingGuests ? (
        <GuestCountDialog
          current={coversValue}
          isSubmitting={busyKey === "guest-count"}
          /*
           * Covers rework Phase 1 — opened FROM THE CLOSE PATH, so it asks a
           * slightly different question than the normal edit path (which
           * passes neither prop and keeps `guestCountDialogTitle`/
           * `guestCountDialogHint`). `closeAfterCovers` is exactly "the close
           * is what opened this", set only in `requestClose`, never by the
           * covers row's own button.
           */
          title={
            closeAfterCovers
              ? t((m) => m.pos.guestCountCloseDialogTitle)
              : undefined
          }
          hint={
            closeAfterCovers
              ? t((m) => m.pos.guestCountCloseDialogHint)
              : undefined
          }
          /*
           * B118 — the one tap that means STOP. Dismissing is the skip the
           * round table insisted on ("a number invented under pressure at 23:50
           * is worse than a blank"), and it is also the only control on this
           * dialog that reads as "not now", so it does not close the bill. The
           * close stays one tap away and `coversAsked` has latched, so that tap
           * goes straight through without being asked again.
           */
          onCancel={() => {
            setCountingGuests(false);
            setCloseAfterCovers(false);
          }}
          onConfirm={async (guestCount) => {
            const result = await run(
              {
                type: "setGuestCount",
                invoiceId: invoice.id,
                payload: { guestCount },
              },
              { key: "guest-count" },
            );
            if (!result) return;
            setCountingGuests(false);
            // The manual dialog is the definitive answer — drop any stale
            // stepper optimism rather than let it fight the fresh figure.
            setCoversDisplayOverride(null);
            /*
             * B118 — FINISH WHAT THE CASHIER ACTUALLY TAPPED.
             *
             * The dialog is dismissed FIRST, so a close that is itself refused
             * (a bill someone settled underneath this screen, a plate still in
             * the kitchen) shows its own toast — or its recovery card — over a
             * clear screen instead of behind a modal that swallowed it. `run`
             * already routes both, so the failure is surfaced by not catching
             * it here rather than by re-stating it.
             *
             * The version comes from the covers write's own response: that
             * command incremented it, and `invoice.version` in this closure is
             * now one behind.
             */
            if (closeAfterCovers) {
              setCloseAfterCovers(false);
              void closeBill(result.invoice?.version ?? invoice.version);
            }
          }}
        />
      ) : null}

      {overrideTarget ? (
        <PriceOverrideDialog
          item={overrideTarget}
          // The same concession the totals block and the menu board price
          // through, so the dialog cannot quote a figure the bill disagrees
          // with.
          discountPercent={discountPercent}
          isSubmitting={busyKey === `add-${overrideTarget.id}`}
          onCancel={() => {
            const item = overrideTarget;
            setOverrideTarget(null);
            // PAPER: asked FROM the picker, so backing out returns there.
            if (paperTill && pickerResume) setAddonTarget(item);
          }}
          onConfirm={confirmOverride}
        />
      ) : null}

      {/* One chit prints at a time — `window.print()` blocks, and a fire that
          spans three stations owes three pieces of paper in the order the
          tickets were minted. Keyed by ticket id so a reprint of the same
          ticket after onDone mounts a FRESH job rather than reviving the
          finished one. */}
      {/* D172 — a reprint asks why (only when the ticket printed before),
          then goes to the station's printer as a durable job; with no active
          printer the browser chit below prints it instead. */}
      {reprintAsk ? (
        <ReprintQuestion
          key={reprintAsk}
          ticketId={reprintAsk}
          onAnswer={(reason, seenPrintCount) => {
            const ticketId = reprintAsk;
            setReprintAsk(null);
            void reprintChit(ticketId, reason, seenPrintCount);
          }}
          onCancel={() => setReprintAsk(null)}
        />
      ) : null}
      {printQueue.length > 0 ? (
        <ChitPrintJob
          key={`${printQueue[0].ticketId}:${printQueue[0].action ?? ""}`}
          ticketId={printQueue[0].ticketId}
          action={printQueue[0].action}
          atFire={printQueue[0].atFire}
          reason={printQueue[0].reason}
          seenPrintCount={printQueue[0].seenPrintCount}
          announcePrinted={printQueue[0].reason !== undefined}
          onDone={() => setPrintQueue((current) => current.slice(1))}
        />
      ) : null}

      <OutOfStockFireConfirm
        items={fireWarning?.items ?? null}
        onCancel={() => setFireWarning(null)}
        onSend={() => {
          const proceed = fireWarning?.proceed;
          setFireWarning(null);
          proceed?.();
        }}
      />
      {/* T6 / D173 — warn, never block: food still cooking for a bag that is
          about to leave. «انتظار» keeps the bill open; «إغلاق الفاتورة» goes
          on to the ordinary hand-over. */}
      <ConfirmModal
        isOpen={stillCooking !== null}
        onClose={() => setStillCooking(null)}
        onConfirm={() => {
          const proceed = stillCooking?.proceed;
          setStillCooking(null);
          proceed?.();
        }}
        tone="caution"
        testId="still-cooking"
        title={t((m) => m.pos.stillCookingTitle)}
        description={
          stillCooking
            ? t(
                countedKey(stillCooking.plates.length, {
                  one: (m) => m.pos.stillCookingBodyOne,
                  two: (m) => m.pos.stillCookingBodyTwo,
                  few: (m) => m.pos.stillCookingBodyFew,
                  many: (m) => m.pos.stillCookingBody,
                }),
                { count: stillCooking.plates.length },
              )
            : undefined
        }
        confirmLabel={t((m) => m.pos.stillCookingClose)}
        cancelLabel={t((m) => m.pos.stillCookingWait)}
        closeLabel={t((m) => m.common.close)}
      >
        {stillCooking ? (
          <ul className="flex flex-col gap-1 text-sm" data-testid="still-cooking-lines">
            {stillCookingLines(stillCooking.plates, stationsQuery.data, localName).map((line) => (
              <li key={line.key}>
                {line.count > 1 ? `${line.count}× ` : null}
                {line.station
                  ? t((m) => m.pos.stillCookingLine, { dish: line.dish, station: line.station })
                  : line.dish}
              </li>
            ))}
          </ul>
        ) : null}
      </ConfirmModal>

      {/* Cancelling is destructive in exactly one way — the basket is
          discarded — and it removes the bill from the day's sales, so it asks
          first. On success the till goes back to the floor: there is no
          invoice left to stand on. */}
      <ConfirmModal
        isOpen={cancelAsked}
        onClose={() => setCancelAsked(false)}
        onConfirm={() => {
          void (async () => {
            const ok = await run(
              { type: "cancelInvoice", invoiceId: invoice.id, payload: {} },
              {
                key: "cancel",
                success: t((m) => m.invoices.cancelInvoiceDone),
              },
            );
            setCancelAsked(false);
            if (ok) {
              navigate(`/${locale}/pos`);
            }
          })();
        }}
        title={t((m) => m.invoices.cancelInvoiceTitle)}
        description={t((m) => m.invoices.cancelInvoicePosBody)}
        confirmLabel={t((m) => m.invoices.cancelInvoice)}
        cancelLabel={t((m) => m.common.cancel)}
        closeLabel={t((m) => m.common.close)}
        isSubmitting={busyKey === "cancel"}
        tone="danger"
      />

      {availability.confirm}

      {/* D156 — «اعتماد المدير», opened by `runApproved` before a covered act. */}
      {approvalGate.sheet}

      {/* B18. Rendered unconditionally rather than behind `warnTarget &&` so
          the modal keeps its subject through its own closing animation — the
          same reason the confirms beside it are written this way. */}
      <IngredientWarningDialog
        item={warnTarget}
        locale={locale}
        onCancel={() => setWarnTarget(null)}
        onConfirm={() => {
          const item = warnTarget;
          setWarnTarget(null);
          if (item) stageFromMenu(item);
        }}
      />

      {voidTarget ? (
        <VoidItemDialog
          kind={voidTarget.comp ? "comp" : "void"}
          maxCount={voidTarget.plateIds.length}
          /* D141 — the cooked plates among the newest `count`, the same
             slice Confirm sends. Never passed for a comp: a comp has no
             choice, and the builder below refuses the key for it anyway. */
          eligibleCountFor={
            voidTarget.comp
              ? undefined
              : (count) => returnableItemIds(voidPlates(count)).length
          }
          onCancel={() => setVoidTarget(null)}
          onConfirm={async (reasonCode, count, reasonNote, returnToStock) => {
            const kind = voidTarget.comp ? "comp" : "void";
            const ok = await runApproved(
              {
                type: voidTarget.comp ? "compItem" : "voidItem",
                invoiceId: invoice.id,
                // The LAST N plates — the adds the cashier most likely
                // regrets — in one all-or-nothing command. D43 (86cbdhg4p):
                // `buildVoidCommandPayload` is the one place the note is
                // added to the wire payload, pinned by its own unit tests.
                // D141: and the one place `returnToStockItemIds` is — only
                // for a void, only when "returned" was chosen, only for the
                // plates this till knows were cooked. Wasted (the default)
                // and every comp send the pre-D141 payload byte for byte.
                payload: buildVoidCommandPayload(
                  voidTarget.plateIds.slice(-count),
                  reasonCode as ReasonCode,
                  reasonNote,
                  returnToStock
                    ? {
                        kind,
                        itemIds: returnableItemIds(voidPlates(count)),
                      }
                    : undefined,
                ),
              },
              { key: "void-confirm" },
            );
            if (ok) {
              setVoidTarget(null);
            }
          }}
        />
      ) : null}

      {remakeTarget ? (
        <VoidItemDialog
          kind="remake"
          onCancel={() => setRemakeTarget(null)}
          onConfirm={async (reasonCode, _count, reasonNote) => {
            const trimmed = reasonNote.trim();
            const ok = await run(
              {
                type: "remakeItem",
                invoiceId: invoice.id,
                payload: {
                  itemId: remakeTarget.itemId,
                  reasonCode,
                  // Omitted rather than sent as `""` — an absent field and an
                  // empty string mean the same thing on the wire, and only
                  // `OTHER` makes the note mandatory anyway.
                  ...(trimmed ? { reasonNote: trimmed } : {}),
                },
              },
              { key: `remake-${remakeTarget.key}` },
            );
            if (ok) {
              setRemakeTarget(null);
            }
          }}
        />
      ) : null}

      {/* PAPER (2026-10-03) — the redesign's Settle window: the whole bill or
          the guest's own plates, through the same commands the two sheets
          below send. It waits behind the covers question when that is asked
          first, and stays up on the paid bill to say so. */}
      {paperTill &&
      (paperSettleAsked || (paperTill && handoffSettleOpen)) &&
      !isCountingGuests &&
      canSettle &&
      invoice.status !== "CANCELLED" &&
      (invoice.status !== "OPEN" || canClose) ? (
        <PaperSettleSheet
          invoice={invoice}
          placeName={
            receiptTableName ??
            t((m) => m.pos.invoiceNumber, { sequence: invoice.dailySequence })
          }
          aggregator={
            invoice.aggregatorId && platformName
              ? {
                  name: platformName,
                  commissionPercent: invoice.commissionPercent ?? 0,
                  // T15 — null on a bill from before the base existed reads
                  // as TOTAL, which is what it was priced on.
                  commissionBase: invoice.commissionBase ?? "TOTAL",
                }
              : null
          }
          transferConfirmed={Boolean(
            paymentsQuery.data?.some(
              (payment) =>
                payment.state === "VERIFIED" && payment.method === "INSTAPAY",
            ),
          )}
          canPayByItem={canAppend}
          // A walk-out writes off the WHOLE bill and needs it closed
          // (`runSettle` stops on an open one), so a bill this settle keeps
          // open offers none — the same rule as the classic sheet below.
          canWriteOff={canWriteOff && !payKeepsOpen}
          /* D126: with something unsent or waiting at the kitchen, Pay takes
             the money without closing the bill. */
          keepOpen={payKeepsOpen}
          /* T6 / D173 — the PAPER till's Settle is never greyed (D126), so
             the reason its Close would give, and the one tap, sit under the
             keep-open line. */
          keepOpenNote={
            tillClose?.reason ? (
              <CloseBlockedNote
                reason={tillClose.reason}
                action={closeUnblock}
                onAction={() => void unblockClose(false)}
                isBusy={isBusy}
                isLoading={busyKey === "close-unblock"}
                surface="settle"
              />
            ) : null
          }
          warning={
            settingsQuery.data?.shiftTrackingEnabled &&
            shiftCurrentQuery.data === null
              ? canManageShift
                ? t((m) => m.pos.noShiftWarning)
                : t((m) => m.pos.noShiftWarningAskManager)
              : undefined
          }
          warningActionLabel={
            canManageShift ? t((m) => m.pos.noShiftWarningOpen) : undefined
          }
          onOpenDrawer={
            settingsQuery.data?.shiftTrackingEnabled &&
            shiftCurrentQuery.data === null &&
            canManageShift
              ? () => setOpenDrawerAsked(true)
              : undefined
          }
          send={async (command, key) => {
            const result = await run(command, { key });
            return result ? { ok: true, invoice: result.invoice } : { ok: false };
          }}
          onClose={() => {
            closePaperSettleSheet();
            setSettlePicked(null);
            setSettleSeed(null);
          }}
          initialPlateIds={settlePicked ?? undefined}
          initialAmount={settleSeed?.amount}
          initialMethod={settleSeed?.method}
          dinerPaymentId={settleSeed?.dinerPaymentId}
        />
      ) : null}

      {/* The whole-bill sheet. On a CLOSED bill as it always was; and on the
          dark till's OPEN direct takeaway (founder, 2026-10-08), where it
          quotes the LIVE figures — an open bill's stored total is 0 until the
          close writes it — and pays through `settleOpenTakeaway`. It stays up
          if that close lands and its settle is refused, on the CLOSED bill. */}
      {(((settleAsked ||
        (!paperTill && handoffSettleOpen && invoice.tenderedTotal === 0)) &&
        invoice.status === "CLOSED") ||
        darkOpenTakeawaySheet) ? (
        <SettleSheet
          total={darkOpenTakeawaySheet ? outstandingOnBill(invoice) : invoice.total}
          tableTitle={settleTableTitle(invoice, receiptTableName, t) ?? undefined}
          breakdown={
            darkOpenTakeawaySheet && invoice.status === "OPEN"
              ? { ...liveOpenTotals(invoice), pricesIncludeTax: invoice.pricesIncludeTax }
              : invoice
          }
          note={
            darkOpenTakeawaySheet && payKeepsOpen
              ? t((m) => m.paperSettle.footKeepsOpen)
              : undefined
          }
          /*
           * A confirmed bank transfer for THIS bill (D44) preselects INSTAPAY.
           * Only when a cashier actually confirmed one: `SUBMITTED` is still
           * just the customer's word, and settling on that would close a bill
           * nobody has checked.
           */
          transferConfirmed={paymentsQuery.data?.some(
            (payment) =>
              payment.state === "VERIFIED" && payment.method === "INSTAPAY",
          )}
          /*
           * F2 — offered and preselected ONLY on a bill a platform brought.
           *
           * The rate comes off the BILL's snapshot, never the platform's
           * current row: editing the rate tomorrow must not restate what this
           * order cost tonight, which is the whole reason it is snapshotted at
           * open. The NAME is looked up, because a name is not a figure and a
           * cashier reading "Talabat" on the pill is reading the right thing.
           */
          aggregator={
            invoice.aggregatorId && platformName
              ? {
                  name: platformName,
                  commissionPercent: invoice.commissionPercent ?? 0,
                  // T15 — null on a bill from before the base existed reads
                  // as TOTAL, which is what it was priced on.
                  commissionBase: invoice.commissionBase ?? "TOTAL",
                }
              : null
          }
          isSubmitting={
            darkOpenTakeawaySheet
              ? busyKey === "close" || busyKey === "settle" || busyKey === "partial"
              : busyKey === "settle"
          }
          // D31: writing a bill off is deciding money will never arrive, which
          // is the same authority as taking a line off the bill. Same rung the
          // void and comp chips use — a second surface, not a new permission.
          // Never over an open bill that stays open (a walk-out is the WHOLE
          // bill and needs it closed — `runSettle` stops on it), nor over one
          // money is already in, which PAPER's window withholds it from too.
          canWriteOff={
            canWriteOff &&
            !(darkOpenTakeawaySheet && (payKeepsOpen || invoice.tenderedTotal > 0))
          }
          /* The D25 pre-warning, branched on authority for the same reason
             the refusal toast is (see `useErrorMessage`): the drawer screens
             are SHIFT_MANAGE. Since D92 the CASHIER preset holds it, so the
             person on the till usually CAN act — but WAITER and KITCHEN still
             cannot, and neither can a custom role built without the rung, so
             "open a drawer first" is still a dead instruction for some of the
             people who meet this warning. The holder gets the instruction AND
             the screen; everyone else is told who to ask, which is the only
             move actually available to them. */
          warning={
            settingsQuery.data?.shiftTrackingEnabled &&
            shiftCurrentQuery.data === null
              ? canManageShift
                ? t((m) => m.pos.noShiftWarning)
                : t((m) => m.pos.noShiftWarningAskManager)
              : undefined
          }
          /* Unit C: opens a drawer OVER this sheet instead of navigating to the
             back office and losing the bill. Offered only to a holder of the
             drawer rung, exactly as the link was. */
          onOpenDrawer={
            settingsQuery.data?.shiftTrackingEnabled &&
            shiftCurrentQuery.data === null &&
            canManageShift
              ? () => setOpenDrawerAsked(true)
              : undefined
          }
          warningLinkLabel={t((m) => m.pos.noShiftWarningOpen)}
          /* B3 — the other way to be paid, one tap from where the cashier
             discovers they need it. Same rung: `settlePartial` is
             `RESTAURANT_INVOICE_SETTLE`, so anyone who may open this sheet may
             take part of the bill through it. */
          onTakePart={
            darkOpenTakeawaySheet
              ? /* The split-tender sheet takes a CLOSED bill only. */
                undefined
              : (method) => {
                  closeSettleSheet();
                  setPartialMethod(method);
                  setPartialAsked(true);
                }
          }
          onCancel={() =>
            darkOpenTakeawaySheet ? setOpenTakeawaySettleFor(null) : closeSettleSheet()
          }
          onConfirm={async (method, amount, reference) => {
            if (darkOpenTakeawaySheet) {
              await settleOpenTakeaway(method, amount, reference);
              return;
            }
            const ok = await run(
              {
                type: "settleInvoice",
                invoiceId: invoice.id,
                payload: {
                  expectedVersion: invoice.version,
                  settlementMethod: method,
                  settlementAmount: amount,
                  // D163 — CARD/INSTAPAY and not blank, or nothing at all.
                  ...tenderReferenceField(method, reference),
                },
              },
              { key: "settle", success: t((m) => m.pos.settledToast) },
            );
            if (ok) {
              closeSettleSheet();
            }
          }}
        />
      ) : null}

      {/* B89 — giving back money the bill no longer asks for. CLOSED only and
          only for a holder of the refund rung, which is the same pair of gates
          the panel's own control applies — one gate in two places, never two. */}
      {overpaidAsked && invoice.status === "CLOSED" && canRefund ? (
        <OverpaymentDialog
          /* Unit C — the same one-tap open the settle sheet carries. */
          onOpenDrawer={
            settingsQuery.data?.shiftTrackingEnabled &&
            shiftCurrentQuery.data === null &&
            canManageShift
              ? () => setOpenDrawerAsked(true)
              : undefined
          }
          invoice={invoice}
          isSubmitting={busyKey === "overpaid"}
          /* The same D25 pre-warning every money-out path carries, branched on
             authority for the reason the refund sheet's is: the drawer screens
             are SHIFT_MANAGE, which the CASHIER preset holds since D92 and
             WAITER still does not — so the instruction is followable by the
             person on the till and dead for anyone else who reaches it. */
          warning={
            settingsQuery.data?.shiftTrackingEnabled &&
            shiftCurrentQuery.data === null
              ? canManageShift
                ? t((m) => m.pos.refundNoShiftWarning)
                : t((m) => m.pos.refundNoShiftWarningAskManager)
              : undefined
          }
          onCancel={() => setOverpaidAsked(false)}
          onConfirm={async (reasonCode, reasonNote) => {
            const result = await run(
              {
                type: "refundOverpayment",
                invoiceId: invoice.id,
                payload: {
                  expectedVersion: invoice.version,
                  reasonCode,
                  // Omitted rather than sent empty: the DTO trims it and an
                  // empty string is not a note anybody wrote.
                  ...(reasonNote ? { reasonNote } : {}),
                },
              },
              { key: "overpaid", success: t((m) => m.pos.overpaidDone) },
            );
            if (result) {
              setOverpaidAsked(false);
            }
          }}
        />
      ) : null}

      {/* B3 — the split-tender sheet. CLOSED only, which is what the server
          allows: a tender against an OPEN bill would be money taken for an
          order still being added to. */}
      {(partialAsked ||
        (!paperTill && handoffSettleOpen && invoice.tenderedTotal > 0)) &&
      invoice.status === "CLOSED" &&
      canSettle ? (
        <PartialSettleSheet
          /* Unit C — the same one-tap open the settle sheet carries. */
          onOpenDrawer={
            settingsQuery.data?.shiftTrackingEnabled &&
            shiftCurrentQuery.data === null &&
            canManageShift
              ? () => setOpenDrawerAsked(true)
              : undefined
          }
          invoice={invoice}
          isSubmitting={busyKey === "partial"}
          initialMethod={partialMethod}
          /* D44, the same gate the whole-bill sheet applies and for the same
             reason: INSTAPAY is offered only once a transfer for THIS bill has
             actually been confirmed. `SUBMITTED` is still the customer's word,
             and a transfer is revenue in neither drawer — so a tender recorded
             against one nobody checked never shows as missing at close. */
          transferConfirmed={paymentsQuery.data?.some(
            (payment) =>
              payment.state === "VERIFIED" && payment.method === "INSTAPAY",
          )}
          /* The same D25 pre-warning the whole-bill sheet carries, and for the
             same reason: money is physically arriving at a till, so with
             tracking on and no drawer open the server refuses this tender. */
          warning={
            settingsQuery.data?.shiftTrackingEnabled &&
            shiftCurrentQuery.data === null
              ? canManageShift
                ? t((m) => m.pos.noShiftWarning)
                : t((m) => m.pos.noShiftWarningAskManager)
              : undefined
          }
          onCancel={() => closePartialSheet()}
          onConfirm={async ({ method, amount, partyId, reference }) => {
            const result = await run(
              {
                type: "settlePartial",
                invoiceId: invoice.id,
                payload: {
                  expectedVersion: invoice.version,
                  settlementMethod: method,
                  // D163 — this tender's own reference, CARD/INSTAPAY only.
                  ...tenderReferenceField(method, reference),
                  // A party tender carries NO amount: the server derives it
                  // from the plate set, and a figure sent alongside could only
                  // ever disagree with it.
                  ...(partyId
                    ? { partyId }
                    : { settlementAmount: amount ?? 0 }),
                },
              },
              { key: "partial", success: t((m) => m.pos.partialTakenToast) },
            );
            // Closed only once the bill is square. While a balance remains the
            // sheet stays up on the bill's NEW outstanding figure, because the
            // next tender is the very next thing that happens — the customer is
            // still at the counter holding the other card.
            if (result?.invoice && result.invoice.status === "SETTLED") {
              closePartialSheet();
            }
            // `run` turns every refusal into a toast and `null`; the sheet
            // clears the cashier's answers (amount, family, D163 reference)
            // only on a tender that was actually taken.
            return { ok: result !== null };
          }}
        />
      ) : null}

      {/* D146 — THE DISCOUNT AT THE BILL. OPEN only, like the command; the
          sheet previews the new total and the server resolves it again. */}
      {discountAsked && invoice.status === "OPEN" ? (
        <BillDiscountSheet
          invoice={invoice}
          capPct={
            settingsQuery.data?.billDiscountMaxPct ?? DEFAULT_BILL_DISCOUNT_MAX_PCT
          }
          isSubmitting={busyKey === "bill-discount"}
          onCancel={() => setDiscountAsked(false)}
          onConfirm={async (payload) => {
            const result = await runApproved(
              { type: "applyBillDiscount", invoiceId: invoice.id, payload },
              {
                key: "bill-discount",
                success: t((m) => m.billDiscount.appliedToast),
              },
            );
            if (result) setDiscountAsked(false);
          }}
        />
      ) : null}

      {/* Round 5 — THE PRE-BILL CHECK. Recorded through the command envelope
          FIRST, printed only once that succeeded: the paper is the side effect
          and a printer that fails cannot undo the record. OPEN only, like the
          command — a closed bill's paper is its cheque. */}
      {checkAsked && invoice.status === "OPEN" ? (
        <PreBillCheckDialog
          invoice={invoiceForCheck(invoice)}
          tableName={receiptTableName}
          timezone={settingsQuery.data?.timezone}
          storeName={settingsQuery.data?.receiptHeader}
          isSubmitting={busyKey === "check"}
          onCancel={() => setCheckAsked(false)}
          onPrint={async (splitGuests) => {
            // A store with a receipt printer gets the paper there, from the
            // job `printCheck` queues in its own transaction (§11) — whichever
            // device pressed the button. Otherwise the browser prints it.
            const queued = settingsQuery.data?.receiptPrinterConfigured === true;
            const result = await run(
              {
                type: "printCheck",
                invoiceId: invoice.id,
                payload: splitGuests === null ? {} : { splitGuests },
              },
              {
                key: "check",
                success: queued
                  ? t((m) => m.preBillCheck.sentToPrinterToast)
                  : t((m) => m.preBillCheck.recordedToast),
              },
            );
            if (!result) return;
            setCheckAsked(false);
            if (queued) return;
            // The RETURNED bill: its `lastCheck` is the record's own time and
            // total. A redacted result (no bill-reading rung) falls back to the
            // bill on screen, which is the same bill one refresh earlier.
            checkPrinting.print({
              invoice: invoiceForCheck(result.invoice ?? invoice),
              splitGuests,
            });
          }}
        />
      ) : null}

      {/* B107 — correcting who an off-premise bill is for. OPEN or CLOSED,
          never SETTLED, and never on a dine-in bill: the server refuses both,
          and the panel's own control is gated identically, so the two cannot
          disagree about when a customer may be corrected. */}
      {customerAsked &&
      invoice.status !== "SETTLED" &&
      invoice.channel !== "DINE_IN" ? (
        <CustomerDialog
          invoice={invoice}
          isSubmitting={busyKey === "customer"}
          onCancel={() => setCustomerAsked(false)}
          onConfirm={async ({
            customerName,
            customerPhone,
            deliveryAddress,
          }) => {
            const ok = await run(
              {
                type: "editCustomer",
                invoiceId: invoice.id,
                payload: {
                  // All three, every time, each nullable — a patch shape would
                  // make "clear the address" indistinguishable from "leave it
                  // alone" the moment one of these was `undefined`, and
                  // clearing a wrong phone number has to be expressible.
                  customerName: customerName.trim() || null,
                  customerPhone: customerPhone.trim() || null,
                  deliveryAddress: deliveryAddress.trim() || null,
                },
              },
              { key: "customer", success: t((m) => m.pos.customerSaved) },
            );
            if (ok) setCustomerAsked(false);
          }}
        />
      ) : null}

      {/* 2026-10-20 — the Delivery tab's «تسليم إلى المندوب»: once, through
          the close's own path and gate (D22, the rung — a greyed close is not
          opened from elsewhere; the bill then shows it, and why). */}
      {assigningDriver && ownDelivery ? (
        <BillAssignPanel
          bill={billDispatchProps.bill}
          ids={assignIds}
          onClose={() => setAssigningDriver(false)}
        />
      ) : null}
      {/* 2026-10-20 — an accepted online order's paper: once, the at-fire
          chits the till's own «إرسال» would print (`queueFireChits`). */}
      {handoffPrintFired && !handoffPrintFiredDone && settingsQuery.isFetched ? (
        <HandOverHandoff
          onFire={() => {
            if (handoffPrintFiredQueued.current) return;
            handoffPrintFiredQueued.current = true;
            setHandoffPrintFiredDone(true);
            // A refresh must not replay the hand-off: the state is gone from
            // history (every other hand-off flag was captured at mount too).
            navigate(location.pathname + location.search, { replace: true, state: null });
            if (invoice.status === "OPEN") queueFireChits(invoice.items);
          }}
        />
      ) : null}
      {handoffHandOver && !handoffHandOverDone && settingsQuery.isFetched ? (
        <HandOverHandoff
          onFire={() => {
            setHandoffHandOverDone(true);
            // A refresh must not replay the hand-off: the state is gone from history.
            navigate(location.pathname + location.search, { replace: true, state: null });
            if (invoice.status === "OPEN" && tillClose?.enabled) requestClose();
          }}
        />
      ) : null}

      {/* T16 — the off-premise close: who the bag left with, and on a platform
          bill the one-tap hand-over (close, then settle AGGREGATOR for what is
          owed, on the version the close returned — the settle sheet's own
          sequence). Only while OPEN: a CLOSED bag is settled from its tile. */}
      {handOverAsked && invoice.status === "OPEN" && invoice.channel !== "DINE_IN" ? (
        <HandOverDialog
          invoice={invoice}
          runningTotal={runningTotal}
          platformName={platformName}
          recentNames={recentHandedTo}
          /* Founder, 2026-10-08: only a delivery asks who it left with; a
             platform's takeaway offers the platform hand-over alone. */
          askWho={invoice.channel === "DELIVERY"}
          /* B3 · P14 — the restaurant's own delivery: the dialog reads the
             bag's driver (shown, not typed) or offers it to one. */
          dispatchInvoiceId={
            invoice.channel === "DELIVERY" && invoice.aggregatorId === null ? invoice.id : undefined
          }
          isSubmitting={busyKey === "close" || busyKey === "settle"}
          onCancel={() => setHandOverAsked(false)}
          onConfirm={async ({ handedTo, toPlatform }) => {
            // A platform hand-over settles right after the close, so the
            // close's own undo would race a bill that is about to be SETTLED
            // (reviewer, 2026-10-07): the toast then offers a reopen that the
            // version guard refuses. No undo on that path; the ordinary close
            // keeps it.
            const closed = await closeBill(invoice.version, handedTo, {
              undo: !toPlatform,
            });
            if (!closed) return;
            setHandOverAsked(false);
            if (!toPlatform || !closed.invoice || closed.invoice.status !== "CLOSED") return;
            await run(
              {
                type: "settleInvoice",
                invoiceId: invoice.id,
                payload: {
                  // The version the CLOSE returned, not this render's.
                  expectedVersion: closed.invoice.version,
                  settlementMethod: "AGGREGATOR",
                  // The SERVER's owed figure on the closed bill.
                  settlementAmount: outstandingOnBill(closed.invoice),
                },
              },
              { key: "settle", success: t((m) => m.pos.settledToast) },
            );
          }}
        />
      ) : null}

      {/* T6 — correcting what an OPEN off-premise bill charges. The same three
          gates as the panel's control and the server's refusals (OPEN,
          off-premise, cashier's rung); a refusal that still arrives — the
          bill closed under this device — is `run`'s ordinary error toast. */}
      {chargesAsked &&
      invoice.status === "OPEN" &&
      invoice.channel !== "DINE_IN" ? (
        <ChargesDialog
          invoice={invoice}
          aggregators={aggregatorsQuery.data ?? []}
          defaultDeliveryFee={settingsQuery.data?.deliveryFeeDefault ?? null}
          isSubmitting={busyKey === "charges"}
          onCancel={() => setChargesAsked(false)}
          onConfirm={async (payload) => {
            const ok = await run(
              { type: "editCharges", invoiceId: invoice.id, payload },
              { key: "charges", success: t((m) => m.pos.chargesSaved) },
            );
            if (ok) setChargesAsked(false);
          }}
        />
      ) : null}

      {/* 2026-10-20 — the printer's choice: the whole check, or chosen items
          printed as a part of the bill; and the parts of a split bill, each
          with its own print and settle. OPEN (the pre-bill check's rung) or
          CLOSED (the cheque's «طباعة»). */}
      {printChoiceAsked && (invoice.status === "OPEN" || invoice.status === "CLOSED") ? (
        <PrintChoiceSheet
          invoice={invoice}
          isSubmitting={busyKey === "check" || busyKey === "party"}
          canMarkParts={canAppend}
          canPrintCheck={invoice.status !== "OPEN" || canClose}
          /* 2026-10-20 (user) — «طباعة التذكرة» moved off the bill's foot into
             this sheet, first, while an unprinted ticket holds the close: the
             same resend path (D172 reprint → 409 → browser chit → printTicket). */
          onPrintTicket={
            invoice.status === "OPEN" && closeUnblock === "resendTicket"
              ? () => {
                  setPrintChoiceAsked(false);
                  void unblockClose(true);
                }
              : undefined
          }
          printTicketBusy={busyKey === "close-unblock"}
          onWhole={printWhole}
          onPrintPlates={printPart}
          onSettlePart={
            paperTill && canSettle && canAppend && (invoice.status !== "OPEN" || canClose)
              ? (plateIds) => {
                  setPrintChoiceAsked(false);
                  setSettlePicked(plateIds);
                  requestPaperSettle();
                }
              : !paperTill && canSettle && invoice.status === "CLOSED"
                ? () => {
                    // The dark till's part-payment sheet: the part is its chip.
                    setPrintChoiceAsked(false);
                    setPartialAsked(true);
                  }
                : undefined
          }
          onEditParts={
            canAppend
              ? () => {
                  setPrintChoiceAsked(false);
                  setPartyAsked(true);
                }
              : undefined
          }
          onCancel={() => setPrintChoiceAsked(false)}
        />
      ) : null}

      {/* B43 — the split sheet. OPEN or CLOSED, never SETTLED: the cheque on
          the table is exactly when a family says which plates are theirs, and
          once the money is in, re-marking would move a share already taken.
          The panel's own control is gated identically. */}
      {partyAsked && invoice.status !== "SETTLED" ? (
        <PartySheet
          invoice={invoice}
          isSubmitting={busyKey === "party"}
          onCancel={() => setPartyAsked(false)}
          onConfirm={async (input) => {
            const payload = resolveAssignPartyPayload(input);
            // The sheet's Confirm is gated on this same call, so a null here is
            // a race and not a state — belt and braces rather than a second
            // rule that could disagree with the first.
            if (!payload) return;
            await run(
              { type: "assignParty", invoiceId: invoice.id, payload },
              { key: "party", success: t((m) => m.pos.partyAssigned) },
            );
            // The sheet STAYS OPEN. Marking a table of ten is several rounds of
            // "these three, then those two", and a sheet that closed after each
            // one would make the waiter reopen it from the footer every time.
          }}
        />
      ) : null}

      {/* R1/F3 — the refund sheet. Only over a SETTLED bill, and only for a
          holder of the rung; the panel's own button is gated identically, so
          this is the second half of one gate rather than a different one. */}
      {refundAsked && invoice.status === "SETTLED" ? (
        <RefundSheet
          /* Unit C — the same one-tap open the settle sheet carries. */
          onOpenDrawer={
            settingsQuery.data?.shiftTrackingEnabled &&
            shiftCurrentQuery.data === null &&
            canManageShift
              ? () => setOpenDrawerAsked(true)
              : undefined
          }
          invoice={invoice}
          isSubmitting={busyKey === "refund"}
          /* R8's threshold, the STORE's own — the same `GET /restaurant/settings`
             read this screen already holds for the charge rates. Omitted while
             the read is in flight or failed, which falls the sheet back to the
             server's own constant rather than to a guess. */
          lateAfterMinutes={settingsQuery.data?.refundLateAfterMinutes}
          /* The D25 pre-warning, branched on authority exactly as the settle
             sheet branches it: money is physically leaving a till, so with
             tracking on and no drawer open the server refuses this refund. The
             drawer screens are SHIFT_MANAGE — held by the CASHIER preset since
             D92, never by WAITER — so for anyone without the rung "open a
             drawer first" is a dead instruction and they are told who to ask
             instead. */
          warning={
            settingsQuery.data?.shiftTrackingEnabled &&
            shiftCurrentQuery.data === null
              ? /* The REFUND's OWN sentence, not the settle sheet's (UI MINOR
                   8). The borrowed copy said "the settlement will be refused",
                   which is the wrong event on this sheet — and on a CARD bill
                   it sat two lines under "the drawer is not touched by this
                   refund at all", so one screen said both. A card refund does
                   need an open drawer even though it never moves one, and that
                   rule is now ON SCREEN (trap 1) rather than left implied. */
                invoice.settlementMethod === "CARD"
                ? canManageShift
                  ? t((m) => m.pos.refundNoShiftWarningCard)
                  : t((m) => m.pos.refundNoShiftWarningCardAskManager)
                : canManageShift
                  ? t((m) => m.pos.refundNoShiftWarning)
                  : t((m) => m.pos.refundNoShiftWarningAskManager)
              : undefined
          }
          onCancel={() => setRefundAsked(false)}
          onConfirm={async (
            itemIds,
            reasonCode,
            reasonNote,
            refundDeliveryFee,
            returnToStockItemIds,
          ) => {
            // What the bill had already given back BEFORE this command. The
            // difference after it is the ledger's own figure for this refund,
            // and it is what the receipt prints. Since F17 that difference
            // includes the courier's fee when the cashier took it, because
            // `refundTotal` is summed over both guest-refund kinds — so the slip
            // states what actually left the drawer without this line changing.
            const refundedBefore = invoice.refundTotal;
            // D156 — asks for a password only when stock comes back.
            const result = await runApproved(
              {
                type: "refundInvoice",
                invoiceId: invoice.id,
                /*
                 * `reasonNote` is omitted, never sent as "": an absent field
                 * and an empty string must mean the same thing on the wire.
                 * F17 — `refundDeliveryFee` is sent only when the cashier
                 * ticked it: the server defaults it to false, so an absent
                 * field and `false` must mean the same thing, and the safe
                 * direction is "the fee stays claimable". D141 —
                 * `returnToStockItemIds` only when "returned to stock" was
                 * chosen for at least one cooked plate; the default (wasted)
                 * is the pre-D141 payload byte for byte. One builder, pinned
                 * by `voidReasons.spec.ts`.
                 */
                payload: buildRefundCommandPayload({
                  expectedVersion: invoice.version,
                  itemIds,
                  reasonCode,
                  reasonNote,
                  refundDeliveryFee,
                  returnToStockItemIds,
                }),
              },
              {
                key: "refund",
                success: t((m) => m.pos.refundedToast),
                onChangedReplay: (replayed) =>
                  setReplayedRefundCommandId(replayed.commandId),
              },
            );
            if (!result) return;
            setRefundAsked(false);
            /*
             * The slip names the method the money left by (R6). The guard is
             * the shared allowlist, not a hand-written pair: when INSTAPAY
             * became refundable a `!== "CASH" && !== "CARD"` here would have
             * silently printed NO slip for a transfer refund that really
             * happened — a customer sent away from a tax document with nothing.
             */
            const settled = invoice.settlementMethod;
            if (!isRefundableSettlementMethod(settled)) return;
            /*
             * The ORIGINAL slip. The allocation is recomputed over the WHOLE
             * chargeable set inside the builder (the policy's property 4) so
             * the paper and the sheet cannot disagree about a plate's split.
             *
             * The NOTE is deliberately not passed: it is the internal record
             * (R10) and the customer's copy prints the reason code's label
             * only — business finding 10.
             */
            // The just-made refund's ORIGINAL supersedes whatever copy was last
            // asked for, so the paper mounted below is this refund's.
            refundPrinting.printOriginal(
              buildRefundDocument({
                invoice,
                itemIds,
                method: settled,
                reasonLabel: voidReasonLabelForCode(t, reasonCode, "refund"),
                // An ISO instant, formatted on the paper in the STORE's
                // timezone. The old `toISOString()` printed UTC on a document
                // carrying the tax registration number (business DEFECT-2).
                refundedAt: new Date().toISOString(),
                // The LEDGER's figure, not the till's sum — see RefundReceipt's
                // own note on why the paper prints the server's number and
                // drops its lines when the two disagree.
                serverTotal:
                  (result.invoice?.refundTotal ?? refundedBefore) -
                  refundedBefore,
                // The refund's own handle, and a real one: it is the id of the
                // command that made it.
                commandId: result.commandId,
              }),
            );
          }}
        />
      ) : null}

      {transferAsked ? (
        <TransferTableDialog
          tables={panelTables}
          currentTableId={invoice.tableIds[0] ?? null}
          isSubmitting={busyKey === "transfer"}
          onCancel={() => setTransferAsked(false)}
          onPick={async (tableId) => {
            const ok = await run(
              {
                type: "transferInvoice",
                invoiceId: invoice.id,
                payload: { tableId },
              },
              { key: "transfer", success: t((m) => m.pos.transferDone) },
            );
            if (ok) {
              setTransferAsked(false);
            }
          }}
        />
      ) : null}

      {noteTarget ? (
        <NoteDialog
          initialNote={noteTarget.note}
          dishName={localName({ nameAr: noteTarget.plateNameAr, nameEn: noteTarget.plateNameEn })}
          isSubmitting={busyKey === "note"}
          onCancel={() => setNoteTarget(null)}
          onSave={async (note) => {
            const ok = await run(
              {
                type: "updateDraftItems",
                invoiceId: invoice.id,
                payload: {
                  // The whole group: "no onions" on two of three identical
                  // burgers is a different group by definition (the note is in
                  // the grouping key), so acting on all plates here is exact.
                  setNotes: noteTarget.plates.map((plate) => ({
                    itemId: plate.id,
                    note,
                  })),
                },
              },
              { key: "note" },
            );
            if (ok) {
              setNoteTarget(null);
            }
          }}
        />
      ) : null}

      {/* The table's name comes from the floor grid this screen already holds
          (the table chips read the same list), so the cheque can name the table
          without a second request. */}
      <Receipt
        invoice={invoice}
        settings={settingsQuery.data ?? null}
        tableName={receiptTableName}
        /* T19 — the band names the platform; the bill has only its id. */
        platformName={platformName}
      />

      {/* The refund's own paper (trap 9), on the same 80mm roll and its own
          print scope so it can never come out superimposed on the cheque
          mounted right above it. The ORIGINAL while this device still holds it,
          and otherwise a COPY rebuilt from the bill. */}
      {refundPrinting.paper ? (
        <RefundReceipt
          document={refundPrinting.paper}
          settings={settingsQuery.data ?? null}
          tableName={receiptTableName}
        />
      ) : null}

      {/* Round 5 — the pre-bill check's paper, in its own `check` print scope
          so it can never come out superimposed on the cheque above it. */}
      {checkPrinting.paper ? (
        <PreBillCheckPaper
          invoice={checkPrinting.paper.invoice}
          splitGuests={checkPrinting.paper.splitGuests}
          settings={settingsQuery.data ?? null}
          tableName={receiptTableName}
        />
      ) : null}

      {/* Unit C — the one-tap open, layered OVER whichever money sheet asked for
          it (`elevated`). The bill stays exactly where it is: the cashier opens a
          drawer and taps settle again, which is the whole difference between this
          and the link to the back office it replaced. */}
      <OpenDrawerDialog
        isOpen={openDrawerAsked}
        elevated
        onClose={() => setOpenDrawerAsked(false)}
      />
    </Screen>
  );
}

/**
 * Runs a mount hand-off once, after the bill rendered — the Delivery tab's
 * hand-over (`InvoiceHandoff.handOver`) and an accepted online order's chits
 * (`InvoiceHandoff.printFired`): the page's close flow and print queue are
 * defined after its loading returns, so they are reached from this child's
 * effect.
 */
function HandOverHandoff({ onFire }: { onFire: () => void }) {
  useEffect(() => {
    onFire();
  }, [onFire]);
  return null;
}
